import React, { useState, ChangeEvent, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import TableRow from './TableRow';
import { v4 as uuidv4 } from 'uuid';
import { useCompany } from '../../company/contexts/CompanyContext';
import { useToast } from '../../shared/contexts/ToastContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useApiCall } from '../../shared/hooks/useApiCall';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import AddCompanyModal from '../../company/components/AddCompanyModal';
import CompanyDetailModal from '../../company/components/CompanyDetailModal';
import EditCompanyModal from '../../company/components/EditCompanyModal';
import CompanySelection from '../../company/components/CompanySelection';
import CompanyTabs from '../../company/components/CompanyTabs';
import FileUpload from './FileUpload';
import TranslationTable, { TranslationResult } from './TranslationTable';
import CompanyDocuments from '../../company/components/CompanyDocuments';
import { Company } from '../../company/types/Company';
import { useLocation, useNavigate } from 'react-router-dom';
import EnChangeHistoryModal from './EnChangeHistoryModal';

interface TranslatorPageProps {
  uploadEndpoint?: string;
}

const TranslatorPage: React.FC<TranslatorPageProps> = ({ 
  uploadEndpoint = 'https://n8n.mahir.ai/webhook/serialize_file' 
}) => {
  const { companies, selectedCompany, setSelectedCompany, addCompany, removeCompany, isLoading, error, refreshCompanies } = useCompany();
  const { success, error: showError, warning, info } = useToast();
  const { user, canStartQualityControl, canDownloadFiles } = useAuth();
  const { apiCall } = useApiCall();
  
  // URL tabanlı sayfa yönetimi
  const location = useLocation();
  const navigate = useNavigate();
  
  // Company selection state
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCompanyForDetail, setSelectedCompanyForDetail] = useState<Company | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompanyForEdit, setSelectedCompanyForEdit] = useState<Company | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompanyForDelete, setSelectedCompanyForDelete] = useState<Company | null>(null);
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [selectedDocumentForDelete, setSelectedDocumentForDelete] = useState<{id: string, name: string} | null>(null);
  const [currentStep, setCurrentStep] = useState<'company-selection' | 'company-tabs'>('company-selection');
  
  // Comment deletion modals
  const [showDeleteAllCommentsModal, setShowDeleteAllCommentsModal] = useState(false);
  const [showDeleteRowCommentsModal, setShowDeleteRowCommentsModal] = useState(false);
  const [showDeleteSingleCommentModal, setShowDeleteSingleCommentModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<{sid: string, timestamp: number} | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedOption, setSelectedOption] = useState<'translations' | 'translate' | null>(null);
  
  // Yeni eklenen şirket için cache
  const [newCompanyCache, setNewCompanyCache] = useState<{
    name: string;
    tempId: string;
    company_id?: number;
    isWaitingForWebhook: boolean;
  } | null>(null);
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadFilename, setDownloadFilename] = useState<string | null>(null);
  const prevDownloadUrl = useRef<string | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const currentDocumentIdRef = useRef<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Report selection states
  const [reportType, setReportType] = useState<string | null>(null);
  const [reportYear, setReportYear] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  
  // Translation language states
  const [sourceLanguage, setSourceLanguage] = useState<string>('tr'); // Default: Türkçe
  const [destinationLanguage, setDestinationLanguage] = useState<string>('en'); // Default: İngilizce
  
  // New translation response states
  const [translationResults, setTranslationResults] = useState<TranslationResult[] | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>('');
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [showDeleteChangesModal, setShowDeleteChangesModal] = useState<boolean>(false);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [showDeleteRowModal, setShowDeleteRowModal] = useState<boolean>(false);
  const [deleteRowSuccess, setDeleteRowSuccess] = useState<boolean>(false);
  const [selectedRowSid, setSelectedRowSid] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState<boolean>(false);
  const [bulkDeleteSuccess, setBulkDeleteSuccess] = useState<boolean>(false);
  const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
  const [showTranslateConfirmModal, setShowTranslateConfirmModal] = useState<boolean>(false);
  
  // Download states
  const [showDownloadConfirmModal, setShowDownloadConfirmModal] = useState<boolean>(false);
  const [selectedDocumentForDownload, setSelectedDocumentForDownload] = useState<{document_id: string, file_name_no_ext: string} | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [showTranslateSuccessModal, setShowTranslateSuccessModal] = useState<boolean>(false);
  
  // Compare documents states
  const [showCompareDocumentsModal, setShowCompareDocumentsModal] = useState<boolean>(false);
  const [companyDocumentsForCompare, setCompanyDocumentsForCompare] = useState<Array<{document_id: string, file_name_no_ext: string, current_date?: string, current_time?: string}> | null>(null);
  const [isLoadingCompareDocuments, setIsLoadingCompareDocuments] = useState<boolean>(false);
  const [selectedCompareDocuments, setSelectedCompareDocuments] = useState<Set<string>>(new Set());
  const [isComparingDocuments, setIsComparingDocuments] = useState<boolean>(false);
  const [compareData, setCompareData] = useState<Array<{sid: string, old_data_runs: string, old_data_en_runs: string, match_percentage: number, is_active: boolean}> | null>(null);
  const [showClearCompareDataModal, setShowClearCompareDataModal] = useState<boolean>(false);
  const [isClearingCompareData, setIsClearingCompareData] = useState<boolean>(false);
  const [clearCompareDataSuccess, setClearCompareDataSuccess] = useState<boolean>(false);
  const [showBulkHistoryReplaceModal, setShowBulkHistoryReplaceModal] = useState<boolean>(false);
  const [historyReplaceThreshold, setHistoryReplaceThreshold] = useState<number>(100);
  const [isBulkHistoryReplacing, setIsBulkHistoryReplacing] = useState<boolean>(false);
  const [selectedHistoryRange, setSelectedHistoryRange] = useState<{min: number, max: number}>({min: 100, max: 100});
  const [selectedHistoryItems, setSelectedHistoryItems] = useState<Set<string>>(new Set());
  const [historyCurrentPage, setHistoryCurrentPage] = useState<number>(1);
  const [historyItemsPerPage] = useState<number>(50);
  const [hideAppliedHistory, setHideAppliedHistory] = useState<boolean>(true);
  const historyTableContainerRef = useRef<HTMLDivElement>(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Go to row state for translated data
  const [goToRowValue, setGoToRowValue] = useState<string>('');
  const [highlightedRowSid, setHighlightedRowSid] = useState<string | null>(null);
  const goToRowTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // English text editing states
  const [editingEnSid, setEditingEnSid] = useState<string | null>(null);
  const [editingEnText, setEditingEnText] = useState<string>('');
  const [showDeleteEnChangesModal, setShowDeleteEnChangesModal] = useState<boolean>(false);
  const [deleteEnSuccess, setDeleteEnSuccess] = useState<boolean>(false);
  const [showDeleteEnRowModal, setShowDeleteEnRowModal] = useState<boolean>(false);
  const [deleteEnRowSuccess, setDeleteEnRowSuccess] = useState<boolean>(false);
  const [selectedEnRowSid, setSelectedEnRowSid] = useState<string | null>(null);
  const [selectedEnRows, setSelectedEnRows] = useState<Set<string>>(new Set());
  const [showBulkDeleteEnModal, setShowBulkDeleteEnModal] = useState<boolean>(false);
  const [bulkDeleteEnSuccess, setBulkDeleteEnSuccess] = useState<boolean>(false);
  const [showDeleteAllEnModal, setShowDeleteAllEnModal] = useState<boolean>(false);
  const [isEnSelectionMode, setIsEnSelectionMode] = useState<boolean>(false);
  const [showUpdateControlModal, setShowUpdateControlModal] = useState<boolean>(false);
  const [isUpdatingControl, setIsUpdatingControl] = useState<boolean>(false);
  
  // Company translations states
  const [companyDocuments, setCompanyDocuments] = useState<Array<{document_id: string, file_name_no_ext: string, current_date?: string, current_time?: string, username?: string, source_language?: string, destination_language?: string, checked_count?: string, unchecked_count?: string, is_completed?: boolean}> | null>(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  
  // Translated data states
  const [translatedData, setTranslatedData] = useState<Array<{sid: string, runs: string, en_runs: string, glossary_tr_regex: string | null, glossary_en_regex: string | null, enter_flag: boolean, is_checked?: boolean}> | null>(null);
  const [translatedDataError, setTranslatedDataError] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [selectedDocumentName, setSelectedDocumentName] = useState<string | null>(null);
  const [selectedDocumentSourceLanguage, setSelectedDocumentSourceLanguage] = useState<string | null>(null);
  const [selectedDocumentDestinationLanguage, setSelectedDocumentDestinationLanguage] = useState<string | null>(null);
  const [showTranslatedDataView, setShowTranslatedDataView] = useState<boolean>(false);
  
  // Completion data states
  const [completionData, setCompletionData] = useState<{checked_count: string, unchecked_count: string} | null>(null);
  
  // Change history states
  const [enChangeHistory, setEnChangeHistory] = useState<any[]>([]);
  const [showChangeHistoryModal, setShowChangeHistoryModal] = useState<boolean>(false);
  const [selectedRowChangeHistory, setSelectedRowChangeHistory] = useState<any | null>(null);
  
  // Find and Replace states
  const [showReplaceInput, setShowReplaceInput] = useState<boolean>(false);
  const [replaceText, setReplaceText] = useState<string>('');
  const [isFindReplaceProcessing, setIsFindReplaceProcessing] = useState<boolean>(false);
  const [replacePreview, setReplacePreview] = useState<{sid: string, oldText: string, newText: string, rowNumber: number}[]>([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(200);
  
  // Translated data pagination states
  const [translatedCurrentPage, setTranslatedCurrentPage] = useState<number>(1);
  const [translatedItemsPerPage, setTranslatedItemsPerPage] = useState<number>(200);

  // Sorting states for translated data
  const [sortField, setSortField] = useState<'score' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Search states for translated data
  const [turkishSearch, setTurkishSearch] = useState<string>('');
  const [englishSearch, setEnglishSearch] = useState<string>('');
  
  // Search options for case sensitivity and whole word matching - Turkish
  const [isTurkishCaseSensitive, setIsTurkishCaseSensitive] = useState<boolean>(false);
  const [isTurkishWholeWord, setIsTurkishWholeWord] = useState<boolean>(false);
  
  // Search options for case sensitivity and whole word matching - English
  const [isEnglishCaseSensitive, setIsEnglishCaseSensitive] = useState<boolean>(false);
  const [isEnglishWholeWord, setIsEnglishWholeWord] = useState<boolean>(false);
  
  // Search debounce timeout
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Pre-search position state to restore position when search is closed
  const [preSearchPosition, setPreSearchPosition] = useState<{page: number, scrollTop: number} | null>(null);

  // Quality control filter and sorting states
  const [showCriticalErrorsOnly, setShowCriticalErrorsOnly] = useState<boolean>(false);
  const [showHumanReviewOnly, setShowHumanReviewOnly] = useState<boolean>(false);
  const [showChangedRowsOnly, setShowChangedRowsOnly] = useState<boolean>(false);
  const [violationSortOrder, setViolationSortOrder] = useState<'none' | 'asc' | 'desc'>('none');
  const [qualityScoreSortOrder, setQualityScoreSortOrder] = useState<'none' | 'asc' | 'desc'>('none');
  const [hideCheckedCriticalErrors, setHideCheckedCriticalErrors] = useState<boolean>(false);
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>('');
  const [showUserFilterDropdown, setShowUserFilterDropdown] = useState<boolean>(false);
  const [showCommentsRowsOnly, setShowCommentsRowsOnly] = useState<boolean>(false);
  const [selectedCommentUserFilter, setSelectedCommentUserFilter] = useState<string>('');
  const [showCommentUserFilterDropdown, setShowCommentUserFilterDropdown] = useState<boolean>(false);
  const [selectedViolationTypes, setSelectedViolationTypes] = useState<Set<string>>(new Set());
  const [showViolationFilterDropdown, setShowViolationFilterDropdown] = useState<boolean>(false);
  const violationFilterDropdownRef = useRef<HTMLDivElement>(null);

  // Column visibility states
  const [showControlColumn, setShowControlColumn] = useState<boolean>(true);
  const [showGlossaryColumn, setShowGlossaryColumn] = useState<boolean>(true);
  const [showQualityColumn, setShowQualityColumn] = useState<boolean>(true);
  const [showCommentsColumn, setShowCommentsColumn] = useState<boolean>(true);
  
  // Comments modal states
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [selectedRowForComments, setSelectedRowForComments] = useState<{sid: string, rowNumber: number} | null>(null);
  const [rowComments, setRowComments] = useState<{[sid: string]: {user: string, userId: number, timestamp: number, comment: string}[]}>({});
  const [newComment, setNewComment] = useState<string>('');

  // Reset page when filters or search changes
  useEffect(() => {
    setTranslatedCurrentPage(1);
  }, [showCriticalErrorsOnly, showHumanReviewOnly, violationSortOrder, qualityScoreSortOrder, hideCheckedCriticalErrors, turkishSearch, englishSearch]);

  // Recalculate Turkish search results when Turkish search options change
  useEffect(() => {
    if (turkishSearch.trim() && translatedData) {
      calculateSearchResults(translatedData, turkishSearch, 'turkish');
    }
  }, [isTurkishCaseSensitive, isTurkishWholeWord]);

  // Recalculate English search results when English search options change
  useEffect(() => {
    if (englishSearch.trim() && translatedData) {
      calculateSearchResults(translatedData, englishSearch, 'english');
      if (showReplaceInput && replaceText) {
        generateReplacePreview(englishSearch, replaceText);
      }
    }
  }, [isEnglishCaseSensitive, isEnglishWholeWord]);

  // Clear compare data when document changes
  useEffect(() => {
    // Reset compare data when switching to a different document
    setCompareData(null);
  }, [selectedDocumentId]);

  // Scroll to top when history table page changes
  useEffect(() => {
    if (historyTableContainerRef.current) {
      historyTableContainerRef.current.scrollTop = 0;
    }
  }, [historyCurrentPage]);

  // Handle translated data page change with scroll to top
  const handleTranslatedPageChange = (page: number) => {
    setTranslatedCurrentPage(page);
    // Scroll to top of the table container
    setTimeout(() => {
      // Try multiple selectors to find the scrollable container
      const tableContainer = 
        document.querySelector('[data-testid="translation-table-container"]') ||
        document.querySelector('[data-testid="translation-table-scroll-container"]') ||
        document.querySelector('[data-testid="translation-table-main-container"]') ||
        document.querySelector('.overflow-auto.flex-1') ||
        document.querySelector('.overflow-y-scroll') ||
        document.querySelector('.overflow-y-auto') ||
        document.querySelector('.max-h-screen') ||
        document.querySelector('.h-screen');
      
      if (tableContainer) {
        tableContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Fallback: scroll to top of window
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100); // Small delay to ensure page change is processed
  };
  
  // Search results and navigation
  const [searchResults, setSearchResults] = useState<{sid: string, index: number}[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(0);
  const [searchType, setSearchType] = useState<'turkish' | 'english' | null>(null);

  // Quality control states
  const [isCheckingQualityControl, setIsCheckingQualityControl] = useState<boolean>(false);
  const [qualityControlStatus, setQualityControlStatus] = useState<any>(null);
  const [showQualityControlModal, setShowQualityControlModal] = useState<boolean>(false);
  const [showQualityControlPopover, setShowQualityControlPopover] = useState<boolean>(false);
  const [isLoadingQualityResults, setIsLoadingQualityResults] = useState<boolean>(false);
  const [isBatchTranslateTriggered, setIsBatchTranslateTriggered] = useState<boolean>(false);
  const [isTriggeringBatchTranslate, setIsTriggeringBatchTranslate] = useState<boolean>(false);

  // Drag and drop states for English text
  const [draggedRowSid, setDraggedRowSid] = useState<string | null>(null);
  const [dragOverRowSid, setDragOverRowSid] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isLoadingTranslationView, setIsLoadingTranslationView] = useState<boolean>(false);
  const [isQualityControlDownloadEnabled, setIsQualityControlDownloadEnabled] = useState<boolean>(false);
  const [isControlMode, setIsControlMode] = useState<boolean>(false);
  const [selectedControlRows, setSelectedControlRows] = useState<Set<string>>(new Set());
  const [showControlPieMenu, setShowControlPieMenu] = useState<boolean>(false);
  const [showPageSizePieMenu, setShowPageSizePieMenu] = useState<boolean>(false);
  const [qualityControlResults, setQualityControlResults] = useState<Array<{
    sid: string;
    checked_tasks: string;
    violations: string;
    auto_corrections: string;
    hard_fail: boolean;
    penalty_points: number;
    score: number;
    band: string;
    comment_tr: string;
    corrected_en: string;
    n_applicable: number;
    n_violation: number;
    rules_passrate: number;
    needs_human_review: boolean;
    diagnostics: string;
    // Legacy fields for backward compatibility
    justification?: string;
    corrected_en_runs?: string;
    meaning?: number;
    wording?: number;
    style?: number;
    overall?: number;
  }> | null>(null);
  const [qualityControlDocumentId, setQualityControlDocumentId] = useState<string | null>(null);
  const [showQualityDetailPopover, setShowQualityDetailPopover] = useState<boolean>(false);
  const [selectedQualityResult, setSelectedQualityResult] = useState<{
    sid: string;
    checked_tasks: string;
    violations: string;
    auto_corrections: string;
    hard_fail: boolean;
    penalty_points: number;
    score: number;
    band: string;
    comment_tr: string;
    corrected_en: string;
    n_applicable: number;
    n_violation: number;
    rules_passrate: number;
    needs_human_review: boolean;
    diagnostics: string;
    // Legacy fields for backward compatibility
    justification?: string;
    corrected_en_runs?: string;
    meaning?: number;
    wording?: number;
    style?: number;
    overall?: number;
  } | null>(null);
  const [qualityPopoverPosition, setQualityPopoverPosition] = useState<{top: number, left: number, arrowTop?: number, arrowDirection?: string} | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{top: number, right: number} | null>(null);
  const qualityControlButtonRef = useRef<HTMLDivElement>(null);
  const qualityControlIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dataRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const userFilterDropdownRef = useRef<HTMLDivElement>(null);
  const commentUserFilterDropdownRef = useRef<HTMLDivElement>(null);
  
  // Completion tooltip states
  const [showCompletionTooltip, setShowCompletionTooltip] = useState<boolean>(false);
  const [completionTooltipPosition, setCompletionTooltipPosition] = useState<{top: number, left: number} | null>(null);
  const completionChartRef = useRef<HTMLDivElement>(null);
  
  // Basit refresh flag sistemi - sadece ref kullan
  const needsRefreshRef = useRef<boolean>(false); // null gelirse true, refresh yapılınca false
  const fetchTranslatedDataRef = useRef<((documentId: string, documentName: string, sourceLanguage?: string, destinationLanguage?: string) => Promise<void>) | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (qualityControlIntervalRef.current) {
        clearInterval(qualityControlIntervalRef.current);
      }
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (dataRefreshIntervalRef.current) {
        clearInterval(dataRefreshIntervalRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to normalize Turkish characters for search
  const normalizeTurkishText = useCallback((text: string): string => {
    // Use Turkish locale for proper lowercase conversion (İ→i, I→ı)
    return text.toLocaleLowerCase('tr-TR').trim();
  }, []);

  // Pagination calculations - no filtering, search navigation handled in TranslationTable
  const totalItems = translationResults?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = translationResults?.slice(startIndex, endIndex) || [];

  // Translated data pagination calculations
  const totalTranslatedItems = translatedData?.length || 0;
  const totalTranslatedPages = React.useMemo(() => {
    return Math.ceil(totalTranslatedItems / translatedItemsPerPage);
  }, [totalTranslatedItems, translatedItemsPerPage]);
  const translatedStartIndex = (translatedCurrentPage - 1) * translatedItemsPerPage;
  const translatedEndIndex = translatedStartIndex + translatedItemsPerPage;
  
  
  
  

  // NOT: translationResults değiştiğinde sayfa sıfırlanmasın (kullanıcı aynı sayfada kalsın)
  // Sadece search term değiştiğinde sayfa sıfırlansın

  // Reset pagination when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Note: Removed automatic page reset when translated data changes
  // to preserve pagination state during data refreshes

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (goToRowTimeoutRef.current) {
        clearTimeout(goToRowTimeoutRef.current);
      }
    };
  }, []);

  // Update output file function
  const updateOutputFile = useCallback(async (outputFileId: string, documentId: string) => {
    try {
      console.log('update_output_file_id endpointine gönderiliyor:', { output_file_id: outputFileId, document_id: documentId });
      
      const response = await fetch('https://n8n.mahir.ai/webhook/update_output_file_id', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          output_file_id: outputFileId,
          document_id: documentId
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('update_output_file_id başarılı:', result);
        
        // Eğer response "success" ise, kalite kontrol indirme butonunu aktif et
        if (result.response === 'success') {
          console.log('Kalite kontrol indirme butonu aktif hale getiriliyor');
          setIsQualityControlDownloadEnabled(true);
          success('Güncelleme Başarılı', 'Output file ID başarıyla güncellendi. Kalite kontrol sonuçları indirilebilir.');
        } else {
          success('Güncelleme Başarılı', 'Output file ID başarıyla güncellendi.');
        }
      } else {
        console.error('update_output_file_id hatası:', response.status, response.statusText);
        showError('Güncelleme Hatası', 'Output file ID güncellenirken hata oluştu.');
      }
    } catch (error) {
      console.error('update_output_file_id çağrısında hata:', error);
      showError('Güncelleme Hatası', 'Output file ID güncellenirken hata oluştu.');
    }
  }, [success, showError]);

  // Quality control functions
  const checkQualityControlStatus = useCallback(async () => {
    if (!selectedDocumentId || isCheckingQualityControl) {
      return;
    }

    // Eğer status zaten completed ise, tekrar API'ye gitme
    if (qualityControlStatus?.status === 'completed') {
      console.log('Kalite kontrol durumu cache\'den kullanılıyor:', qualityControlStatus?.status);
      return;
    }

    setIsCheckingQualityControl(true);

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/batch_get_status_front', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId
        }),
      });

      if (response.ok) {
        try {
          const data = await response.json();
          
          // Basit refresh sistemi
          if (!data || data.status === null || data.status === undefined) {
          // Null gelirse flag at (sadece bir kere)
          if (!needsRefreshRef.current) {
            console.log('🔴 NULL geldi, refresh flag atıldı');
            logRefreshChange(true, 'NULL geldi');
          }
          } else {
          // Null dışında bir şey geldi
          console.log('🟡 Geçerli status:', data.status, 'needsRefresh:', needsRefreshRef.current);
          
          // Eğer refresh flag'i varsa, datayı tazele (sadece bir kere)
          if (needsRefreshRef.current) {
            console.log('🟢 NULL\'dan sonra ilk geçerli değer geldi, datayı tazeliyorum');
            console.log('🔍 Debug değerler:', { 
              selectedDocumentId, 
              selectedDocumentName, 
              hasRef: !!fetchTranslatedDataRef.current 
            });
            
            if (selectedDocumentId && selectedDocumentName && fetchTranslatedDataRef.current) {
              fetchTranslatedDataRef.current(selectedDocumentId, selectedDocumentName, selectedDocumentSourceLanguage || undefined, selectedDocumentDestinationLanguage || undefined);
              console.log('🔄 Data tazelendi');
            } else {
              console.log('❌ Refresh koşulları sağlanmadı:', {
                hasDocumentId: !!selectedDocumentId,
                hasDocumentName: !!selectedDocumentName,
                hasRef: !!fetchTranslatedDataRef.current
              });
            }
              
              // Flag'i kapat (bir daha refresh yapılmayacak)
              logRefreshChange(false, 'refresh tamamlandı');
              console.log('✅ Refresh flag kapatıldı, bir daha refresh yapılmayacak');
            }
          }
          
          setQualityControlStatus(data);
          console.log('Kalite kontrol durumu:', data);
          
          // Eğer completed gelirse, update_output_file_id endpointini çağır
          if (data.status === 'completed') {
          console.log('Kalite kontrol tamamlandı, update_output_file_id çağrılacak');
          
          // output_file_id ve document_id değerlerini kontrol et
          if (data.output_file_id && selectedDocumentId) {
            await updateOutputFile(data.output_file_id, selectedDocumentId);
          } else {
            console.warn('output_file_id veya document_id eksik:', { 
              output_file_id: data.output_file_id, 
              document_id: selectedDocumentId 
            });
          }
        }
        
        // Eğer completed gelirse, interval'ı durdur
        if (data.status === 'completed') {
          console.log('Kalite kontrol tamamlandı, interval durdurulacak:', data.status);
          if (qualityControlIntervalRef.current) {
            clearInterval(qualityControlIntervalRef.current);
            qualityControlIntervalRef.current = null;
          }
          
          // Otomatik olarak kalite kontrol sonuçlarını indir
          console.log('Kalite kontrol tamamlandı, otomatik indirme başlatılıyor...');
          setTimeout(() => {
            loadQualityControlResults();
          }, 1000); // 1 saniye bekle ki UI güncellensin
        }
        } catch (jsonError) {
          // JSON parse hatası - bu da null olarak sayılır
          console.log('🔴 JSON parse hatası, null olarak işleniyor:', jsonError);
          if (!needsRefreshRef.current) {
            console.log('🔴 JSON hatası nedeniyle refresh flag atıldı');
            logRefreshChange(true, 'JSON hatası');
          }
          setQualityControlStatus(null);
        }
      } else {
        console.error('Kalite kontrol durumu alınırken hata:', response.status, response.statusText);
        
        // API hatası durumunda da refresh flag at (sadece bir kere)
        if (!needsRefreshRef.current) {
          console.log('🔴 API hatası, refresh flag atıldı');
          logRefreshChange(true, 'API hatası');
        }
      }
    } catch (error) {
      console.error('Kalite kontrol durumu alınırken hata:', error);
    } finally {
      setIsCheckingQualityControl(false);
    }
  }, [selectedDocumentId, updateOutputFile, selectedDocumentName]);

  const startQualityControlPolling = useCallback(() => {
    // Önce mevcut interval'ı temizle
    if (qualityControlIntervalRef.current) {
      clearInterval(qualityControlIntervalRef.current);
    }

    // İlk kontrolü hemen yap
    checkQualityControlStatus();

    // 10 saniyede bir kontrol et
    qualityControlIntervalRef.current = setInterval(() => {
      checkQualityControlStatus();
    }, 10000);

    console.log('Kalite kontrol polling başlatıldı (10 saniye interval)');
  }, [checkQualityControlStatus]);

  const stopQualityControlPolling = useCallback(() => {
    if (qualityControlIntervalRef.current) {
      clearInterval(qualityControlIntervalRef.current);
      qualityControlIntervalRef.current = null;
      console.log('Kalite kontrol polling durduruldu');
    }
  }, []);

  // Trigger batch translate when status info is not available
  const triggerBatchTranslate = useCallback(async () => {
    if (!selectedDocumentId || isTriggeringBatchTranslate || isBatchTranslateTriggered) {
      return;
    }

    setIsTriggeringBatchTranslate(true);

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/front_batch_translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('front_batch_translate response:', data);
        
        // Check if response is an array with success: true
        if (Array.isArray(data) && data.length > 0 && data[0].success === true) {
          setIsBatchTranslateTriggered(true);
          success('Başarılı', 'Kalite kontrol işlemi başlatıldı');
          console.log('Batch translate başarıyla tetiklendi');
        } else {
          showError('Hata', 'Kalite kontrol işlemi başlatılamadı');
          console.error('Beklenmeyen yanıt formatı:', data);
        }
      } else {
        showError('Hata', 'Kalite kontrol işlemi başlatılamadı');
        console.error('front_batch_translate hatası:', response.status, response.statusText);
      }
    } catch (error) {
      showError('Hata', 'Kalite kontrol işlemi başlatılırken bir hata oluştu');
      console.error('front_batch_translate çağrısında hata:', error);
    } finally {
      setIsTriggeringBatchTranslate(false);
    }
  }, [selectedDocumentId, isTriggeringBatchTranslate, isBatchTranslateTriggered, success, showError]);

  // Önceki döküman ID'sini takip etmek için ref
  const prevDocumentIdRef = useRef<string | null>(null);

  // Scrollbar genişliğini hesaplamak için ref
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(0);

  // Scrollbar genişliğini hesapla
  useEffect(() => {
    const calculateScrollbarWidth = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        setScrollbarWidth(scrollbarWidth);
      }
    };

    calculateScrollbarWidth();
    
    // Resize olduğunda yeniden hesapla
    window.addEventListener('resize', calculateScrollbarWidth);
    return () => window.removeEventListener('resize', calculateScrollbarWidth);
  }, [translatedData]);

  // Close user filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userFilterDropdownRef.current && !userFilterDropdownRef.current.contains(event.target as Node)) {
        setShowUserFilterDropdown(false);
      }
    };

    if (showUserFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserFilterDropdown]);

  // Close comment user filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (commentUserFilterDropdownRef.current && !commentUserFilterDropdownRef.current.contains(event.target as Node)) {
        setShowCommentUserFilterDropdown(false);
      }
    };

    if (showCommentUserFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCommentUserFilterDropdown]);

  // Close violation filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (violationFilterDropdownRef.current && !violationFilterDropdownRef.current.contains(event.target as Node)) {
        setShowViolationFilterDropdown(false);
      }
    };

    if (showViolationFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showViolationFilterDropdown]);

  // Reset quality control status when document changes and start polling
  React.useEffect(() => {
    // Sadece farklı bir döküman açıldığında kalite kontrol verilerini sıfırla
    const isDifferentDocument = prevDocumentIdRef.current !== selectedDocumentId;
    
    if (isDifferentDocument) {
      setQualityControlStatus(null);
      setShowQualityControlPopover(false);
      setIsQualityControlDownloadEnabled(false); // İndirme butonunu sıfırla
      
      // Sadece gerçekten farklı bir döküman açıldığında (null'dan farklı bir değere geçişte) kalite kontrol sonuçlarını sıfırla
      if (prevDocumentIdRef.current !== null) {
        setQualityControlResults(null); // Kalite kontrol sonuçlarını sıfırla
      }
      
      setShowQualityDetailPopover(false); // Detay popover'ını kapat
      setSelectedQualityResult(null); // Seçili sonucu sıfırla
      
      // Döküman değişti, refresh flag'ini sıfırla
      console.log('📄 Döküman değişti, refresh flag sıfırlandı', { 
        oldDocId: prevDocumentIdRef.current, 
        newDocId: selectedDocumentId
      });
      logRefreshChange(false, 'döküman değişti');
      
      // Önceki döküman ID'sini güncelle
      prevDocumentIdRef.current = selectedDocumentId;
    }
    
    // Eğer doküman seçilmişse polling'i başlat
    if (selectedDocumentId) {
      startQualityControlPolling();
    } else {
      stopQualityControlPolling();
    }

    // Cleanup function - component unmount olduğunda veya doküman değiştiğinde
    return () => {
      stopQualityControlPolling();
    };
  }, [selectedDocumentId]);

  // Component unmount olduğunda polling'i durdur
  React.useEffect(() => {
    return () => {
      stopQualityControlPolling();
    };
  }, []);


  // Helper function to format date and time
  const formatDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return '';
    
    // dateStr format: YYYYMMDD (e.g., "20251008")
    // timeStr format: HHMM (e.g., "1751")
    
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    
    const hour = timeStr.substring(0, 2);
    const minute = timeStr.substring(2, 4);
    
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
    
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper functions for bulk selection
  const handleRowSelect = (sid: string) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(sid)) {
      newSelectedRows.delete(sid);
    } else {
      newSelectedRows.add(sid);
    }
    setSelectedRows(newSelectedRows);
  };


  const clearSelection = () => {
    setSelectedRows(new Set());
  };

  const startSelectionMode = () => {
    setIsSelectionMode(true);
    setSelectedRows(new Set());
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedRows(new Set());
  };

  // English text selection mode functions
  const handleEnRowSelect = (sid: string) => {
    const newSelectedEnRows = new Set(selectedEnRows);
    if (newSelectedEnRows.has(sid)) {
      newSelectedEnRows.delete(sid);
    } else {
      newSelectedEnRows.add(sid);
    }
    setSelectedEnRows(newSelectedEnRows);
  };

  const startEnSelectionMode = () => {
    setIsEnSelectionMode(true);
    setSelectedEnRows(new Set());
  };

  const exitEnSelectionMode = () => {
    setIsEnSelectionMode(false);
    setSelectedEnRows(new Set());
  };

  const handleControlRowSelect = (sid: string) => {
    const newSelectedRows = new Set(selectedControlRows);
    if (newSelectedRows.has(sid)) {
      newSelectedRows.delete(sid);
    } else {
      newSelectedRows.add(sid);
    }
    setSelectedControlRows(newSelectedRows);
  };

  // Fetch company documents function
  const fetchCompanyDocuments = useCallback(async (companyId: string | number) => {
    setIsLoadingDocuments(true);
    setDocumentsError(null);
    
    const result = await apiCall(async () => {
      console.log('get_documents endpoint\'e gönderilen company_id:', companyId);

      const response = await fetch('https://n8n.mahir.ai/webhook/get_documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: companyId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const documents = await response.json();
      console.log('get_documents endpoint\'ten dönen veriler:', documents);
      
      return documents;
    }, {
      loadingKey: 'fetchCompanyDocuments',
      loadingMessage: 'Şirket dökümanları yükleniyor...',
      overlay: false,
      successMessage: '',
      errorMessage: 'Şirket dökümanları yüklenirken hata oluştu',
      showToast: false
    });

    setIsLoadingDocuments(false);

    if (result) {
      // Boş obje kontrolü - [{}] formatında gelirse kayıt yok demektir
      const hasValidDocuments = result && Array.isArray(result) && 
        result.length > 0 && 
        result.some((doc: any) => doc && Object.keys(doc).length > 0 && doc.document_id);
      
      if (!hasValidDocuments) {
        console.log('Boş döküman listesi algılandı - kayıt yok');
        setCompanyDocuments([]);
        return [];
      }
      
      // Admin kullanıcılar tüm dökümanları görebilir, diğerleri sadece kendilerine atananları
      const filteredDocuments = user?.app_roles?.translate?.toLowerCase() === 'admin'
        ? result // Admin tüm dökümanları görür
        : user?.id
          ? result.filter((doc: any) => {
              // assigned_user tanımlı ve kullanıcının ID'si ile eşleşen dökümanları göster
              if (doc.assigned_user !== undefined && doc.assigned_user !== null) {
                const userId = user.id.toString();
                
                // assigned_user array ise (birden fazla kişi atanmış)
                if (Array.isArray(doc.assigned_user)) {
                  return doc.assigned_user.some((assignedId: any) => 
                    assignedId.toString() === userId
                  );
                }
                
                // assigned_user tek değer ise
                const assignedUserId = doc.assigned_user.toString();
                return userId === assignedUserId;
              }
              // assigned_user yoksa gösterme
              return false;
            })
          : result;
      
      console.log(`Kullanıcıya atanan ${filteredDocuments.length} döküman yüklendi (Toplam: ${result.length})`);
      
      setCompanyDocuments(filteredDocuments);
      return filteredDocuments;
    } else {
      setDocumentsError('Dökümanlar yüklenemedi');
      setCompanyDocuments(null);
      return null;
    }
  }, [apiCall]);

  // Delete document function
  const deleteDocument = useCallback(async (documentId: string) => {
    const result = await apiCall(async () => {
      console.log('delete_document endpoint\'e gönderilen document_id:', documentId);

      const response = await fetch('https://n8n.mahir.ai/webhook/delete_document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('delete_document endpoint\'ten dönen veriler:', result);
      
      return result;
    }, {
      loadingKey: 'deleteDocument',
      loadingMessage: 'Döküman siliniyor...',
      overlay: true,
      successMessage: 'Döküman başarıyla silindi',
      errorMessage: 'Döküman silinirken hata oluştu',
      showToast: true
    });

    if (result && selectedCompany?.id) {
      // Silme işlemi başarılı olursa döküman listesini yenile
      await fetchCompanyDocuments(selectedCompany.id);
    }

    // Modal'ı kapat
    setShowDeleteDocumentModal(false);
    setSelectedDocumentForDelete(null);

    return result;
  }, [apiCall, selectedCompany, fetchCompanyDocuments]);

  // Handle delete document modal
  const handleDeleteDocumentClick = useCallback((documentId: string, documentName: string) => {
    setSelectedDocumentForDelete({ id: documentId, name: documentName });
    setShowDeleteDocumentModal(true);
  }, []);

  const handleConfirmDeleteDocument = useCallback(() => {
    if (selectedDocumentForDelete) {
      deleteDocument(selectedDocumentForDelete.id);
    }
  }, [selectedDocumentForDelete, deleteDocument]);

  // Toggle document completion status
  const toggleDocumentCompletion = useCallback(async (documentId: string, currentStatus: boolean) => {
    const endpoint = currentStatus 
      ? 'https://n8n.mahir.ai/webhook/set_document_uncompleted'
      : 'https://n8n.mahir.ai/webhook/set_document_completed';

    const result = await apiCall(async () => {
      console.log(`${currentStatus ? 'Uncompleting' : 'Completing'} document:`, documentId);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Toggle completion response:', data);
      
      // Check if response is successful
      if (Array.isArray(data) && data.length > 0 && data[0]?.success === true) {
        return data;
      } else {
        throw new Error('İşlem başarısız oldu');
      }
    }, {
      loadingKey: 'toggleDocumentCompletion',
      loadingMessage: currentStatus ? 'Tamamlanmadı olarak işaretleniyor...' : 'Tamamlandı olarak işaretleniyor...',
      overlay: false,
      successMessage: currentStatus ? 'Döküman tamamlanmadı olarak işaretlendi' : 'Döküman tamamlandı olarak işaretlendi',
      errorMessage: 'Durum değiştirilirken hata oluştu',
      showToast: true
    });

    if (result && selectedCompany?.id) {
      // Update was successful, refresh the documents list
      await fetchCompanyDocuments(selectedCompany.id);
    }

    return result;
  }, [apiCall, selectedCompany, fetchCompanyDocuments]);

  // Fetch completion data
  const fetchCompletionData = useCallback(async (documentId: string) => {
    console.log('🔄 fetchCompletionData başlatıldı:', { documentId });
    
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/check_control_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Completion data başarıyla alındı:', data);
        if (data && data.length > 0) {
          setCompletionData(data[0]);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Completion data alınırken hata:', response.status, response.statusText, errorText);
      }
    } catch (error) {
      console.error('❌ Completion data alınırken hata:', error);
    }
  }, []);

  // Fetch EN change history
  const fetchEnChangeHistory = useCallback(async (documentId: string) => {
    console.log('🔄 fetchEnChangeHistory başlatıldı:', { documentId });
    
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/get_en_log_change_history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ EN change history başarıyla alındı:', data);
        setEnChangeHistory(data || []);
      } else {
        const errorText = await response.text();
        console.error('❌ EN change history alınırken hata:', response.status, response.statusText, errorText);
        setEnChangeHistory([]);
      }
    } catch (error) {
      console.error('❌ EN change history alınırken hata:', error);
      setEnChangeHistory([]);
    }
  }, []);

  // Fetch Comments function
  const fetchComments = useCallback(async (documentId: string) => {
    console.log('💬 fetchComments başlatıldı:', { documentId });
    
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/get_comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Yorumlar başarıyla alındı:', data);
        
        // Backend'den gelen formatı rowComments state formatına dönüştür
        // Beklenen format: [{sid, user_id, username, comment, comment_time}, ...]
        const commentsMap: {[sid: string]: {user: string; userId: number; timestamp: number; comment: string}[]} = {};
        
        if (Array.isArray(data)) {
          data.forEach((item: any) => {
            if (!commentsMap[item.sid]) {
              commentsMap[item.sid] = [];
            }
            commentsMap[item.sid].push({
              user: item.username || 'Kullanıcı',
              userId: item.user_id,
              timestamp: item.comment_time,
              comment: item.comment
            });
          });
        }
        
        setRowComments(commentsMap);
      } else {
        const errorText = await response.text();
        console.error('❌ Yorumlar alınırken hata:', response.status, response.statusText, errorText);
        setRowComments({});
      }
    } catch (error) {
      console.error('❌ Yorumlar alınırken hata:', error);
      setRowComments({});
    }
  }, []);

  // Fetch translated data function
  const fetchTranslatedData = useCallback(async (documentId: string, documentName: string, sourceLanguage?: string, destinationLanguage?: string) => {
    console.log('🔄 fetchTranslatedData başlatıldı:', { documentId, documentName });
    setTranslatedDataError(null);
    
    // Doküman değiştiğinde kalite kontrol verilerini temizle
    console.log('🧹 Kalite kontrol verileri temizleniyor - doküman değişimi');
    setQualityControlResults(null);
    setQualityControlDocumentId(null);
    setQualityControlStatus(null);
    setIsCheckingQualityControl(false);
    setIsLoadingQualityResults(false);
    setIsQualityControlDownloadEnabled(false);
    
    // Kalite kontrol interval'ını temizle
    if (qualityControlIntervalRef.current) {
      clearInterval(qualityControlIntervalRef.current);
      qualityControlIntervalRef.current = null;
    }

    // Önce doküman hata durumunu kontrol et
    const errorCheckResult = await apiCall(async () => {
      console.log('get_document_error_code endpoint\'e gönderilen document_id:', documentId);

      const response = await fetch('https://n8n.mahir.ai/webhook/get_document_error_code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('get_document_error_code endpoint\'ten dönen veriler:', data);
      
      return data;
    }, {
      loadingKey: 'checkDocumentError',
      loadingMessage: 'Doküman durumu kontrol ediliyor...',
      overlay: true,
      successMessage: '',
      errorMessage: 'Doküman durumu kontrol edilirken hata oluştu',
      showToast: false
    });

    // Hata kontrolü sonucuna göre akışı yönet
    if (errorCheckResult) {
      let errorResponse;
      
      // API'den dönen veri formatını kontrol et (array veya direkt obje olabilir)
      if (Array.isArray(errorCheckResult) && errorCheckResult.length > 0) {
        errorResponse = errorCheckResult[0]?.response;
      } else if (errorCheckResult.response) {
        errorResponse = errorCheckResult.response;
      }
      
      console.log('🔍 Doküman hata durumu:', errorResponse);
      
      if (errorResponse === 'TIMEOUT') {
        console.log('⏰ Doküman TIMEOUT hatası algılandı');
        setTranslatedDataError('Çeviri verisi bulunamadı. Doküman işleme süresi aşıldı.');
        setTranslatedData(null);
        setShowTranslatedDataView(true); // Hata mesajını tablo alanında göstermek için true
        return;
      }
      
      if (errorResponse === 'MAX_TOKENS') {
        console.log('📄 Doküman MAX_TOKENS hatası algılandı');
        setTranslatedDataError('Dosya boyutu çok büyük. Lütfen daha küçük bir dosya yükleyin.');
        setTranslatedData(null);
        setShowTranslatedDataView(true); // Hata mesajını tablo alanında göstermek için true
        return;
      }
      
      if (errorResponse === 'success') {
        console.log('✅ Doküman durumu başarılı, çeviri verilerini getiriliyor');
        // SUCCESS durumunda normal akışa devam et
      } else {
        console.log('⚠️ Bilinmeyen doküman durumu:', errorResponse);
        // Bilinmeyen durumlarda da devam et
      }
    }
    
    // Normal çeviri verilerini getir
    const result = await apiCall(async () => {
      console.log('get_translated_data endpoint\'e gönderilen document_id:', documentId);

      const response = await fetch('https://n8n.mahir.ai/webhook/get_translated_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('get_translated_data endpoint\'ten dönen veriler:', data);
      
      return data;
    }, {
      loadingKey: 'fetchTranslatedData',
      loadingMessage: 'Çeviri verileri yükleniyor...',
      overlay: true,
      successMessage: '',
      errorMessage: 'Çeviri verileri yüklenirken hata oluştu',
      showToast: false
    });


    if (result) {
      // Boş obje kontrolü - [{}] formatında gelirse kayıt yok demektir
      const hasValidData = result && Array.isArray(result) && 
        result.length > 0 && 
        result.some((item: any) => item && Object.keys(item).length > 0 && item.sid);
      
      if (!hasValidData) {
        console.log('Boş çeviri verisi algılandı - kayıt yok');
      }
      
      setTranslatedData(hasValidData ? result : []);
      setSelectedDocumentId(documentId);
      setSelectedDocumentName(documentName);
      setSelectedDocumentSourceLanguage(sourceLanguage || null);
      setSelectedDocumentDestinationLanguage(destinationLanguage || null);
      setShowTranslatedDataView(true);
      console.log('✅ fetchTranslatedData başarıyla tamamlandı, tablo güncellendi');
      console.log('🔄 İngilizce metin edit işlevleri fresh data ile birlikte aktif hale geldi');
      
      // Fetch completion data, change history, and comments
      await fetchCompletionData(documentId);
      await fetchEnChangeHistory(documentId);
      await fetchComments(documentId);
    } else {
      setTranslatedDataError('Çeviri verileri yüklenemedi');
      setTranslatedData(null);
      console.log('❌ fetchTranslatedData başarısız oldu');
    }
  }, [apiCall, fetchCompletionData, fetchEnChangeHistory, fetchComments]);

  // fetchTranslatedData ref'e ata
  useEffect(() => {
    fetchTranslatedDataRef.current = fetchTranslatedData;
  }, [fetchTranslatedData]);

  // Debug: needsRefresh değişimlerini logla (ref için)
  const logRefreshChange = (newValue: boolean, reason: string) => {
    needsRefreshRef.current = newValue;
    console.log(`🏁 needsRefresh ${reason}:`, newValue);
  };

  // Load documents when translations tab is opened
  React.useEffect(() => {
    if (selectedCompany && selectedOption === 'translations') {
      const companyId = selectedCompany.company_id || selectedCompany.id;
      fetchCompanyDocuments(companyId);
    }
  }, [selectedCompany, selectedOption]);

  // Translate function
  const handleTranslate = async () => {
    if (!currentDocumentId) return setStatusMessage('Document ID bulunamadı.');

    // Butona basılır basılmaz modal'ı göster ve state'leri temizle
    setTranslationResults(null); // Tabloyu temizle
    setEditingIndex(null);
    setEditingText('');
    setStatusMessage('');
    setUploadProgress(0);
    setIsUploading(false);
    setShowTranslateSuccessModal(true);

    // Company ID'yi akıllı şekilde belirle
    let companyId = '';
    
    if (selectedCompany?.company_id) {
      // Gerçek company_id varsa onu kullan
      companyId = selectedCompany.company_id.toString();
    } else if (selectedCompany?.name) {
      // Sadece name varsa onu kullan
      companyId = selectedCompany.name;
    } else {
      // Hiçbiri yoksa default
      companyId = 'default_company';
    }
    
    console.log('Translate endpoint\'e gönderilen parametreler:', {
      document_id: currentDocumentId,
      company_id: companyId,
      selectedCompany
    });

    // Webhook'u arka planda çağır - yanıtını bekleme ve hata durumunda bile modal'ı kapatma
    fetch('https://n8n.mahir.ai/webhook/translate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        document_id: currentDocumentId,
        company_id: companyId
      })
    }).then(response => {
      if (response.ok) {
        console.log('Çeviri işlemi başarıyla başlatıldı');
        success('Çeviri Başlatıldı', 'Çeviri işlemi başarıyla başlatıldı');
      } else {
        console.error('Çeviri işlemi başlatılamadı:', response.status, response.statusText);
        showError('Çeviri Hatası', 'Çeviri işlemi başlatılırken hata oluştu');
      }
    }).catch(error => {
      console.error('Çeviri işlemi başlatılırken hata:', error);
      showError('Çeviri Hatası', 'Çeviri işlemi başlatılırken hata oluştu');
    });
  };


  // Çevirileri görüntüle ekranına yönlendir ve ilgili dökümanı aç
  const handleGoToTranslations = async () => {
    setIsLoadingTranslationView(true);
    setShowTranslateSuccessModal(false);
    
    try {
      // Çevirileri görüntüle sekmesine geç
      setSelectedOption('translations');
      
      // Şirket dökümanlarını yenile ve sonucu bekle
      if (selectedCompany && currentDocumentId) {
        const companyId = selectedCompany.company_id || selectedCompany.id;
        const documents = await fetchCompanyDocuments(companyId);
        
        // Dökümanlar yüklendikten sonra eşleşen dökümanı bul ve aç
        if (documents && Array.isArray(documents)) {
          const document = documents.find((doc: any) => doc.document_id === currentDocumentId);
          if (document) {
            console.log('Eşleşen döküman bulundu:', document);
            await fetchTranslatedData(currentDocumentId, document.file_name_no_ext);
          } else {
            console.log('Eşleşen döküman bulunamadı. Aranan document_id:', currentDocumentId);
            console.log('Mevcut dökümanlar:', documents);
          }
        }
      }
    } catch (error) {
      console.error('Çeviri görüntüleme hatası:', error);
    } finally {
      setIsLoadingTranslationView(false);
    }
  };

  // Helper function to parse runs string and extract text
  // Memoized parsing with cache
  const parseCache = useRef<Map<string, string>>(new Map());
  
  const parseRunsText = useCallback((runsString: string): string => {
    if (!runsString) return '';
    
    // Cache'den kontrol et
    if (parseCache.current.has(runsString)) {
      return parseCache.current.get(runsString)!;
    }
    
    try {
      const runs = JSON.parse(runsString);
      if (Array.isArray(runs) && runs.length > 0) {
        const result = runs.map(run => run.text || '').join(' ');
        parseCache.current.set(runsString, result);
        return result;
      }
      parseCache.current.set(runsString, runsString);
      return runsString;
    } catch (error) {
      console.error('Error parsing runs:', error);
      parseCache.current.set(runsString, runsString);
      return runsString;
    }
  }, []);

  // Helper function to create runs string from text
  const createRunsString = (text: string, originalEnRuns?: string): string => {
    // Mevcut en_runs'dan bold değerini korumaya çalış
    let boldValue = false;
    
    if (originalEnRuns) {
      try {
        const parsed = JSON.parse(originalEnRuns);
        if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0].bold === 'boolean') {
          boldValue = parsed[0].bold;
        }
      } catch (e) {
        console.warn('En_runs parse edilemedi, bold: false kullanılıyor', e);
      }
    }
    
    return JSON.stringify([{ text: text, bold: boldValue }]);
  };

  // Edit functions
  const handleEditStart = (index: number, currentText: string) => {
    setEditingIndex(index);
    setEditingText(currentText);
  };

  // Calculate textarea rows based on text length
  const calculateRows = (text: string): number => {
    const lineBreaks = (text.match(/\n/g) || []).length;
    const estimatedLines = Math.ceil(text.length / 80); // Approximate 80 chars per line
    return Math.max(1, Math.min(lineBreaks + estimatedLines, 4)); // Min 1, max 4 rows
  };

  const handleEditSave = async (index: number) => {
    if (translationResults) {
      const newRuns = createRunsString(editingText);
      const sid = translationResults[index].sid;
      
      // Log changes endpoint'ine gönder (bu tabloyu da yenileyecek)
      await logChanges(sid, newRuns);
    }
    setEditingIndex(null);
    setEditingText('');
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingText('');
  };

  // Toggle is_checked value for a specific row
  const handleToggleIsChecked = async (sid: string) => {
    if (!translatedData) return;
    
    const updatedData = translatedData.map(item => {
      if (item.sid === sid) {
        const newIsChecked = !item.is_checked;
        return { ...item, is_checked: newIsChecked };
      }
      return item;
    });
    
    setTranslatedData(updatedData);
    
    // Backend'e is_checked değerini gönder
    try {
      const currentItem = translatedData.find(item => item.sid === sid);
      const newIsChecked = !currentItem?.is_checked;
      
      console.log(`Updating is_checked for sid ${sid} to ${newIsChecked}`);
      
      const response = await fetch('https://n8n.mahir.ai/webhook/update_is_checked', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId,
          sid: sid,
          is_checked: newIsChecked,
          user_id: Number(user?.id),
          timestamp: Math.floor(Date.now() / 1000)
        })
      });

      if (response.ok) {
        console.log('is_checked değeri başarıyla güncellendi');
      } else {
        console.error('is_checked güncellenirken hata:', response.status, response.statusText);
        // Hata durumunda eski state'e geri dön
        setTranslatedData(translatedData);
      }
    } catch (error) {
      console.error('is_checked güncellenirken hata:', error);
      // Hata durumunda eski state'e geri dön
      setTranslatedData(translatedData);
    }
  };

  // Bulk update checked rows
  const updateCheckedRows = async () => {
    try {
      if (!selectedDocumentId || selectedControlRows.size === 0) {
        console.warn('Document ID veya seçili satırlar bulunamadı');
        return;
      }

      const sidArray = Array.from(selectedControlRows);
      
      // Her sid için yeni is_checked değerini hesapla (toggle)
      const checkParameterArray = sidArray.map(sid => {
        const currentItem = translatedData?.find(item => item.sid === sid);
        return !currentItem?.is_checked; // Toggle: false -> true, true -> false
      });
      
      // Format sids as requested: ('sid1', 'sid2', 'sid3')
      const formattedSids = `(${sidArray.map(sid => `'${sid}'`).join(', ')})`;
      
      // Format check_parameter as requested: (true, false, true)
      const formattedCheckParameter = `(${checkParameterArray.join(', ')})`;
      
      console.log('Bulk update checked rows gönderiliyor:', {
        document_id: selectedDocumentId,
        sids: formattedSids,
        check_parameter: formattedCheckParameter
      });
      
      const response = await fetch('https://n8n.mahir.ai/webhook/update_checked_rows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId,
          sids: formattedSids,
          check_parameter: formattedCheckParameter,
          user_id: Number(user?.id),
          timestamp: Math.floor(Date.now() / 1000)
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Update checked rows response:', responseText);
        
        if (responseText && responseText.trim()) {
          const jsonResponse = JSON.parse(responseText);
          
          // Success mesajını kontrol et (array içinde geliyor)
          if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
            console.log('Checked rows başarıyla güncellendi');
            
            // Seçimi temizle
            setSelectedControlRows(new Set());
            
            // Tablo datasını yenile
            if (selectedDocumentId && selectedDocumentName) {
              await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
            }
            
            success('Seçili satırlar başarıyla güncellendi');
          } else {
            console.error('Update checked rows başarısız:', jsonResponse);
            showError('Satırlar güncellenirken hata oluştu');
          }
        } else {
          console.log('Sunucudan boş response geldi');
          showError('Sunucudan geçersiz yanıt alındı');
        }
      } else {
        console.error('Update checked rows gönderilirken hata:', response.status, response.statusText);
        showError('Satırlar güncellenirken hata oluştu');
      }
    } catch (error) {
      console.error('Update checked rows gönderilirken hata:', error);
      showError('Satırlar güncellenirken hata oluştu');
    }
  };

  // Go to row function
  const handleGoToRow = (rowNumber: number, targetSid: string) => {
    if (!translationResults || rowNumber < 1 || rowNumber > translationResults.length) {
      return;
    }

    // Calculate which page the row is on
    const targetPage = Math.ceil(rowNumber / itemsPerPage);
    
    // Set the current page to show the target row
    setCurrentPage(targetPage);
    
    // Scroll to the target row after page change
    setTimeout(() => {
      const targetElement = document.getElementById(`row-${targetSid}`);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100); // Small delay to ensure page has changed
  };

  // English text edit functions
  const handleEnEditStart = (sid: string, currentText: string) => {
    setEditingEnSid(sid);
    setEditingEnText(currentText);
  };

  const handleEnEditSave = async (sid: string) => {
    if (translatedData && editingEnSid) {
      // Mevcut satırın en_runs değerini al
      const currentRow = translatedData.find(item => item.sid === sid);
      const originalEnRuns = currentRow?.en_runs;
      
      const newEnRuns = createRunsString(editingEnText, originalEnRuns);
      
      try {
        if (!selectedDocumentId || !user?.id) {
          console.warn('Document ID veya User ID bulunamadı');
          return;
        }

        const logData = {
          sid: sid,
          en_runs: newEnRuns,
          document_id: selectedDocumentId,
          user_id: user.id,
          timestamp: Math.floor(Date.now() / 1000)
        };

        console.log('EN Log changes gönderiliyor:', logData);

        const response = await fetch('https://n8n.mahir.ai/webhook/en_log_changes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logData)
        });

        if (response.ok) {
          console.log('EN Log changes başarıyla gönderildi');
          
          // Only refresh translated data, preserve pagination and sorting
          if (selectedDocumentId && selectedDocumentName) {
            await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
          }
        } else {
          console.error('EN Log changes gönderilirken hata:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('EN Log changes gönderilirken hata:', error);
      }
    }
    setEditingEnSid(null);
    setEditingEnText('');
  };

  const handleEnEditCancel = () => {
    setEditingEnSid(null);
    setEditingEnText('');
  };

  // Add comment function
  const handleAddComment = async (sid: string, commentText: string) => {
    if (!selectedDocumentId || !user?.id || !commentText.trim()) {
      return;
    }

    const commentTime = Math.floor(Date.now() / 1000);
    
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/add_comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: Number(user.id),
          sid: sid,
          document_id: selectedDocumentId,
          comment: commentText.trim(),
          comment_time: commentTime
        })
      });

      if (response.ok) {
        console.log('✅ Yorum başarıyla eklendi');
        
        // Fetch fresh comments from backend (no cache)
        await fetchComments(selectedDocumentId);
        
        success('Başarılı', 'Yorum eklendi');
        return true;
      } else {
        console.error('❌ Yorum eklenirken hata:', response.status, response.statusText);
        showError('Hata', 'Yorum eklenirken bir hata oluştu');
        return false;
      }
    } catch (error) {
      console.error('❌ Yorum eklenirken hata:', error);
      showError('Hata', 'Yorum eklenirken bir hata oluştu');
      return false;
    }
  };

  // Delete all comments in document
  const handleDeleteAllDocumentComments = async () => {
    if (!selectedDocumentId) {
      return false;
    }

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/delete_comments_by_document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId
        })
      });

      if (response.ok) {
        console.log('✅ Dokümandaki tüm yorumlar silindi');
        
        // Fetch fresh comments from backend
        await fetchComments(selectedDocumentId);
        
        success('Başarılı', 'Dokümandaki tüm yorumlar silindi');
        return true;
      } else {
        console.error('❌ Yorumlar silinirken hata:', response.status, response.statusText);
        showError('Hata', 'Yorumlar silinirken bir hata oluştu');
        return false;
      }
    } catch (error) {
      console.error('❌ Yorumlar silinirken hata:', error);
      showError('Hata', 'Yorumlar silinirken bir hata oluştu');
      return false;
    }
  };

  // Delete all comments in a row (by sid)
  const handleDeleteRowComments = async (sid: string) => {
    if (!selectedDocumentId) {
      return false;
    }

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/delete_comments_by_sid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId,
          sid: sid
        })
      });

      if (response.ok) {
        console.log('✅ Satırdaki tüm yorumlar silindi');
        
        // Fetch fresh comments from backend
        await fetchComments(selectedDocumentId);
        
        success('Başarılı', 'Satırdaki tüm yorumlar silindi');
        return true;
      } else {
        console.error('❌ Yorumlar silinirken hata:', response.status, response.statusText);
        showError('Hata', 'Yorumlar silinirken bir hata oluştu');
        return false;
      }
    } catch (error) {
      console.error('❌ Yorumlar silinirken hata:', error);
      showError('Hata', 'Yorumlar silinirken bir hata oluştu');
      return false;
    }
  };

  // Delete specific comment by sid and epoch time
  const handleDeleteComment = async (sid: string, commentTime: number) => {
    if (!selectedDocumentId) {
      return false;
    }

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/delete_comments_by_sid_epoch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId,
          sid: sid,
          comment_time: commentTime
        })
      });

      if (response.ok) {
        console.log('✅ Yorum silindi');
        
        // Fetch fresh comments from backend
        await fetchComments(selectedDocumentId);
        
        success('Başarılı', 'Yorum silindi');
        return true;
      } else {
        console.error('❌ Yorum silinirken hata:', response.status, response.statusText);
        showError('Hata', 'Yorum silinirken bir hata oluştu');
        return false;
      }
    } catch (error) {
      console.error('❌ Yorum silinirken hata:', error);
      showError('Hata', 'Yorum silinirken bir hata oluştu');
      return false;
    }
  };

  // Drag and drop handlers for English text
  const handleDragStart = (e: React.DragEvent, sid: string, enText: string) => {
    // Only allow drag if there's English text content
    if (!enText || enText.trim() === '' || enText === '[]') {
      e.preventDefault();
      return;
    }
    
    setDraggedRowSid(sid);
    setIsDragging(true);
    
    // Set drag data
    e.dataTransfer.setData('text/plain', sid);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = () => {
    setDraggedRowSid(null);
    setDragOverRowSid(null);
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent, sid: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Don't allow dropping on the same row
    if (draggedRowSid !== sid) {
      setDragOverRowSid(sid);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the row entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverRowSid(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetSid: string) => {
    e.preventDefault();
    
    const sourceSid = e.dataTransfer.getData('text/plain');
    
    if (!sourceSid || sourceSid === targetSid || !translatedData) {
      setDragOverRowSid(null);
      return;
    }

    // Find source and target rows
    const sourceRow = translatedData.find(item => item.sid === sourceSid);
    const targetRow = translatedData.find(item => item.sid === targetSid);
    
    if (!sourceRow || !targetRow) {
      setDragOverRowSid(null);
      return;
    }

    // Get the English text from source row
    const sourceEnText = parseRunsText(sourceRow.en_runs) as string;
    
    if (!sourceEnText || sourceEnText.trim() === '') {
      setDragOverRowSid(null);
      return;
    }

    // Get the current English text from target row
    const targetEnText = parseRunsText(targetRow.en_runs) as string;
    
    // Combine texts: target text + source text
    const combinedText = targetEnText && targetEnText.trim() !== '' && targetEnText !== '[]' 
      ? `${targetEnText} ${sourceEnText}` 
      : sourceEnText;

    try {
      // Update target row with combined text (preserve bold from target)
      const newTargetEnRuns = createRunsString(combinedText, targetRow.en_runs);
      await logEnChanges(targetSid, newTargetEnRuns);

      // Clear source row English text (preserve bold from source)
      const emptyEnRuns = createRunsString('', sourceRow.en_runs);
      await logEnChanges(sourceSid, emptyEnRuns);

      // Show success message
      success('İngilizce metin başarıyla taşındı');
      
    } catch (error) {
      console.error('Drag and drop error:', error);
      showError('Metin taşıma sırasında hata oluştu');
    }

    setDragOverRowSid(null);
  };

  // Get serialized data from endpoint
  const getSerializedData = async (documentId?: string) => {
    const docId = documentId || currentDocumentId;
    console.log('getSerializedData fonksiyonu çağrıldı');
    console.log('Kullanılacak document ID:', docId);
    console.log('currentDocumentId state değeri:', currentDocumentId);
    
    if (!docId) {
      console.warn('Document ID bulunamadı, getSerializedData çıkılıyor');
      return;
    }

    const result = await apiCall(async () => {
      console.log('Serialized data çekiliyor, document_id:', docId);

      const response = await fetch('https://n8n.mahir.ai/webhook/get_serialized_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify({
          document_id: docId
        })
      });

      if (!response.ok) {
        throw new Error(`Veri yükleme hatası: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('Get serialized data response:', responseText);
      
      if (!responseText || !responseText.trim()) {
        throw new Error('Sunucudan boş response geldi');
      }

      const jsonResponse = JSON.parse(responseText);
      
      if (!Array.isArray(jsonResponse)) {
        throw new Error('Beklenmeyen response formatı');
      }

      return jsonResponse;
    }, {
      loadingKey: 'getSerializedData',
      loadingMessage: 'Veriler yükleniyor...',
      overlay: false,
      successMessage: 'Veriler başarıyla yüklendi',
      errorMessage: 'Veri yükleme sırasında hata oluştu'
    });

    if (result) {
      console.log('🔄 Yeni translation results set ediliyor, data count:', result.length);
      console.log('🔄 İlk 3 satır:', result.slice(0, 3).map((r: any) => ({ sid: r.sid, runs: r.runs?.substring(0, 50) })));
      
      // Direct update to preserve scroll position - no clearing first
      setTranslationResults([...result]); // Set new data directly
      setTableRefreshKey((prev: number) => prev + 1); // Force table re-render
      console.log('✅ Translation results güncellendi (scroll pozisyonu korundu)');
      
      setStatusMessage('Veriler başarıyla yüklendi.');
      // NOT: currentPage state'i korunuyor, sayfa değişmiyor
    }
  };

  // Download deserialize file
  const downloadDeserializeFile = async (documentId: string, fileName: string) => {
    const controller = new AbortController();
    let timeoutId: number | null = null;

    try {
      setIsDownloading(true);
      setDownloadProgress(20);
      
      console.log('Deserialize file indiriliyor, document_id:', documentId);

      timeoutId = window.setTimeout(() => controller.abort(), 5 * 60 * 1000);

      const response = await fetch('https://n8n.mahir.ai/webhook/deserialize_file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        }),
        signal: controller.signal
      });

      setDownloadProgress(60);

      if (!response.ok) {
        throw new Error(`Dosya indirme hatası: ${response.status}`);
      }

      // Binary dosyayı blob olarak al
      const blob = await response.blob();
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      
      setDownloadProgress(80);
      
      // Dosyayı indir
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${fileName}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setDownloadProgress(100);
      success('Başarılı', 'Dosya başarıyla indirildi');
      
      // Modal'ı kapat
      setTimeout(() => {
        setShowDownloadConfirmModal(false);
        setSelectedDocumentForDownload(null);
        setDownloadProgress(0);
        setIsDownloading(false);
      }, 1500);
      
    } catch (error) {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      console.error('Dosya indirme hatası:', error);
      showError('Hata', 'Dosya indirme sırasında bir hata oluştu');
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Fetch company documents for comparison
  const fetchCompanyDocumentsForCompare = async () => {
    if (!selectedCompany) {
      showError('Hata', 'Şirket seçilmemiş');
      return;
    }

    if (!selectedDocumentId) {
      showError('Hata', 'Doküman seçilmemiş');
      return;
    }
    
    try {
      setIsLoadingCompareDocuments(true);
      
      // Her zaman önce get_compare_data endpointine istek at
      console.log('get_compare_data endpointine istek gönderiliyor, document_id:', selectedDocumentId);
      
      const compareDataResponse = await fetch('https://n8n.mahir.ai/webhook/get_compare_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId
        })
      });

      if (!compareDataResponse.ok) {
        throw new Error(`HTTP ${compareDataResponse.status}: ${compareDataResponse.statusText}`);
      }

      const compareDataResult = await compareDataResponse.json();
      console.log('get_compare_data endpointinden dönen veri:', compareDataResult);
      
      // Boş data kontrolü - [{}] formatında gelirse doküman seçme modalını aç
      const isEmptyData = Array.isArray(compareDataResult) && 
                         compareDataResult.length === 1 && 
                         Object.keys(compareDataResult[0]).length === 0;
      
      if (isEmptyData) {
        console.log('Boş data geldi, doküman seçme modalı açılıyor');
        setCompareData(null);
        setSelectedCompareDocuments(new Set()); // Seçili dokümanları sıfırla
        
        // Şirketin dokümanlarını getir
        const response = await fetch('https://n8n.mahir.ai/webhook/get_documents', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_id: Number(selectedCompany.id)
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const documents = await response.json();
        
        // Boş obje kontrolü
        if (Array.isArray(documents) && documents.length === 1 && Object.keys(documents[0]).length === 0) {
          setCompanyDocumentsForCompare([]);
        } else if (Array.isArray(documents)) {
          // Mevcut dokümanı hariç tut
          const filteredDocuments = documents.filter((doc: any) => doc.document_id !== selectedDocumentId);
          setCompanyDocumentsForCompare(filteredDocuments);
        } else {
          setCompanyDocumentsForCompare([]);
        }
        
        setShowCompareDocumentsModal(true);
      } else {
        // Data varsa, direkt toplu değiştirme modalını aç
        console.log('Karşılaştırma verisi mevcut, modal açılıyor:', compareDataResult);
        setCompareData(compareDataResult);
        setSelectedHistoryRange({min: 100, max: 100}); // Varsayılan olarak %100 seç
        setSelectedHistoryItems(new Set()); // Seçimleri temizle
        setHistoryCurrentPage(1); // Sayfa 1'e dön
        setHideAppliedHistory(true); // Varsayılan olarak uygulanmış satırları gizle
        setShowBulkHistoryReplaceModal(true);
      }
      
    } catch (error) {
      console.error('Karşılaştırma için dokümanlar yüklenirken hata:', error);
      showError('Hata', 'Dokümanlar yüklenirken bir hata oluştu');
      setCompanyDocumentsForCompare([]);
    } finally {
      setIsLoadingCompareDocuments(false);
    }
  };

  // Bulk history replace function
  const handleBulkHistoryReplace = async () => {
    if (!compareData || compareData.length === 0 || !selectedDocumentId || !user?.id) {
      showError('Hata', 'Gerekli veriler eksik');
      return;
    }

    if (selectedHistoryItems.size === 0) {
      warning('Uyarı', 'Lütfen en az bir satır seçin');
      return;
    }

    setIsBulkHistoryReplacing(true);

    try {
      // Filter items based on selected SIDs
      const itemsToReplace = compareData.filter(item => selectedHistoryItems.has(item.sid));
      
      if (itemsToReplace.length === 0) {
        warning('Uyarı', 'Seçili satır bulunamadı');
        setIsBulkHistoryReplacing(false);
        return;
      }

      // Prepare changes array
      const changes: { sid: string; en_runs: string }[] = itemsToReplace.map(item => ({
        sid: item.sid,
        en_runs: item.old_data_en_runs
      }));

      // Send batch request to multiple_en_log_changes
      const batchData = {
        document_id: selectedDocumentId,
        user_id: user.id,
        timestamp: Math.floor(Date.now() / 1000),
        changes: changes
      };

      console.log('Toplu geçmiş değiştirme gönderiliyor:', batchData);

      const response = await fetch('https://n8n.mahir.ai/webhook/multiple_en_log_changes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batchData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Toplu geçmiş değiştirme hatası:', errorText);
        showError('Hata', 'Değiştirme sırasında bir hata oluştu');
        setIsBulkHistoryReplacing(false);
        return;
      }

      console.log('Toplu geçmiş değiştirme başarıyla tamamlandı');

      // Step 2: Call set_compare_active to mark items as active
      const sidList = itemsToReplace.map(item => item.sid);
      const activateData = {
        document_id: selectedDocumentId,
        activated_by: user.id,
        sid_list: sidList
      };

      console.log('set_compare_active çağrılıyor:', activateData);

      const activateResponse = await fetch('https://n8n.mahir.ai/webhook/set_compare_active', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(activateData)
      });

      if (!activateResponse.ok) {
        const errorText = await activateResponse.text();
        console.error('set_compare_active hatası:', errorText);
        // Continue anyway since the main change was successful
      } else {
        console.log('set_compare_active başarıyla tamamlandı');
      }

      // Step 3: Refresh translated data
      if (selectedDocumentId && selectedDocumentName) {
        await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
      }

      // Step 4: Refresh compare data to get updated is_active values
      console.log('Compare data yenileniyor...');
      const compareResponse = await fetch('https://n8n.mahir.ai/webhook/get_compare_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ document_id: selectedDocumentId })
      });

      if (compareResponse.ok) {
        const newCompareData = await compareResponse.json();
        if (newCompareData && newCompareData.length > 0 && newCompareData[0].sid) {
          setCompareData(newCompareData);
          console.log('Compare data başarıyla yenilendi');
        }
      } else {
        console.error('Compare data yenileme hatası');
      }
      
      success('Başarılı', `${itemsToReplace.length} satır geçmiş verilerle değiştirildi`);
      
      // Close modal and reset selections
      setShowBulkHistoryReplaceModal(false);
      setSelectedHistoryItems(new Set());
      setHistoryCurrentPage(1);
    } catch (error) {
      console.error('Toplu geçmiş değiştirme hatası:', error);
      showError('Hata', 'Değiştirme sırasında bir hata oluştu');
    } finally {
      setIsBulkHistoryReplacing(false);
    }
  };

  // Compare selected documents
  const compareDocuments = async () => {
    if (selectedCompareDocuments.size === 0) {
      warning('Uyarı', 'Lütfen en az bir doküman seçin');
      return;
    }

    if (!selectedDocumentId) {
      showError('Hata', 'Geçerli doküman seçilmemiş');
      return;
    }

    if (!user?.id) {
      showError('Hata', 'Kullanıcı bilgisi bulunamadı');
      return;
    }

    setIsComparingDocuments(true);

    try {
      const documentIds = Array.from(selectedCompareDocuments);
      const compareTime = Math.floor(Date.now() / 1000); // Epoch time (seconds)
      
      const requestBody = {
        document_ids: documentIds,
        document_id: [selectedDocumentId],
        user_id: user.id,
        compare_time: compareTime
      };
      
      console.log('Karşılaştırma isteği gönderiliyor:', requestBody);
      
      const result = await apiCall(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/compare_documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('compare_documents endpoint\'ten dönen veri:', data);
      return data;
    }, {
      loadingKey: 'compareDocuments',
      loadingMessage: 'Dokümanlar karşılaştırılıyor...',
      overlay: true,
      successMessage: `${documentIds.length} doküman karşılaştırma için gönderildi`,
      errorMessage: 'Karşılaştırma sırasında bir hata oluştu',
      showToast: true
    });
    
    if (result) {
      setShowCompareDocumentsModal(false);
      setSelectedCompareDocuments(new Set()); // Seçimleri temizle
      
      // Karşılaştırma tamamlandı, şimdi get_compare_data çağır ve modalı aç
      try {
        console.log('compare_documents tamamlandı, get_compare_data çağrılıyor...');
        
        const compareDataResponse = await fetch('https://n8n.mahir.ai/webhook/get_compare_data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            document_id: selectedDocumentId
          })
        });

        if (compareDataResponse.ok) {
          const compareDataResult = await compareDataResponse.json();
          console.log('get_compare_data sonucu:', compareDataResult);
          
          // Boş değilse modalı aç
          const isEmptyData = Array.isArray(compareDataResult) && 
                             compareDataResult.length === 1 && 
                             Object.keys(compareDataResult[0]).length === 0;
          
          if (!isEmptyData && compareDataResult.length > 0) {
            setCompareData(compareDataResult);
            setSelectedHistoryRange({min: 100, max: 100});
            setSelectedHistoryItems(new Set());
            setHistoryCurrentPage(1);
            setHideAppliedHistory(true);
            setShowBulkHistoryReplaceModal(true);
            success('Başarılı', 'Karşılaştırma verisi yüklendi');
          } else {
            warning('Uyarı', 'Karşılaştırma verisi bulunamadı');
          }
        }
      } catch (error) {
        console.error('get_compare_data hatası:', error);
        showError('Hata', 'Karşılaştırma verisi yüklenirken hata oluştu');
      }
    }
    } catch (error) {
      console.error('Karşılaştırma hatası:', error);
    } finally {
      setIsComparingDocuments(false);
    }
  };

  // Toggle document selection for comparison
  const toggleDocumentSelection = (documentId: string) => {
    const newSelection = new Set(selectedCompareDocuments);
    if (newSelection.has(documentId)) {
      newSelection.delete(documentId);
    } else {
      newSelection.add(documentId);
    }
    setSelectedCompareDocuments(newSelection);
  };

  // Clear compare data for the current document
  const clearCompareData = async () => {
    if (!selectedDocumentId) {
      showError('Hata', 'Doküman seçilmemiş');
      return;
    }

    setIsClearingCompareData(true);
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/undo_compare_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Undo compare data response:', responseText);

        const jsonResponse = responseText && responseText.trim() ? JSON.parse(responseText) : null;

        if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
          setClearCompareDataSuccess(true);
          setTimeout(() => {
            setClearCompareDataSuccess(false);
            setShowClearCompareDataModal(false);
            setShowBulkHistoryReplaceModal(false);
            setCompareData(null);
            setSelectedHistoryItems(new Set());
            setHistoryCurrentPage(1);
          }, 1000);
        } else {
          console.error('Karşılaştırma verisi temizleme başarısız:', jsonResponse);
          showError('Hata', 'Karşılaştırma verileri temizlenemedi');
        }
      } else {
        console.error('Karşılaştırma verisi temizlenirken hata:', response.status, response.statusText);
        showError('Hata', 'Karşılaştırma verileri temizlenirken hata oluştu');
      }
    } catch (error) {
      console.error('Karşılaştırma verisi temizleme hatası:', error);
      showError('Hata', 'Karşılaştırma verileri temizlenirken hata oluştu');
    } finally {
      setIsClearingCompareData(false);
    }
  };

  // Delete log changes
  const deleteLogChanges = async () => {
    try {
      if (!currentDocumentId) {
        console.warn('Document ID bulunamadı');
        return;
      }

      console.log('Log changes siliniyor, document_id:', currentDocumentId);

      console.log('🗑️ Delete All: Tüm değişiklikler siliniyor...');

      const response = await fetch('https://n8n.mahir.ai/webhook/tr_delete_log_changes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
          document_id: currentDocumentId
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Delete log changes response:', responseText);
        
        if (responseText && responseText.trim()) {
          const jsonResponse = JSON.parse(responseText);
          
          // Success mesajını kontrol et (array içinde geliyor)
          if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
            console.log('Log changes başarıyla silindi');
            // Success state'ini set et
            setDeleteSuccess(true);
            // 2 saniye sonra modal'ı kapat ve tabloyu yenile
            setTimeout(async () => {
              setDeleteSuccess(false);
              setShowDeleteChangesModal(false);
              // Fresh serialized data çek
              console.log('🔄 Delete sonrası yenileme başlatılıyor:', { currentDocumentId });
              if (currentDocumentId) {
                console.log('🔄 getSerializedData çağrılıyor...');
                await getSerializedData(currentDocumentId);
                console.log('✅ getSerializedData tamamlandı');
              } else {
                console.warn('⚠️ currentDocumentId eksik:', { currentDocumentId });
              }
            }, 1000);
          } else {
            console.log('Delete işlemi başarısız:', jsonResponse);
            setShowDeleteChangesModal(false);
          }
        } else {
          console.log('Sunucudan boş response geldi');
          setShowDeleteChangesModal(false);
        }
      } else {
        console.error('Log changes silinirken hata:', response.status, response.statusText);
        setShowDeleteChangesModal(false);
      }
    } catch (error) {
      console.error('Log changes silinirken hata:', error);
    }
  };

  // Delete log changes by SID
  const deleteLogChangesBySid = async () => {
    try {
      if (!currentDocumentId || !selectedRowSid) {
        console.warn('Document ID veya SID bulunamadı');
        return;
      }

      console.log('Row log changes siliniyor, document_id:', currentDocumentId, 'sid:', selectedRowSid);

      console.log('🗑️ Delete Row: Satır siliniyor, sid:', selectedRowSid);

      const response = await fetch('https://n8n.mahir.ai/webhook/tr_delete_log_changes_by_sid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
          document_id: currentDocumentId,
          sid: selectedRowSid
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Delete log changes by sid response:', responseText);
        
        if (responseText && responseText.trim()) {
          const jsonResponse = JSON.parse(responseText);
          
          // Success mesajını kontrol et (array içinde geliyor)
          if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
            console.log('Row log changes başarıyla silindi');
            // Success state'ini set et
            setDeleteRowSuccess(true);
            // 2 saniye sonra modal'ı kapat ve tabloyu yenile
            setTimeout(async () => {
              setDeleteRowSuccess(false);
              setShowDeleteRowModal(false);
              setSelectedRowSid(null);
              // Fresh serialized data çek
              console.log('🔄 Delete sonrası yenileme başlatılıyor:', { currentDocumentId });
              if (currentDocumentId) {
                console.log('🔄 getSerializedData çağrılıyor...');
                await getSerializedData(currentDocumentId);
                console.log('✅ getSerializedData tamamlandı');
              } else {
                console.warn('⚠️ currentDocumentId eksik:', { currentDocumentId });
              }
            }, 1000);
          } else {
            console.log('Row delete işlemi başarısız:', jsonResponse);
            setShowDeleteRowModal(false);
            setSelectedRowSid(null);
          }
        } else {
          console.log('Sunucudan boş response geldi');
          setShowDeleteRowModal(false);
          setSelectedRowSid(null);
        }
      } else {
        console.error('Row log changes silinirken hata:', response.status, response.statusText);
        setShowDeleteRowModal(false);
        setSelectedRowSid(null);
      }
    } catch (error) {
      console.error('Row log changes silinirken hata:', error);
      setShowDeleteRowModal(false);
      setSelectedRowSid(null);
    }
  };

  // Delete log changes by SID list (bulk delete)
  const deleteLogChangesBySidList = async () => {
    try {
      if (!currentDocumentId || selectedRows.size === 0) {
        console.warn('Document ID veya seçili satırlar bulunamadı');
        return;
      }

      // SID'leri 'sid1', 'sid2', 'sid3' formatında hazırla
      const sidList = Array.from(selectedRows).map(sid => `'${sid}'`).join(', ');
      
      console.log('Bulk log changes siliniyor, document_id:', currentDocumentId, 'sids:', sidList);

      console.log('🗑️ Bulk Delete: Toplu silme başlatılıyor, count:', selectedRows.size);

      const response = await fetch('https://n8n.mahir.ai/webhook/tr_delete_log_changes_by_sid_list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
          document_id: currentDocumentId,
          sid_list: sidList
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Delete log changes by sid list response:', responseText);
        
        if (responseText && responseText.trim()) {
          const jsonResponse = JSON.parse(responseText);
          
          // Success mesajını kontrol et (array içinde geliyor)
          if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
            console.log('Bulk log changes başarıyla silindi');
            // Success state'ini set et
            setBulkDeleteSuccess(true);
            // 2 saniye sonra modal'ı kapat ve tabloyu yenile
            setTimeout(async () => {
              setBulkDeleteSuccess(false);
              setShowBulkDeleteModal(false);
              setSelectedRows(new Set());
              setIsSelectionMode(false); // Seçim modunu kapat
              // Fresh serialized data çek
              console.log('🔄 Delete sonrası yenileme başlatılıyor:', { currentDocumentId });
              if (currentDocumentId) {
                console.log('🔄 getSerializedData çağrılıyor...');
                await getSerializedData(currentDocumentId);
                console.log('✅ getSerializedData tamamlandı');
              } else {
                console.warn('⚠️ currentDocumentId eksik:', { currentDocumentId });
              }
            }, 1000);
          } else {
            console.log('Bulk delete işlemi başarısız:', jsonResponse);
            setShowBulkDeleteModal(false);
            setSelectedRows(new Set());
          }
        } else {
          console.log('Sunucudan boş response geldi');
          setShowBulkDeleteModal(false);
          setSelectedRows(new Set());
        }
      } else {
        console.error('Bulk log changes silinirken hata:', response.status, response.statusText);
        setShowBulkDeleteModal(false);
        setSelectedRows(new Set());
      }
    } catch (error) {
      console.error('Bulk log changes silinirken hata:', error);
      setShowBulkDeleteModal(false);
      setSelectedRows(new Set());
    }
  };

  // Log changes to endpoint
  const logChanges = async (sid: string, newRuns: string) => {
    try {
      if (!currentDocumentId || !user?.id) {
        console.warn('Document ID veya User ID bulunamadı');
        return;
      }

      const logData = {
        sid: sid,
        runs: newRuns,
        document_id: currentDocumentId,
        user_id: user.id,
        timestamp: Math.floor(Date.now() / 1000) // Epoch cinsinden zaman
      };

      console.log('Log changes gönderiliyor:', logData);

      const response = await fetch('https://n8n.mahir.ai/webhook/tr_log_changes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });

      if (response.ok) {
        console.log('Log changes başarıyla gönderildi');
        console.log('getSerializedData çağrılıyor...');
        // Değişiklik kaydedildikten sonra tabloyu yenile
        await getSerializedData();
        console.log('getSerializedData tamamlandı');
      } else {
        console.error('Log changes gönderilirken hata:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Log changes gönderilirken hata:', error);
    }
  };

  // English text log changes to endpoint
  const logEnChanges = async (sid: string, newEnRuns: string) => {
    try {
      if (!selectedDocumentId || !user?.id) {
        console.warn('Document ID veya User ID bulunamadı');
        return;
      }

      const logData = {
        sid: sid,
        en_runs: newEnRuns,
        document_id: selectedDocumentId,
        user_id: user.id,
        timestamp: Math.floor(Date.now() / 1000) // Epoch cinsinden zaman
      };

      console.log('EN Log changes gönderiliyor:', logData);

      const response = await fetch('https://n8n.mahir.ai/webhook/en_log_changes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });

      if (response.ok) {
        console.log('EN Log changes başarıyla gönderildi');
        console.log('refreshTranslatedDataOnly çağrılıyor...');
        // Değişiklik kaydedildikten sonra tabloyu yenile (sayfa ve filtreler korunur)
        if (selectedDocumentId && selectedDocumentName) {
          await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
        }
        console.log('refreshTranslatedDataOnly tamamlandı');
      } else {
        console.error('EN Log changes gönderilirken hata:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('EN Log changes gönderilirken hata:', error);
    }
  };

  // Delete EN log changes
  const deleteEnLogChanges = async () => {
    try {
      if (!selectedDocumentId) {
        console.warn('Document ID bulunamadı');
        return;
      }

      console.log('EN Log changes siliniyor, document_id:', selectedDocumentId);

      const response = await fetch('https://n8n.mahir.ai/webhook/en_delete_log_changes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Delete EN log changes response:', responseText);
        
        if (responseText && responseText.trim()) {
          const jsonResponse = JSON.parse(responseText);
          console.log('Delete EN log changes JSON response:', jsonResponse);
          
          if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
            console.log('EN Log changes başarıyla silindi');
            
            // Hemen fresh data çek - sadece translated data'yı yenile
            console.log('🔄 EN Delete sonrası yenileme başlatılıyor:', { selectedDocumentId, selectedDocumentName });
            if (selectedDocumentId && selectedDocumentName) {
              console.log('🔄 refreshTranslatedDataOnly çağrılıyor...');
              await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
              console.log('✅ refreshTranslatedDataOnly tamamlandı');
            } else {
              console.warn('⚠️ selectedDocumentId veya selectedDocumentName eksik:', { selectedDocumentId, selectedDocumentName });
            }
            
            // Başarı mesajını göster
            setDeleteEnSuccess(true);
            
            // 1 saniye sonra modalı kapat
            setTimeout(() => {
              setDeleteEnSuccess(false);
              setShowDeleteEnChangesModal(false);
            }, 1000);
          } else {
            console.log('EN Delete işlemi başarısız:', jsonResponse);
            setShowDeleteEnChangesModal(false);
          }
        } else {
          console.log('Sunucudan boş response geldi');
          setShowDeleteEnChangesModal(false);
        }
      } else {
        console.error('EN Delete log changes gönderilirken hata:', response.status, response.statusText);
        setShowDeleteEnChangesModal(false);
      }
    } catch (error) {
      console.error('EN Log changes silinirken hata:', error);
      setShowDeleteEnChangesModal(false);
    }
  };

  // Delete EN log changes by SID
  const deleteEnLogChangesBySid = async () => {
    try {
      if (!selectedDocumentId || !selectedEnRowSid) {
        console.warn('Document ID veya SID bulunamadı');
        return;
      }

      console.log('Row EN log changes siliniyor, document_id:', selectedDocumentId, 'sid:', selectedEnRowSid);

      const response = await fetch('https://n8n.mahir.ai/webhook/en_delete_log_changes_by_sid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId,
          sid: selectedEnRowSid
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Delete EN log changes by sid response:', responseText);
        
        if (responseText && responseText.trim()) {
          const jsonResponse = JSON.parse(responseText);
          console.log('Delete EN log changes by sid JSON response:', jsonResponse);
          
          if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
            console.log('EN Row log changes başarıyla silindi');
            
            // Hemen fresh data çek - sadece translated data'yı yenile
            console.log('🔄 EN Delete sonrası yenileme başlatılıyor:', { selectedDocumentId, selectedDocumentName });
            if (selectedDocumentId && selectedDocumentName) {
              console.log('🔄 refreshTranslatedDataOnly çağrılıyor...');
              await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
              console.log('✅ refreshTranslatedDataOnly tamamlandı');
            } else {
              console.warn('⚠️ selectedDocumentId veya selectedDocumentName eksik:', { selectedDocumentId, selectedDocumentName });
            }
            
            // Başarı mesajını göster
            setDeleteEnRowSuccess(true);
            
            // 1 saniye sonra modalı kapat
            setTimeout(() => {
              setDeleteEnRowSuccess(false);
              setShowDeleteEnRowModal(false);
              setSelectedEnRowSid(null);
            }, 1000);
          } else {
            console.log('EN Row delete işlemi başarısız:', jsonResponse);
            setShowDeleteEnRowModal(false);
            setSelectedEnRowSid(null);
          }
        } else {
          console.log('Sunucudan boş response geldi');
          setShowDeleteEnRowModal(false);
          setSelectedEnRowSid(null);
        }
      } else {
        console.error('EN Delete log changes by sid gönderilirken hata:', response.status, response.statusText);
        setShowDeleteEnRowModal(false);
        setSelectedEnRowSid(null);
      }
    } catch (error) {
      console.error('EN Row log changes silinirken hata:', error);
      setShowDeleteEnRowModal(false);
      setSelectedEnRowSid(null);
    }
  };

  // Delete EN log changes by SID list (bulk delete)
  const deleteEnLogChangesBySidList = async () => {
    try {
      if (!selectedDocumentId || selectedEnRows.size === 0) {
        console.warn('Document ID veya seçili satırlar bulunamadı');
        return;
      }

      // SID'leri 'sid1', 'sid2', 'sid3' formatında hazırla
      const sidList = Array.from(selectedEnRows).map(sid => `'${sid}'`).join(', ');
      
      console.log('Bulk EN log changes siliniyor, document_id:', selectedDocumentId, 'sids:', sidList);

       const response = await fetch('https://n8n.mahir.ai/webhook/en_delete_log_changes_by_sid_list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId,
          sid_list: sidList
        })
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('Delete EN log changes by sid list response:', responseText);
        
        if (responseText && responseText.trim()) {
          const jsonResponse = JSON.parse(responseText);
          console.log('Delete EN log changes by sid list JSON response:', jsonResponse);
          
          if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
            console.log('EN Bulk log changes başarıyla silindi');
            
            // Hemen fresh data çek - sadece translated data'yı yenile
            console.log('🔄 EN Delete sonrası yenileme başlatılıyor:', { selectedDocumentId, selectedDocumentName });
            if (selectedDocumentId && selectedDocumentName) {
              console.log('🔄 refreshTranslatedDataOnly çağrılıyor...');
              await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
              console.log('✅ refreshTranslatedDataOnly tamamlandı');
            } else {
              console.warn('⚠️ selectedDocumentId veya selectedDocumentName eksik:', { selectedDocumentId, selectedDocumentName });
            }
            
            // Başarı mesajını göster
            setBulkDeleteEnSuccess(true);
            
            // 1 saniye sonra modalı kapat
            setTimeout(() => {
              setBulkDeleteEnSuccess(false);
              setShowBulkDeleteEnModal(false);
              setSelectedEnRows(new Set());
              setIsEnSelectionMode(false); // Seçim modunu kapat
            }, 1000);
          } else {
            console.log('EN Bulk delete işlemi başarısız:', jsonResponse);
            setShowBulkDeleteEnModal(false);
            setSelectedEnRows(new Set());
          }
        } else {
          console.log('Sunucudan boş response geldi');
          setShowBulkDeleteEnModal(false);
          setSelectedEnRows(new Set());
        }
      } else {
        console.error('EN Delete log changes by sid list gönderilirken hata:', response.status, response.statusText);
        setShowBulkDeleteEnModal(false);
        setSelectedEnRows(new Set());
      }
    } catch (error) {
      console.error('EN Bulk log changes silinirken hata:', error);
      setShowBulkDeleteEnModal(false);
      setSelectedEnRows(new Set());
    }
  };


  // Load quality control results
  const loadQualityControlResults = async () => {
    if (!selectedDocumentId) {
      showError('Doküman ID bulunamadı', 'Kalite kontrol sonuçları indirilemedi.');
      return;
    }

    setIsLoadingQualityResults(true);
    try {
      console.log('batch_get_output endpointine istek gönderiliyor:', { document_id: selectedDocumentId });
      
      const response = await fetch('https://n8n.mahir.ai/webhook/batch_get_output', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocumentId
        }),
      });

      if (response.ok) {
        const results = await response.json();
        console.log('Kalite kontrol sonuçları başarıyla alındı:', results);
        
        // Sonuçları state'e kaydet
        if (Array.isArray(results)) {
          setQualityControlResults(results);
          setQualityControlDocumentId(selectedDocumentId); // Hangi doküman için yüklendiğini kaydet
          console.log('🔗 Kalite kontrol verileri doküman ID ile eşleştirildi:', selectedDocumentId);
          success('Yükleme Başarılı', 'Kalite kontrol sonuçları tabloda gösteriliyor.');
        } else {
          console.warn('Beklenmeyen veri formatı:', results);
          showError('Veri Formatı Hatası', 'Kalite kontrol sonuçları beklenmeyen formatta geldi.');
        }
      } else {
        console.error('Kalite kontrol sonuçları alınırken hata:', response.status, response.statusText);
        showError('İndirme Hatası', 'Kalite kontrol sonuçları alınırken hata oluştu.');
      }
    } catch (error) {
      console.error('Kalite kontrol sonuçları alınırken hata:', error);
      showError('İndirme Hatası', 'Kalite kontrol sonuçları alınırken hata oluştu.');
    } finally {
      setIsLoadingQualityResults(false);
      setShowQualityControlPopover(false);
    }
  };

  // Check if we should skip company selection
  useEffect(() => {
    if (selectedCompany) {
      setCurrentStep('company-tabs');
    } else {
      setCurrentStep('company-selection');
    }
  }, [selectedCompany]);

  // Company selection handlers
  const handleCompanySelect = (company: any) => {
    // Admin kullanıcılar için yetki kontrolü yapılmaz
    if (user?.app_roles?.translate?.toLowerCase() !== 'admin') {
      // Yetki kontrolü: Kullanıcının ID'si ile şirketin assigned_user değeri eşleşmeli
      if (company.assigned_user && user?.id) {
        // user.id number veya string olabilir, karşılaştırma için string'e çevir
        const userId = user.id.toString();
        
        // assigned_user array ise (birden fazla kişi atanmış)
        if (Array.isArray(company.assigned_user)) {
          const hasAccess = company.assigned_user.some((assignedId: any) => 
            assignedId.toString() === userId
          );
          
          if (!hasAccess) {
            showError('Yetkiniz Yok', 'Bu şirkete erişim yetkiniz bulunmamaktadır. Sadece size atanan şirketlere erişebilirsiniz.');
            return;
          }
        } else {
          // assigned_user tek değer ise
          const assignedUserId = company.assigned_user.toString();
          
          if (userId !== assignedUserId) {
            showError('Yetkiniz Yok', 'Bu şirkete erişim yetkiniz bulunmamaktadır. Sadece size atanan şirketlere erişebilirsiniz.');
            return;
          }
        }
      }
    }
    
    setSelectedCompany(company);
    setCurrentStep('company-tabs');
  };

  const handleAddCompany = async (companyData: { name: string; glossary: File | null; normalize: File | null; }) => {
    try {
      const newCompany = await addCompany({
        name: companyData.name,
        glossaryFile: companyData.glossary,
        normalizeFile: companyData.normalize
      });
      
      if (newCompany) {
        // Yeni şirket cache'ini oluştur
        setNewCompanyCache({
          name: companyData.name,
          tempId: newCompany.id,
          company_id: newCompany.company_id, // Webhook'tan gelen company_id
          isWaitingForWebhook: !newCompany.company_id // company_id varsa webhook tamamlanmış
        });
        
        setShowAddCompanyModal(false);
        // Hemen şirketi seç (cache ile çalışacak)
        handleCompanySelect(newCompany);
        
        // Arka planda companies listesini yenile
        setTimeout(async () => {
          await refreshCompanies();
          // Cache'i temizle
          setNewCompanyCache(null);
        }, 1000);
      } else {
        // Şirket eklenmemişse modal'ı kapat ama şirket seçme
        setShowAddCompanyModal(false);
      }
      return newCompany;
    } catch (error) {
      console.error('Error adding company:', error);
      return null;
    }
  };

  const handleBackToCompanySelection = () => {
    setSelectedCompany(null);
    setCurrentStep('company-selection');
    setSelectedOption(null); // Reset selected option
    // Reset file upload state
    setSelectedFile(null);
    setDownloadUrl(null);
    setDownloadFilename(null);
    setStatusMessage('');
    setUploadProgress(0);
    setIsUploading(false);
    setTranslationResults(null); // Reset translation results
    setEditingIndex(null); // Reset editing state
    setEditingText('');
    setCurrentDocumentId(null); // Reset document ID
    currentDocumentIdRef.current = null; // Reset document ID ref
    setTableRefreshKey(0); // Reset table refresh key
    // Cache'i temizle
    setNewCompanyCache(null);
  };

  // Company management handlers
  const handleShowCompany = (company: Company) => {
    setSelectedCompanyForDetail(company);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedCompanyForDetail(null);
  };

  const handleRemoveCompany = async (company: Company, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering company selection
    setSelectedCompanyForDelete(company);
    setShowDeleteModal(true);
  };

  const confirmDeleteCompany = async () => {
    if (selectedCompanyForDelete) {
      removeCompany(selectedCompanyForDelete.id);
      // Şirket silindikten sonra verileri yenile
      setTimeout(async () => {
        await refreshCompanies();
      }, 1000);
      setShowDeleteModal(false);
      setSelectedCompanyForDelete(null);
    }
  };

  const cancelDeleteCompany = () => {
    setShowDeleteModal(false);
    setSelectedCompanyForDelete(null);
  };

  const handleEditCompany = (company: Company, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering company selection
    setSelectedCompanyForEdit(company);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCompanyForEdit(null);
  };

  const handleUpdateCompany = (updatedCompany: Company) => {
    console.log('Company updated:', updatedCompany);
    // The modal handles the actual update through CompanyContext
    // We just need to refresh the UI if needed
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStatusMessage('');
    const file = e.target.files && e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Dosya ismini uzantısız olarak ayarla
      const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      setFileName(nameWithoutExtension.slice(0, 50)); // Max 50 karakter
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setStatusMessage('');
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      // Dosya ismini uzantısız olarak ayarla
      const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      setFileName(nameWithoutExtension.slice(0, 50)); // Max 50 karakter
    }
  };

  const handleFileDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleFileDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Yeni handler'lar
  const handleContinue = () => {
    setShowConfigModal(true);
  };

  const handleConfigModalClose = () => {
    setShowConfigModal(false);
  };

  const handleFileNameChange = (value: string) => {
    setFileName(value);
  };

  const handleSourceLanguageChange = (value: string) => {
    setSourceLanguage(value);
  };

  const handleDestinationLanguageChange = (value: string) => {
    setDestinationLanguage(value);
  };

  // Dil kodunu kullanıcı dostu isme çeviren fonksiyon
  const getLanguageName = (code: string): string => {
    const languageMap: { [key: string]: string } = {
      'tr': 'Türkçe',
      'en': 'İngilizce',
      'en-US': 'Amerikan İngilizcesi',
      'en-GB': 'İngiliz İngilizcesi',
      'es': 'İspanyolca',
      'fr': 'Fransızca',
      'it': 'İtalyanca',
      'de': 'Almanca'
    };
    return languageMap[code] || code.toUpperCase();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileName('');
    setStatusMessage('');
    setUploadProgress(0);
  };

  const handleUpload = async () => {
    if (!selectedFile) return setStatusMessage('Lütfen önce bir dosya seçin.');
    if (!uploadEndpoint) return setStatusMessage('Upload endpoint henüz belirlenmedi.');

    try {
      setStatusMessage('Yükleniyor...');
      setIsUploading(true);

      // Zaman damgasını istediğiniz formatta üret
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(
        now.getHours()
      )}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

      const docxName = `C:/n8n_tmp/translate_${timestamp}.docx`;
      const jsonName = `C:/n8n_tmp/translate_${timestamp}.json`;
      const documentId = uuidv4();

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('docx_file_name', docxName);
      formData.append('serialize_json_file', jsonName);
      formData.append('document_id', documentId);
      
      // Document ID'yi sakla
      setCurrentDocumentId(documentId);
      currentDocumentIdRef.current = documentId;
      // Company ID'yi akıllı şekilde belirle
      let companyId = '';
      
      if (selectedCompany?.company_id) {
        // Gerçek company_id varsa onu kullan
        companyId = selectedCompany.company_id.toString();
      } else if (newCompanyCache && selectedCompany?.id === newCompanyCache.tempId && newCompanyCache.company_id) {
        // Yeni eklenen şirket için cache'den company_id kullan
        companyId = newCompanyCache.company_id.toString();
      }
      
      if (!companyId) {
        showError('Hata', 'Şirket bilgisi bulunamadı. Lütfen şirketi yeniden seçin.');
        return;
      }
      
      formData.append('company_id', companyId);
      
      // User ID'yi ekle
      if (user?.id) {
        formData.append('user_id', user.id);
      }
      
      // Yeni değişkenleri ekle
      // Company code'u belirle
      let companyCode = '';
      if (selectedCompany?.company_code) {
        companyCode = selectedCompany.company_code;
      }
      
      // Kullanıcının değiştirdiği dosya adını kullan (uzantısız)
      const fileNameNoExt = fileName || selectedFile.name.replace(/\.[^/.]+$/, "");
      
      // Yeni değişkenleri FormData'ya ekle
      formData.append('company_code', companyCode || '');
      formData.append('report_type', reportType || '');
      formData.append('year', reportYear || '');
      formData.append('file_name_no_ext', fileNameNoExt);
      formData.append('source_language', sourceLanguage);
      formData.append('destination_language', destinationLanguage);
      
      console.log('Translate endpoint\'e gönderilen veriler:', {
        company_id: companyId,
        user_id: user?.id,
        selectedCompany: selectedCompany,
        newCompanyCache: newCompanyCache,
        isNewCompany: newCompanyCache && selectedCompany?.id === newCompanyCache.tempId,
        companyCode: companyCode,
        reportType: reportType,
        reportYear: reportYear,
        fileNameNoExt: fileNameNoExt,
        source_language: sourceLanguage,
        destination_language: destinationLanguage
      });

      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadEndpoint);
      xhrRef.current = xhr;
      // Webhook JSON response beklediğimiz için text olarak al
      xhr.responseType = 'text';

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percent);
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const responseText = xhr.response as string;
            console.log('Serialize file response:', responseText);
            
            if (responseText && responseText.trim()) {
              // JSON response'u parse et
              const jsonResponse = JSON.parse(responseText);
              
              // Success mesajını kontrol et (array içinde geliyor)
              if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
                console.log('Success true alındı, getSerializedData çağrılacak');
                console.log('currentDocumentId değeri:', currentDocumentId);
                setStatusMessage('Dosya ayrıştırma başarılı. Veriler yükleniyor...');
                
                // Eski download state'lerini temizle
                setDownloadUrl(null);
                setDownloadFilename(null);
                
                // Serialized data'yı çek
                console.log('getSerializedData çağrılıyor...');
                console.log('Ref\'ten alınan document ID:', currentDocumentIdRef.current);
                await getSerializedData(currentDocumentIdRef.current || undefined);
                console.log('getSerializedData tamamlandı');
                setIsUploading(false);
              } else {
                console.log('Success false veya beklenmeyen format:', jsonResponse);
                setStatusMessage('Dosya ayrıştırma başarısız.');
                setIsUploading(false);
              }
            } else {
              setStatusMessage('Sunucudan boş response geldi.');
              setIsUploading(false);
            }
          } catch (err) {
            console.error('JSON parse error:', err);
            setStatusMessage('Response işlenirken hata oluştu.');
            setIsUploading(false);
          }
        } else {
          setStatusMessage(`Sunucu hatası: ${xhr.status}`);
          setIsUploading(false);
        }
      };

      xhr.onerror = () => {
        setStatusMessage('Ağ hatası oluştu.');
        setIsUploading(false);
      };

      xhr.send(formData);
    } catch (err) {
      setStatusMessage('Beklenmeyen hata.');
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    if (xhrRef.current) {
      try { xhrRef.current.abort(); } catch (e) {}
      xhrRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);
    setStatusMessage('Yükleme iptal edildi.');
  };

  // Temizlik: bileşen unmount veya yeni url gelince eski objectURL'leri temizle
  useEffect(() => {
    return () => {
      if (prevDownloadUrl.current) {
        URL.revokeObjectURL(prevDownloadUrl.current);
        prevDownloadUrl.current = null;
      }
    };
  }, []);

  // URL parametrelerini yönetme fonksiyonları
  const updateURL = useCallback((params: {
    step?: string;
    option?: string;
    view?: string;
    companyId?: string;
    documentId?: string;
  }) => {
    const searchParams = new URLSearchParams(location.search);
    
    // Parametreleri güncelle
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    
    // URL'i güncelle
    const newSearch = searchParams.toString();
    const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
    navigate(newPath, { replace: true });
  }, [location, navigate]);

  // URL'den parametreleri okuma
  const getURLParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      step: searchParams.get('step'),
      option: searchParams.get('option'),
      view: searchParams.get('view'),
      companyId: searchParams.get('companyId'),
      documentId: searchParams.get('documentId')
    };
  }, [location.search]);

  // URL parametrelerine göre state'i güncelle (sadece sayfa yüklendiğinde)
  useEffect(() => {
    const params = getURLParams();
    
    // Eğer URL'de hiç parametre yoksa, default duruma geç
    if (!params.step) {
      if (currentStep !== 'company-selection') {
        setCurrentStep('company-selection');
        setSelectedOption(null);
        setShowTranslatedDataView(false);
      }
      return;
    }
    
    // Şirket seçimi
    if (params.companyId && companies.length > 0) {
      const company = companies.find(c => 
        c.id.toString() === params.companyId || 
        (c.company_id && c.company_id.toString() === params.companyId)
      );
      if (company && company !== selectedCompany) {
        setSelectedCompany(company);
      }
    }
    
    // Adım kontrolü
    if (params.step === 'company-selection') {
      if (currentStep !== 'company-selection') {
        setCurrentStep('company-selection');
        setSelectedOption(null);
        setShowTranslatedDataView(false);
      }
    } else if (params.step === 'company-tabs') {
      if (currentStep !== 'company-tabs') {
        setCurrentStep('company-tabs');
      }
      
      // Seçenek kontrolü
      if (params.option === 'translations') {
        if (selectedOption !== 'translations') {
          setSelectedOption('translations');
        }
        if (params.view === 'detail' && params.documentId) {
          if (!showTranslatedDataView) {
            setShowTranslatedDataView(true);
          }
          if (selectedDocumentId !== params.documentId) {
            setSelectedDocumentId(params.documentId);
          }
        } else {
          if (showTranslatedDataView) {
            setShowTranslatedDataView(false);
          }
        }
      } else if (params.option === 'translate') {
        if (selectedOption !== 'translate') {
          setSelectedOption('translate');
        }
        if (showTranslatedDataView) {
          setShowTranslatedDataView(false);
        }
      } else {
        if (selectedOption !== null) {
          setSelectedOption(null);
        }
        if (showTranslatedDataView) {
          setShowTranslatedDataView(false);
        }
      }
    }
  }, [location.search, companies]);

  // Sayfa yenilendiğinde detail ekranında fetchTranslatedData ve kalite kontrol çağır
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const documentId = searchParams.get('documentId');
    const view = searchParams.get('view');
    
    // Detail view'da documentId varsa ve henüz data yüklenmemişse
    if (view === 'detail' && documentId && 
        showTranslatedDataView && selectedDocumentId === documentId &&
        !translatedData && companyDocuments && companyDocuments.length > 0 &&
        fetchTranslatedDataRef.current) {
      
      // companyDocuments'tan documentName'i bul
      const document = companyDocuments.find(doc => doc.document_id === documentId) as {document_id: string, file_name_no_ext: string, current_date?: string, current_time?: string, username?: string, source_language?: string, destination_language?: string} | undefined;
      
      if (document && document.file_name_no_ext) {
        console.log('🔄 Sayfa yenilendi - fetchTranslatedData çağrılıyor:', {
          documentId: documentId,
          documentName: document.file_name_no_ext,
          sourceLanguage: document.source_language,
          destinationLanguage: document.destination_language
        });
        
        // Dil bilgilerini hemen state'lere set et
        setSelectedDocumentSourceLanguage(document.source_language || null);
        setSelectedDocumentDestinationLanguage(document.destination_language || null);
        
        // fetchTranslatedData'yı ref üzerinden çağır ve dil bilgilerini de geç
        fetchTranslatedDataRef.current(documentId, document.file_name_no_ext, document.source_language, document.destination_language);
      }
    }
    
    // Detail view'da documentId varsa kalite kontrol polling'ini de başlat
    // Sadece status yoksa veya completed değilse başlat
    if (view === 'detail' && documentId && 
        showTranslatedDataView && selectedDocumentId === documentId &&
        (!qualityControlStatus || qualityControlStatus?.status !== 'completed') && 
        !qualityControlIntervalRef.current) {
      
      console.log('🔄 Sayfa yenilendi - kalite kontrol polling başlatılıyor:', documentId);
      
      // Kalite kontrol durumunu kontrol et
      const checkStatus = async () => {
        if (isCheckingQualityControl) return;
        
        setIsCheckingQualityControl(true);
        try {
          const response = await fetch('https://n8n.mahir.ai/webhook/batch_get_status_front', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              document_id: documentId
            }),
          });

          if (response.ok) {
            const data = await response.json();
            setQualityControlStatus(data);
            console.log('Kalite kontrol durumu:', data);
            
            // Eğer completed gelirse, update_output_file_id endpointini çağır
            if (data?.status === 'completed') {
              console.log('✅ Kalite kontrol tamamlandı - polling durduruluyor');
              console.log('🔍 Debug - data:', data);
              console.log('🔍 Debug - documentId:', documentId);
              console.log('🔍 Debug - data.output_file_id:', data.output_file_id);
              
              // output_file_id ve document_id değerlerini kontrol et
              if (data.output_file_id && selectedDocumentId) {
                console.log('✅ Koşullar sağlandı - updateOutputFile çağrılacak');
                
                // Orijinal updateOutputFile fonksiyonunu kullan
                try {
                  console.log('update_output_file_id endpointine gönderiliyor:', { 
                    output_file_id: data.output_file_id, 
                    document_id: selectedDocumentId 
                  });
                  
                  const response = await fetch('https://n8n.mahir.ai/webhook/update_output_file_id', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      output_file_id: data.output_file_id,
                      document_id: selectedDocumentId
                    }),
                  });

                  if (response.ok) {
                    const result = await response.json();
                    console.log('update_output_file_id başarılı:', result);
                    
                    // Eğer response "success" ise, kalite kontrol indirme butonunu aktif et
                    if (result.response === 'success') {
                      setIsQualityControlDownloadEnabled(true);
                      console.log('Kalite kontrol indirme butonu aktif edildi');
                    }
                  } else {
                    console.error('update_output_file_id hatası:', response.status, response.statusText);
                  }
                } catch (updateError) {
                  console.error('update_output_file_id çağrısında hata:', updateError);
                }
              } else {
                console.warn('output_file_id veya document_id eksik:', { 
                  output_file_id: data.output_file_id, 
                  document_id: selectedDocumentId 
                });
              }
              
              // Interval'ı durdur
              if (qualityControlIntervalRef.current) {
                clearInterval(qualityControlIntervalRef.current);
                qualityControlIntervalRef.current = null;
              }
            }
          }
        } catch (error) {
          console.error('Kalite kontrol durumu alınırken hata:', error);
        } finally {
          setIsCheckingQualityControl(false);
        }
      };
      
      // İlk kontrolü hemen yap
      checkStatus();
      
      // 10 saniyede bir kontrol et (sadece completed değilse)
      if (!qualityControlStatus || qualityControlStatus?.status !== 'completed') {
        qualityControlIntervalRef.current = setInterval(checkStatus, 10000);
      }
    }
  }, [showTranslatedDataView, selectedDocumentId, translatedData, companyDocuments, location.search]);

  // Kalite kontrol durumu completed veya failed olduğunda polling'i durdur
  useEffect(() => {
    if (qualityControlStatus?.status === 'completed' && qualityControlIntervalRef.current) {
      console.log('✅ Kalite kontrol tamamlandı - polling durduruluyor (useEffect)');
      clearInterval(qualityControlIntervalRef.current);
      qualityControlIntervalRef.current = null;
    }
    if (qualityControlStatus?.status === 'failed' && qualityControlIntervalRef.current) {
      console.log('❌ Kalite kontrol başarısız - polling durduruluyor (useEffect)');
      clearInterval(qualityControlIntervalRef.current);
      qualityControlIntervalRef.current = null;
    }
  }, [qualityControlStatus?.status]);

  // fetchTranslatedData fonksiyonunu ref'e assign et
  useEffect(() => {
    fetchTranslatedDataRef.current = fetchTranslatedData;
  }, [fetchTranslatedData]);

  // Reset batch translate trigger when document changes
  useEffect(() => {
    if (selectedDocumentId !== prevDocumentIdRef.current) {
      setIsBatchTranslateTriggered(false);
      prevDocumentIdRef.current = selectedDocumentId;
    }
  }, [selectedDocumentId]);

  // Tablo datasını yenileme interval'i - en_runs boşsa 10 saniyede bir yenile
  useEffect(() => {
    // Sadece translated data view açıksa ve document seçiliyse çalış
    if (!showTranslatedDataView || !selectedDocumentId || !selectedDocumentName) {
      if (dataRefreshIntervalRef.current) {
        clearInterval(dataRefreshIntervalRef.current);
        dataRefreshIntervalRef.current = null;
      }
      return;
    }

    // en_runs değerlerini kontrol et
    const hasAnyEnRuns = translatedData && translatedData.length > 0 && translatedData.some(item => 
      item.en_runs && 
      item.en_runs.trim() !== '' && 
      item.en_runs !== 'null'
    );

    if (hasAnyEnRuns) {
      // en_runs varsa interval'ı durdur
      if (dataRefreshIntervalRef.current) {
        console.log('✅ en_runs değerleri geldi - tablo yenileme durduruluyor');
        clearInterval(dataRefreshIntervalRef.current);
        dataRefreshIntervalRef.current = null;
      }
    } else {
      // en_runs yoksa ve interval çalışmıyorsa başlat
      if (!dataRefreshIntervalRef.current) {
        console.log('🔄 en_runs değerleri yok - 10 saniyede bir tablo yenilenecek');
        dataRefreshIntervalRef.current = setInterval(() => {
          console.log('⏰ Tablo datasını yenileme zamanı...');
          if (fetchTranslatedDataRef.current && selectedDocumentId && selectedDocumentName) {
            fetchTranslatedDataRef.current(selectedDocumentId, selectedDocumentName);
          }
        }, 10000);
      }
    }

    // Cleanup
    return () => {
      if (dataRefreshIntervalRef.current) {
        clearInterval(dataRefreshIntervalRef.current);
        dataRefreshIntervalRef.current = null;
      }
    };
  }, [translatedData, showTranslatedDataView, selectedDocumentId, selectedDocumentName]);

  // State değişikliklerinde URL'i güncelle (sadece kullanıcı etkileşimi sonrası)
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    // İlk yükleme tamamlandığında işaretle
    if (companies.length > 0 || !isLoading) {
      setIsInitialized(true);
    }
  }, [companies.length, isLoading]);

  useEffect(() => {
    // Sadece sistem initialize olduktan sonra URL güncelle
    if (!isInitialized) return;
    
    const params = getURLParams();
    const companyId = selectedCompany?.company_id?.toString() || selectedCompany?.id?.toString();
    
    // Mevcut URL parametreleri ile karşılaştır, gereksiz güncellemeleri önle
    const shouldUpdate = 
      (currentStep === 'company-selection' && params.step !== 'company-selection') ||
      (currentStep === 'company-tabs' && (
        params.step !== 'company-tabs' ||
        params.companyId !== companyId ||
        params.option !== selectedOption ||
        (selectedOption === 'translations' && showTranslatedDataView && params.view !== 'detail') ||
        (selectedOption === 'translations' && showTranslatedDataView && params.documentId !== selectedDocumentId) ||
        (selectedOption === 'translations' && !showTranslatedDataView && params.view === 'detail')
      ));
    
    if (!shouldUpdate) return;
    
    if (currentStep === 'company-selection') {
      updateURL({ 
        step: 'company-selection',
        option: undefined,
        view: undefined,
        companyId: undefined,
        documentId: undefined
      });
    } else if (currentStep === 'company-tabs' && selectedCompany) {
      const newParams: any = {
        step: 'company-tabs',
        companyId: companyId
      };
      
      if (selectedOption === 'translations') {
        newParams.option = 'translations';
        if (showTranslatedDataView && selectedDocumentId) {
          newParams.view = 'detail';
          newParams.documentId = selectedDocumentId;
        } else {
          newParams.view = undefined;
          newParams.documentId = undefined;
        }
      } else if (selectedOption === 'translate') {
        newParams.option = 'translate';
        newParams.view = undefined;
        newParams.documentId = undefined;
      } else {
        newParams.option = undefined;
        newParams.view = undefined;
        newParams.documentId = undefined;
      }
      
      updateURL(newParams);
    }
  }, [currentStep, selectedOption, showTranslatedDataView, selectedCompany, selectedDocumentId, isInitialized, updateURL, getURLParams]);

  // Company Selection Screen
  const renderCompanySelection = () => (
    <div className="max-w-4xl mx-auto">
      {/* Çevirmen Header - Sadece şirket seçim ekranında */}
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Çevirmen
        </h1>
      </div>
      
      <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8">
        {/* Header with View Toggle and Add Company Button */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Şirket Seçin
          </h2>
          
          <div className="flex items-center space-x-3">
            {/* Add Company Button - Always visible */}
            <button
              onClick={() => setShowAddCompanyModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Yeni Şirket Ekle
            </button>
            
            {/* View Toggle - Only when companies exist */}
            {companies.length > 0 && (
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 6h18m-9 8h9m-9 4h9m-9-8h9m-9 4h9"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Şirket verileri yükleniyor...
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Lütfen bekleyin, veriler webhook'tan çekiliyor.
            </p>
          </div>
        )}
        
        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Veri yükleme hatası
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </p>
            <button
              onClick={refreshCompanies}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        )}
        
        {!isLoading && !error && companies.length === 0 ? (
          // No companies - show add company prompt
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Henüz şirket eklenmemiş
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Çeviri işlemlerine başlamak için önce bir şirket ekleyin. Yukarıdaki "Yeni Şirket Ekle" butonunu kullanabilirsiniz.
            </p>
          </div>
        ) : !isLoading && !error && companies.length > 0 ? (
          // Companies exist - show selection in grid or table view
          <div>
            {viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {companies.map((company) => (
                  <div
                    key={company.id}
                    onClick={() => handleCompanySelect(company)}
                    className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:border-blue-400 transition-all duration-200 group relative overflow-hidden"
                  >
                    {/* Header with icon and actions */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-800/40 transition-colors flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"></path>
                        </svg>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={(e) => handleEditCompany(company, e)}
                          className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20 rounded-md transition-colors"
                          title="Şirketi Düzenle"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleRemoveCompany(company, e)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          title="Şirketi Sil"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Company info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-base text-gray-900 dark:text-white truncate">
                          {company.name}
                        </h3>
                        {company.company_code && (
                          <span className="text-xs text-gray-600 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full font-mono">
                            {company.company_code}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(company.createdAt).toLocaleDateString('tr-TR')}
                      </p>
                      {company.username && (
                        <div>
                          <span className="inline-block text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-full font-medium">
                            @{company.username}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* File indicators */}
                    {(company.glossaryFileName || company.glossary_file || company.normalizeFileName || company.normalize_file) && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                        <div className="flex flex-wrap gap-2">
                          {(company.glossaryFileName || company.glossary_file) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                              </svg>
                              Glossary
                            </span>
                          )}
                          {(company.normalizeFileName || company.normalize_file) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                              </svg>
                              Normalize
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Seç Butonu */}
                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-600">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompanySelect(company);
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Seç
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Table View
              <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{width: '35%'}}>
                        Şirket
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{width: '15%'}}>
                        Tarih
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{width: '20%'}}>
                        Kullanıcı
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" style={{width: '30%'}}>
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {companies.map((company) => (
                        <tr
                          key={company.id}
                          onClick={() => handleCompanySelect(company)}
                          className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                        >
                          <td className="px-3 py-4">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"/>
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {company.name}
                                  </div>
                                  {company.company_code && (
                                    <span className="text-xs text-gray-600 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full font-mono">
                                      {company.company_code}
                                    </span>
                                  )}
                                </div>
                                {company.username && (
                                  <div className="text-xs text-blue-600 dark:text-blue-400 truncate mt-1">
                                    @{company.username}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-4 text-xs text-gray-500 dark:text-gray-400">
                            {new Date(company.createdAt).toLocaleDateString('tr-TR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </td>
                          <td className="px-2 py-4">
                            {company.username ? (
                              <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 truncate max-w-full">
                                @{company.username}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-600 dark:text-gray-200">-</span>
                            )}
                          </td>
                          <td className="px-2 py-4 text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCompanySelect(company);
                                }}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors flex items-center"
                                title="Seç"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                Seç
                              </button>
                              <button
                                onClick={(e) => handleEditCompany(company, e)}
                                className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                title="Düzenle"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                </svg>
                              </button>
                              <button
                                onClick={(e) => handleRemoveCompany(company, e)}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Sil"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );

  // Company Options Screen
  const renderCompanyTabs = () => {
    // Eğer bir seçenek seçildiyse, o seçeneğin içeriğini göster
    if (selectedOption === 'translations') {
      return renderTranslationsTab();
    }
    if (selectedOption === 'translate') {
      return renderTranslateTab();
    }

    // Seçenek seçilmemişse, card seçim ekranını göster
    return (
      <div className="w-full max-w-6xl mx-auto">
        {/* Navigasyon ve Header */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={handleBackToCompanySelection}
              className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
              </svg>
              <span className="text-sm font-medium">Şirket Seçimi</span>
            </button>
          </div>
          
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {selectedCompany?.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Yapmak istediğiniz işlemi seçin
            </p>
          </div>
        </div>

        {/* Option Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Çeviriler Card */}
          <div
            onClick={() => {
              if (!isLoadingTranslationView) {
                setSelectedOption('translations');
              }
            }}
            className={`bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8 transition-all duration-300 group ${
              isLoadingTranslationView 
                ? 'cursor-not-allowed opacity-60' 
                : 'cursor-pointer hover:shadow-xl hover:border-blue-400 dark:hover:border-blue-500'
            }`}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 dark:group-hover:bg-purple-800/40 transition-colors">
                <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {selectedCompany?.name} Çevirileri
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Geçmiş çevirilerinizi görüntüleyin ve yönetin
              </p>
              <div className="inline-flex items-center text-purple-600 dark:text-purple-400 font-medium">
                {isLoadingTranslationView ? (
                  <>
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    Çevirileri Görüntüle
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Çeviri Yap Card */}
          <div
            onClick={() => setSelectedOption('translate')}
            className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8 cursor-pointer hover:shadow-xl hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 group"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/40 transition-colors">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {selectedCompany?.name} İçin Çeviri Yap
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Yeni dosya yükleyip çeviri işlemi başlatın
              </p>
              <div className="inline-flex items-center text-blue-600 dark:text-blue-400 font-medium">
                Çeviri Başlat
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Normalize Turkish characters for search
  const normalizeTurkish = (text: string): string => {
    // Use Turkish locale for proper lowercase conversion (İ→i, I→ı)
    return text.toLocaleLowerCase('tr-TR');
  };

  // Build a regex-safe search pattern that supports Turkish-aware case-insensitive matching.
  const buildTurkishAwareSearchPattern = (text: string, isCaseSensitive: boolean): string => {
    const normalizedText = text.normalize('NFC');
    if (isCaseSensitive) {
      return normalizedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    return Array.from(normalizedText).map(char => {
      const lower = char.toLocaleLowerCase('tr-TR');
      const upper = char.toLocaleUpperCase('tr-TR');
      if (lower !== upper) {
        const escapedLower = lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const escapedUpper = upper.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return `[${escapedLower}${escapedUpper}]`;
      }
      return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }).join('');
  };

  // Search function for translated data - arama aktifken sadece eşleşen satırları döndür
  const searchTranslatedData = (data: typeof translatedData) => {
    const activeSearch = turkishSearch.trim() || englishSearch.trim();
    if (!activeSearch) return data;

    const type: 'turkish' | 'english' = turkishSearch.trim() ? 'turkish' : 'english';
    const isCaseSensitive = type === 'turkish' ? isTurkishCaseSensitive : isEnglishCaseSensitive;
    const isWholeWord = type === 'turkish' ? isTurkishWholeWord : isEnglishWholeWord;

    return data?.filter(item => {
      const turkishText = parseRunsText(item.runs) as string || '';
      const englishText = parseRunsText(item.en_runs) as string || '';
      const text = type === 'turkish' ? turkishText : englishText;

      if (isWholeWord) {
        if (isCaseSensitive) {
          const normalizedText = text.normalize('NFC');
          const normalizedSearch = activeSearch.normalize('NFC');
          const escaped = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escaped})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu');
          return regex.test(normalizedText);
        } else {
          const normalizedText = normalizeTurkish(text);
          const normalizedSearch = normalizeTurkish(activeSearch);
          const escaped = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escaped})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu');
          return regex.test(normalizedText);
        }
      } else {
        if (isCaseSensitive) {
          return text.normalize('NFC').includes(activeSearch.normalize('NFC'));
        } else {
          return normalizeTurkish(text).includes(normalizeTurkish(activeSearch));
        }
      }
    }) || [];
  };

  // Save current position before starting search
  const savePreSearchPosition = () => {
    // Only save if not already saved (i.e., this is the first search)
    if (preSearchPosition === null) {
      const tableContainer = 
        document.querySelector('[data-testid="translation-table-container"]') ||
        document.querySelector('[data-testid="translation-table-scroll-container"]') ||
        document.querySelector('[data-testid="translation-table-main-container"]') ||
        document.querySelector('.overflow-auto.flex-1') ||
        document.querySelector('.overflow-y-scroll') ||
        document.querySelector('.overflow-y-auto');
      
      const scrollTop = tableContainer?.scrollTop || 0;
      setPreSearchPosition({
        page: translatedCurrentPage,
        scrollTop: scrollTop
      });
    }
  };

  // Search results calculation
  const calculateSearchResults = (data: typeof translatedData, searchTerm: string, type: 'turkish' | 'english') => {
    if (!data || !searchTerm.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      setSearchType(null);
      return;
    }

    // Use appropriate search options based on search type
    const isCaseSensitive = type === 'turkish' ? isTurkishCaseSensitive : isEnglishCaseSensitive;
    const isWholeWord = type === 'turkish' ? isTurkishWholeWord : isEnglishWholeWord;

    const results: {sid: string, index: number}[] = [];
    
    data.forEach((item, index) => {
      const turkishText = parseRunsText(item.runs) as string || '';
      const englishText = parseRunsText(item.en_runs) as string || '';
      
      const text = type === 'turkish' ? turkishText : englishText;
      
      let isMatch = false;
      
      if (isWholeWord) {
        // Whole word matching - use custom word boundaries for better Turkish support
        if (isCaseSensitive) {
          // Case sensitive: normalize Unicode but preserve case
          const normalizedText = text.normalize('NFC');
          const normalizedSearchTerm = searchTerm.normalize('NFC');
          const escapedSearchTerm = normalizedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Match word only if surrounded by whitespace, punctuation, or start/end of string
          const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchTerm})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu');
          isMatch = regex.test(normalizedText);
        } else {
          // Case insensitive: normalize both text and search term (for Turkish chars)
          const normalizedText = normalizeTurkish(text);
          const normalizedSearchTerm = normalizeTurkish(searchTerm);
          const escapedSearchTerm = normalizedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${escapedSearchTerm})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu');
          isMatch = regex.test(normalizedText);
        }
      } else {
        // Simple substring matching
        if (isCaseSensitive) {
          // Normalize Unicode but preserve case for Turkish chars
          const normalizedText = text.normalize('NFC');
          const normalizedSearchTerm = searchTerm.normalize('NFC');
          isMatch = normalizedText.includes(normalizedSearchTerm);
        } else {
          const normalizedText = normalizeTurkish(text);
          const normalizedSearchTerm = normalizeTurkish(searchTerm);
          isMatch = normalizedText.includes(normalizedSearchTerm);
        }
      }
      
      if (isMatch) {
        results.push({ sid: item.sid, index: index + 1 });
      }
    });
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
    setSearchType(type);
    
    // Jump to first result if any
    if (results.length > 0) {
      jumpToSearchResult(0, results);
    }
  };

  // Jump to specific search result
  const jumpToSearchResult = (resultIndex: number, results = searchResults) => {
    if (results.length === 0 || resultIndex < 0 || resultIndex >= results.length) return;
    
    const result = results[resultIndex];
    const targetRow = translatedData?.find(item => item.sid === result.sid);
    
    if (targetRow) {
      // Set highlighted row
      setHighlightedRowSid(targetRow.sid);
      
      // Clear highlight after animation
      setTimeout(() => {
        setHighlightedRowSid(null);
      }, 3000);
      
      // Arama aktifken filtrelenmiş listedeki pozisyonu kullan
      let targetPage: number;
      if (turkishSearch.trim() || englishSearch.trim()) {
        const filteredIndex = processedTranslatedData?.findIndex(item => item.sid === result.sid) ?? -1;
        targetPage = filteredIndex !== -1 ? Math.ceil((filteredIndex + 1) / translatedItemsPerPage) : 1;
      } else {
        targetPage = Math.ceil(result.index / translatedItemsPerPage);
      }
      setTranslatedCurrentPage(targetPage);
      
      // Scroll to the row after a short delay to ensure page change
      setTimeout(() => {
        const rowElement = document.getElementById(`translated-row-${targetRow.sid}`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Navigate to next search result
  const goToNextSearchResult = () => {
    if (searchResults.length === 0) return;
    
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    jumpToSearchResult(nextIndex);
  };

  // Navigate to previous search result
  const goToPreviousSearchResult = () => {
    if (searchResults.length === 0) return;
    
    const prevIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    jumpToSearchResult(prevIndex);
  };

  // Go to row functions for translated data
  const handleTranslatedGoToRow = () => {
    const rowNumber = parseInt(goToRowValue);
    const totalRows = translatedData?.length || 0;
    
    if (rowNumber && rowNumber >= 1 && rowNumber <= totalRows) {
      // Clear all filters and sorting to show original table
      setSearchTerm('');
      setTurkishSearch('');
      setEnglishSearch('');
      setSortField(null);
      setSortDirection('asc');
      setShowCriticalErrorsOnly(false);
      setShowHumanReviewOnly(false);
      setViolationSortOrder('none');
      setQualityScoreSortOrder('none');
      
      // Find the target row's sid from original data
      const targetRow = translatedData?.[rowNumber - 1];
      if (targetRow) {
        // Set highlighted row
        setHighlightedRowSid(targetRow.sid);
        
        // Clear highlight after animation
        setTimeout(() => {
          setHighlightedRowSid(null);
        }, 3000);
        
        // Calculate which page the row is on (based on original data)
        const targetPage = Math.ceil(rowNumber / translatedItemsPerPage);
        setTranslatedCurrentPage(targetPage);
        
        // Scroll to the row after a short delay to ensure page change and filters are cleared
        setTimeout(() => {
          const rowElement = document.getElementById(`translated-row-${targetRow.sid}`);
          if (rowElement) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 200); // Increased delay to ensure filters are cleared
        
        setGoToRowValue('');
      }
    }
  };

  const handleTranslatedGoToRowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setGoToRowValue(value);
      
      // Önceki timeout'u iptal et
      if (goToRowTimeoutRef.current) {
        clearTimeout(goToRowTimeoutRef.current);
      }
      
      // Eğer değer boşsa, timeout kurma
      if (value === '') {
        return;
      }
      
      // Debounce: Kullanıcı yazmayı bitirdikten 500ms sonra satıra git
      goToRowTimeoutRef.current = setTimeout(() => {
        const rowNumber = parseInt(value);
        const totalRows = translatedData?.length || 0;
        
        if (rowNumber && rowNumber >= 1 && rowNumber <= totalRows) {
          // Clear all filters and sorting to show original table
          setSearchTerm('');
          setTurkishSearch('');
          setEnglishSearch('');
          setSortField(null);
          setSortDirection('asc');
          setShowCriticalErrorsOnly(false);
          setShowHumanReviewOnly(false);
          setViolationSortOrder('none');
          setQualityScoreSortOrder('none');
          
          // Find the target row's sid from original data
          const targetRow = translatedData?.[rowNumber - 1];
          if (targetRow) {
            // Set highlighted row
            setHighlightedRowSid(targetRow.sid);
            
            // Clear highlight after animation
            setTimeout(() => {
              setHighlightedRowSid(null);
            }, 3000);
            
            // Calculate which page the row is on (based on original data)
            const targetPage = Math.ceil(rowNumber / translatedItemsPerPage);
            setTranslatedCurrentPage(targetPage);
            
            // Scroll to the row after a short delay to ensure page change and filters are cleared
            setTimeout(() => {
              const rowElement = document.getElementById(`translated-row-${targetRow.sid}`);
              if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 200);
          }
        }
      }, 500); // 500ms debounce
    }
  };

  // Handle go to row change for translation table (runs table)
  const handleGoToRowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setGoToRowValue(value);
      
      // Önceki timeout'u iptal et
      if (goToRowTimeoutRef.current) {
        clearTimeout(goToRowTimeoutRef.current);
      }
      
      // Eğer değer boşsa, timeout kurma
      if (value === '') {
        return;
      }
      
      // Debounce: Kullanıcı yazmayı bitirdikten 500ms sonra satıra git
      goToRowTimeoutRef.current = setTimeout(() => {
        const rowNumber = parseInt(value);
        const totalRows = translationResults?.length || 0;
        
        if (rowNumber && rowNumber >= 1 && rowNumber <= totalRows) {
          // Calculate which page the row is on
          const targetPage = Math.ceil(rowNumber / itemsPerPage);
          setCurrentPage(targetPage);
          
          // Find the target row's sid
          const targetRow = translationResults?.[rowNumber - 1];
          if (targetRow) {
            // Scroll to the row after a short delay to ensure page change
            setTimeout(() => {
              const rowElement = document.getElementById(`translation-row-${targetRow.sid}`);
              if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 200);
          }
        }
      }, 500); // 500ms debounce
    }
  };

  const handleTranslatedGoToRowKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTranslatedGoToRow();
    }
  };

  // Sorting function for translated data
  const sortTranslatedData = (data: typeof translatedData) => {
    if (!data || !sortField || !qualityControlResults) return data;

    return [...data].sort((a, b) => {
      const aQuality = qualityControlResults.find(qr => qr.sid === a.sid);
      const bQuality = qualityControlResults.find(qr => qr.sid === b.sid);

      // Items without quality results go to the end
      if (!aQuality && !bQuality) return 0;
      if (!aQuality) return 1;
      if (!bQuality) return -1;

      const aValue = aQuality[sortField] ?? 0;
      const bValue = bQuality[sortField] ?? 0;

      // Ensure we're working with numbers
      const aNum = typeof aValue === 'number' ? aValue : 0;
      const bNum = typeof bValue === 'number' ? bValue : 0;

      if (sortDirection === 'desc') {
        return bNum - aNum;
      } else {
        return aNum - bNum;
      }
    });
  };

  // Handle sorting
  const handleSort = () => {
    // Kalite kontrol verileri yüklenmemişse sıralama yapma
    if (!qualityControlResults || qualityControlResults.length === 0) {
      console.log('⚠️ Kalite kontrol verileri yüklenmemiş, sıralama devre dışı');
      return;
    }

    // Kalite kontrol verileri doğru dokümana ait değilse sıralama yapma
    if (qualityControlDocumentId !== selectedDocumentId) {
      console.log('⚠️ Kalite kontrol verileri farklı dokümana ait, sıralama devre dışı');
      return;
    }

    if (sortField === 'score') {
      if (sortDirection === 'desc') {
        // First click: desc -> asc
        setSortDirection('asc');
      } else {
        // Second click: asc -> off (no sorting)
        setSortField(null);
        setSortDirection('desc'); // Reset to default for next time
      }
    } else {
      // Start sorting by score, default to descending (highest scores first)
      setSortField('score');
      setSortDirection('desc');
    }
    // Reset to first page when sorting changes
    setTranslatedCurrentPage(1);
  };

  // Refresh only translated data without affecting quality control data or pagination/sorting
  const refreshTranslatedDataOnly = useCallback(async (documentId: string, documentName: string) => {
    console.log('🔄 refreshTranslatedDataOnly başlatıldı:', { documentId, documentName });
    
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/get_translated_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Translated data başarıyla yenilendi');
        setTranslatedData(data);
        setTranslatedDataError(null);
        
        // Refresh completion data and change history as well
        await fetchCompletionData(documentId);
        await fetchEnChangeHistory(documentId);
      } else {
        const errorText = await response.text();
        console.error('❌ Translated data yenilenirken hata:', response.status, response.statusText, errorText);
        setTranslatedDataError('Veri yenilenirken hata oluştu');
      }
    } catch (error) {
      console.error('❌ Translated data yenilenirken hata:', error);
      setTranslatedDataError('Veri yenilenirken hata oluştu');
    }
  }, [fetchCompletionData, fetchEnChangeHistory]);

  // Apply corrected version to main table
  const applyCorrectedVersion = async (sid: string, correctedText: string) => {
    try {
      if (!selectedDocumentId || !user?.id) {
        console.warn('Document ID veya User ID bulunamadı');
        return;
      }

      // Get original en_runs to preserve bold value
      const currentRow = translatedData?.find(item => item.sid === sid);
      const originalEnRuns = currentRow?.en_runs;

      // Create runs string from corrected text (preserve bold)
      const newEnRuns = createRunsString(correctedText, originalEnRuns);
      
      // Save the correction directly without using logEnChanges to avoid unnecessary refreshes
      const logData = {
        sid: sid,
        en_runs: newEnRuns,
        document_id: selectedDocumentId,
        user_id: user.id,
        timestamp: Math.floor(Date.now() / 1000)
      };

      console.log('EN Log changes gönderiliyor:', logData);

      const response = await fetch('https://n8n.mahir.ai/webhook/en_log_changes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData)
      });

      if (response.ok) {
        console.log('EN Log changes başarıyla gönderildi');
        
        // Only refresh translated data, preserve pagination and sorting
        if (selectedDocumentId && selectedDocumentName) {
          await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
        }
        
        // Show success message
        success('Başarılı', 'Düzeltilmiş sürüm ana tabloya uygulandı.');
        
        // Close the quality detail modal
        setSelectedQualityResult(null);
      } else {
        console.error('EN Log changes gönderilirken hata:', response.status, response.statusText);
        showError('Hata', 'Düzeltilmiş sürüm uygulanırken hata oluştu.');
      }
      
    } catch (error) {
      console.error('Düzeltilmiş sürüm uygulanırken hata:', error);
      showError('Hata', 'Düzeltilmiş sürüm uygulanırken hata oluştu.');
    }
  };

  // Helper function to highlight only the changed parts
  const highlightDifferences = (oldText: string, newText: string, searchText: string) => {
    const regex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    
    // Find all matches in the old text
    const matches: {start: number, end: number, match: string}[] = [];
    let match;
    while ((match = regex.exec(oldText)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        match: match[0]
      });
    }
    
    return { matches, oldText, newText };
  };

  // Generate replace preview
  const generateReplacePreview = useCallback((searchText: string, replaceWith: string) => {
    if (!searchText || !replaceWith || !translatedData) {
      setReplacePreview([]);
      return;
    }

    const preview: {sid: string, oldText: string, newText: string, rowNumber: number}[] = [];
    
    // Use EXACT same logic as calculateSearchResults for English search
    translatedData.forEach((item, index) => {
      const englishText = parseRunsText(item.en_runs) as string || '';
      
      const searchPattern = buildTurkishAwareSearchPattern(searchText, isEnglishCaseSensitive);
      const regex = isEnglishWholeWord
        ? new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu')
        : new RegExp(searchPattern, 'gu');

      regex.lastIndex = 0;
      const hasMatch = regex.test(englishText.normalize('NFC'));
      
      if (hasMatch) {
        const originalEnglishText = englishText.normalize('NFC');
        regex.lastIndex = 0;
        // For whole word with capture groups, preserve surrounding characters
        const newText = isEnglishWholeWord 
          ? originalEnglishText.replace(regex, `$1${replaceWith}$3`)
          : originalEnglishText.replace(regex, replaceWith);
        
        if (originalEnglishText !== newText) {
          preview.push({
            sid: item.sid,
            oldText: originalEnglishText,
            newText: newText,
            rowNumber: index + 1  // Add row number (1-indexed)
          });
        }
      }
    });

    setReplacePreview(preview);
  }, [translatedData, parseRunsText, isEnglishCaseSensitive, isEnglishWholeWord]);

  // Single row replace function (like en_log_changes)
  const handleSingleReplace = async (sid: string, oldText: string, newText: string) => {
    if (!selectedDocumentId || !user?.id || !translatedData) {
      showError('Hata', 'Gerekli bilgiler eksik.');
      return;
    }

    // Close edit mode if this row is being edited
    if (editingEnSid === sid) {
      setEditingEnSid(null);
      setEditingEnText('');
    }

    try {
      // Find the item from translatedData
      const item = translatedData.find(d => d.sid === sid);
      if (!item || !item.en_runs) {
        showError('Hata', 'Satır bulunamadı.');
        return;
      }

      // Parse and update en_runs
      const searchPattern = buildTurkishAwareSearchPattern(englishSearch, isEnglishCaseSensitive);
      const regex = isEnglishWholeWord
        ? new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu')
        : new RegExp(searchPattern, 'gu');
      
      const runs = JSON.parse(item.en_runs);
      
      if (Array.isArray(runs) && runs.length > 0) {
        const newRuns = runs.map(run => {
          if (run.text && typeof run.text === 'string') {
            const originalRunText = run.text.normalize('NFC');
            regex.lastIndex = 0;
            const updatedNormalizedText = isEnglishWholeWord 
              ? originalRunText.replace(regex, `$1${replaceText}$3`)
              : originalRunText.replace(regex, replaceText);
            
            if (updatedNormalizedText !== originalRunText) {
              return { ...run, text: updatedNormalizedText };
            }
          }
          return run;
        });

        const newEnRuns = JSON.stringify(newRuns);

        // Send to en_log_changes endpoint (same as table edit)
        const response = await fetch('https://n8n.mahir.ai/webhook/en_log_changes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            document_id: selectedDocumentId,
            user_id: user.id,
            sid: sid,
            en_runs: newEnRuns,
            timestamp: Math.floor(Date.now() / 1000)
          })
        });

        if (response.ok) {
          console.log('✅ Tek satır değişiklik başarılı:', sid);
          
          // Remove this item from preview
          setReplacePreview(prev => prev.filter(p => p.sid !== sid));
          
          // Refresh translated data
          if (selectedDocumentId && selectedDocumentName) {
            await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
          }
          
          success('Başarılı', 'Değişiklik kaydedildi.');
        } else {
          throw new Error('Değişiklik kaydedilemedi');
        }
      }
    } catch (error) {
      console.error('Tek satır değiştirme hatası:', error);
      showError('Hata', 'Değişiklik kaydedilirken hata oluştu.');
    }
  };

  // Find and Replace function
  const handleFindAndReplace = async () => {
    if (!englishSearch || !replaceText || !translatedData || !selectedDocumentId || !user?.id) {
      showError('Hata', 'Lütfen arama metni ve değiştirilecek metni girin.');
      return;
    }

    // Close edit mode if open
    if (editingEnSid) {
      setEditingEnSid(null);
      setEditingEnText('');
    }

    setIsFindReplaceProcessing(true);

    try {
      const changes: { sid: string; en_runs: string }[] = [];
      let matchCount = 0;

      const searchPattern = buildTurkishAwareSearchPattern(englishSearch, isEnglishCaseSensitive);
      const regex = isEnglishWholeWord
        ? new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu')
        : new RegExp(searchPattern, 'gu');

      // Iterate through all translated data - use EXACT same logic as calculateSearchResults
      translatedData.forEach(item => {
        const englishText = parseRunsText(item.en_runs) as string || '';
        regex.lastIndex = 0;
        const hasMatch = regex.test(englishText.normalize('NFC'));
        
        if (!hasMatch) {
          return; // Skip items that don't match
        }
        
        // Process the actual en_runs JSON
        if (item.en_runs && item.en_runs.trim() !== '' && item.en_runs !== '[]') {
          try {
            const runs = JSON.parse(item.en_runs);
            if (Array.isArray(runs) && runs.length > 0) {
              let hasChange = false;
              const newRuns = runs.map(run => {
                if (run.text && typeof run.text === 'string') {
                  const originalRunText = run.text.normalize('NFC');
                  regex.lastIndex = 0;
                  const matches = originalRunText.match(regex);
                  if (matches) matchCount += matches.length;
                  
                  const updatedNormalizedText = isEnglishWholeWord 
                    ? originalRunText.replace(regex, `$1${replaceText}$3`)
                    : originalRunText.replace(regex, replaceText);
                  
                  if (updatedNormalizedText !== originalRunText) {
                    hasChange = true;
                    return { ...run, text: updatedNormalizedText };
                  }
                }
                return run;
              });

              if (hasChange) {
                const newEnRuns = JSON.stringify(newRuns);
                changes.push({
                  sid: item.sid,
                  en_runs: newEnRuns
                });
              }
            }
          } catch (e) {
            console.error('En_runs parse hatası:', e);
          }
        }
      });

      if (changes.length === 0) {
        showError('Eşleşme Bulunamadı', `"${englishSearch}" için hiçbir eşleşme bulunamadı.`);
        setIsFindReplaceProcessing(false);
        return;
      }

      // Send batch request to multiple_en_log_changes
      const batchData = {
        document_id: selectedDocumentId,
        user_id: user.id,
        timestamp: Math.floor(Date.now() / 1000),
        changes: changes
      };

      console.log('Multiple EN Log changes gönderiliyor:', batchData);

      const response = await fetch('https://n8n.mahir.ai/webhook/multiple_en_log_changes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batchData)
      });

      if (response.ok) {
        console.log('Multiple EN Log changes başarıyla gönderildi');
        
        // Refresh translated data
        if (selectedDocumentId && selectedDocumentName) {
          await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
        }
        
        success('Başarılı', `${matchCount} eşleşme "${englishSearch}" → "${replaceText}" ile değiştirildi.`);
        
        // Reset
        setShowReplaceInput(false);
        setReplaceText('');
        setReplacePreview([]);
        setEnglishSearch('');
      } else {
        const errorText = await response.text();
        console.error('Multiple EN Log changes hatası:', response.status, response.statusText, errorText);
        showError('Hata', 'Değişiklikler kaydedilirken hata oluştu.');
      }
    } catch (error) {
      console.error('Find and replace hatası:', error);
      showError('Hata', 'Find and replace işlemi sırasında hata oluştu.');
    } finally {
      setIsFindReplaceProcessing(false);
    }
  };

  // Memoized quality control lookup map for O(1) access
  const qualityControlMap = useMemo(() => {
    if (!qualityControlResults || qualityControlDocumentId !== selectedDocumentId) {
      return new Map();
    }
    return new Map(qualityControlResults.map(qr => [qr.sid, qr]));
  }, [qualityControlResults, qualityControlDocumentId, selectedDocumentId]);

  // Get all unique violation types from quality control results
  const availableViolationTypes = useMemo(() => {
    if (!qualityControlResults || qualityControlDocumentId !== selectedDocumentId) {
      return [];
    }
    
    const violationCodesSet = new Set<string>();
    qualityControlResults.forEach((qr: any) => {
      if (qr.violations) {
        try {
          const violationsList = JSON.parse(qr.violations);
          violationsList.forEach((violation: {code: string, detail: string}) => {
            if (violation.code) {
              violationCodesSet.add(violation.code);
            }
          });
        } catch (e) {
          // Parse error, skip
        }
      }
    });
    
    return Array.from(violationCodesSet).sort();
  }, [qualityControlResults, qualityControlDocumentId, selectedDocumentId]);

  // Get violation type counts
  const violationTypeCounts = useMemo(() => {
    if (!qualityControlResults || qualityControlDocumentId !== selectedDocumentId) {
      return new Map<string, number>();
    }
    
    const countsMap = new Map<string, number>();
    qualityControlResults.forEach((qr: any) => {
      if (qr.violations) {
        try {
          const violationsList = JSON.parse(qr.violations);
          violationsList.forEach((violation: {code: string, detail: string}) => {
            if (violation.code) {
              countsMap.set(violation.code, (countsMap.get(violation.code) || 0) + 1);
            }
          });
        } catch (e) {
          // Parse error, skip
        }
      }
    });
    
    return countsMap;
  }, [qualityControlResults, qualityControlDocumentId, selectedDocumentId]);

  // Get unique usernames from change history
  const uniqueUsernames = useMemo(() => {
    if (!enChangeHistory || enChangeHistory.length === 0) {
      return [];
    }
    
    const usernamesSet = new Set<string>();
    enChangeHistory.forEach((historyItem: any) => {
      if (historyItem.changes && historyItem.changes.length > 0) {
        historyItem.changes.forEach((change: any) => {
          if (change.username) {
            usernamesSet.add(change.username);
          }
        });
      }
    });
    
    return Array.from(usernamesSet).sort();
  }, [enChangeHistory]);

  // Get unique usernames from comments
  const uniqueCommentUsernames = useMemo(() => {
    if (!rowComments || Object.keys(rowComments).length === 0) {
      return [];
    }
    
    const usernamesSet = new Set<string>();
    Object.values(rowComments).forEach((comments) => {
      comments.forEach((comment) => {
        if (comment.user) {
          usernamesSet.add(comment.user);
        }
      });
    });
    
    return Array.from(usernamesSet).sort();
  }, [rowComments]);

  // Memoized filter and sort function
  const filterAndSortTranslatedDataByQuality = useCallback((data: typeof translatedData) => {
    if (!data || qualityControlMap.size === 0) {
      return data;
    }

    // First filter the data
    let filteredData = data.filter(item => {
      const qualityResult = qualityControlMap.get(item.sid);
      
      // If no quality result found for this item, include it by default
      if (!qualityResult) {
        return true;
      }

      // Critical errors filter
      if (showCriticalErrorsOnly && !qualityResult.hard_fail) {
        return false;
      }

      // Human review filter
      if (showHumanReviewOnly && !qualityResult.needs_human_review) {
        return false;
      }

      return true;
    });

    // Apply hide checked items filter (independent of quality control)
    if (hideCheckedCriticalErrors) {
      filteredData = filteredData.filter(item => !item.is_checked);
    }

    // Changed rows filter (check if row has change history)
    if (showChangedRowsOnly) {
      filteredData = filteredData.filter(item => {
        const hasChanges = enChangeHistory?.some((historyItem: any) => 
          historyItem.sid === item.sid && historyItem.changes && historyItem.changes.length > 0
        );
        return hasChanges;
      });
    }

    // User filter (filter by username who made changes)
    if (selectedUserFilter) {
      filteredData = filteredData.filter(item => {
        const historyItem = enChangeHistory?.find((hi: any) => hi.sid === item.sid);
        if (!historyItem || !historyItem.changes) {
          return false;
        }
        return historyItem.changes.some((change: any) => change.username === selectedUserFilter);
      });
    }

    // Comments rows filter (check if row has comments)
    if (showCommentsRowsOnly) {
      filteredData = filteredData.filter(item => {
        const hasComments = rowComments[item.sid] && rowComments[item.sid].length > 0;
        return hasComments;
      });
    }

    // Comment user filter (filter by username who made comments)
    if (selectedCommentUserFilter) {
      filteredData = filteredData.filter(item => {
        const comments = rowComments[item.sid];
        if (!comments || comments.length === 0) {
          return false;
        }
        return comments.some((comment: any) => comment.user === selectedCommentUserFilter);
      });
    }

    // Violation type filter (filter by selected violation types)
    if (selectedViolationTypes.size > 0) {
      filteredData = filteredData.filter(item => {
        const qualityResult = qualityControlMap.get(item.sid);
        if (!qualityResult || !qualityResult.violations) {
          return false;
        }
        
        try {
          const violationsList = JSON.parse(qualityResult.violations);
          // Check if any of the violations in this item match selected types
          return violationsList.some((violation: {code: string, detail: string}) => 
            selectedViolationTypes.has(violation.code)
          );
        } catch (e) {
          return false;
        }
      });
    }

    // Then apply sorting
    if (violationSortOrder !== 'none' || qualityScoreSortOrder !== 'none') {
      filteredData = [...filteredData].sort((a, b) => {
        const qualityResultA = qualityControlMap.get(a.sid);
        const qualityResultB = qualityControlMap.get(b.sid);

        // If one item doesn't have quality results, put it at the end
        if (!qualityResultA && qualityResultB) return 1;
        if (qualityResultA && !qualityResultB) return -1;
        if (!qualityResultA && !qualityResultB) return 0;

        // Sort by violation count if selected
        if (violationSortOrder !== 'none') {
          const violationDiff = qualityResultA!.n_violation - qualityResultB!.n_violation;
          if (violationDiff !== 0) {
            return violationSortOrder === 'asc' ? violationDiff : -violationDiff;
          }
        }

        // Sort by quality score if selected
        if (qualityScoreSortOrder !== 'none') {
          const scoreDiff = qualityResultA!.score - qualityResultB!.score;
          return qualityScoreSortOrder === 'asc' ? scoreDiff : -scoreDiff;
        }

        return 0;
      });
    }

    return filteredData;
  }, [qualityControlMap, showCriticalErrorsOnly, showHumanReviewOnly, showChangedRowsOnly, violationSortOrder, qualityScoreSortOrder, hideCheckedCriticalErrors, enChangeHistory, selectedUserFilter, showCommentsRowsOnly, selectedCommentUserFilter, rowComments, selectedViolationTypes]);

  // Check if there are any English text values in translated data
  const hasEnglishTextValues = () => {
    if (!translatedData || translatedData.length === 0) return false;
    
    return translatedData.some(item => 
      item.en_runs && 
      item.en_runs.trim() !== '' && 
      item.en_runs !== '[]'
    );
  };

  // Memoized data processing pipeline
  const processedTranslatedData = useMemo(() => {
    if (!translatedData) return null;
    
    // First search, then filter and sort by quality, then sort by general criteria
    const searchedData = searchTranslatedData(translatedData);
    const qualityFilteredAndSortedData = filterAndSortTranslatedDataByQuality(searchedData);
    const sortedData = sortTranslatedData(qualityFilteredAndSortedData);
    
    return sortedData;
  }, [translatedData, filterAndSortTranslatedDataByQuality, sortField, sortDirection, turkishSearch, englishSearch, isTurkishCaseSensitive, isTurkishWholeWord, isEnglishCaseSensitive, isEnglishWholeWord]);

  // Keep pagination for compatibility
  const paginatedTranslatedData = useMemo(() => {
    if (!processedTranslatedData || !translatedData) return { items: [], totalItems: 0, totalPages: 0 };
    
    const totalTranslatedItems = processedTranslatedData.length;
    const totalTranslatedPages = Math.ceil(totalTranslatedItems / translatedItemsPerPage);
    const startTranslatedIndex = (translatedCurrentPage - 1) * translatedItemsPerPage;
    const endTranslatedIndex = startTranslatedIndex + translatedItemsPerPage;
    const currentTranslatedItems = processedTranslatedData.slice(startTranslatedIndex, endTranslatedIndex);
    
    // Her item için orijinal index'i önceden hesapla
    const itemsWithOriginalIndex = currentTranslatedItems.map(item => ({
      ...item,
      originalIndex: translatedData.findIndex(originalItem => originalItem.sid === item.sid)
    }));
    
    return {
      items: itemsWithOriginalIndex,
      totalItems: totalTranslatedItems,
      totalPages: totalTranslatedPages
    };
  }, [processedTranslatedData, translatedData, translatedCurrentPage, translatedItemsPerPage]);


  // Translated Data View
  const renderTranslatedDataView = () => {
    const { items: currentTranslatedItems, totalItems: totalTranslatedItems, totalPages: totalTranslatedPages } = paginatedTranslatedData;

    // Calculate dynamic width for text columns based on hidden columns
    const hiddenColumnsCount = [!showControlColumn, !showGlossaryColumn, !showQualityColumn, !showCommentsColumn].filter(Boolean).length;
    const textColumnWidth = hiddenColumnsCount === 0 ? 'w-5/12' : 
                           hiddenColumnsCount === 1 ? 'w-1/2' : 
                           hiddenColumnsCount === 2 ? 'w-7/12' : 
                           hiddenColumnsCount === 3 ? 'w-2/3' : 'w-3/4';

    return (
      <div className="w-full">
        {/* Navigation */}
        <div className="mb-6 max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setShowTranslatedDataView(false);
                setTranslatedData(null);
                setSelectedDocumentId(null);
                setSelectedDocumentName(null);
                setTranslatedDataError(null);
                setTranslatedCurrentPage(1);
                // Reset sorting
                setSortField(null);
                setSortDirection('desc');
                // Reset search
                setTurkishSearch('');
                setEnglishSearch('');
                // Kalite kontrol verilerini temizle
                console.log('🧹 Kalite kontrol verileri temizleniyor - çeviriler sekmesinden çıkış');
                setQualityControlResults(null);
                setQualityControlDocumentId(null);
                setQualityControlStatus(null);
                setIsCheckingQualityControl(false);
                setIsLoadingQualityResults(false);
                setIsQualityControlDownloadEnabled(false);
                // Kalite kontrol interval'ını temizle
                if (qualityControlIntervalRef.current) {
                  clearInterval(qualityControlIntervalRef.current);
                  qualityControlIntervalRef.current = null;
                }
              }}
              className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
              </svg>
              <span className="text-sm font-medium">Çeviriler</span>
            </button>
          </div>
        </div>

        <div className="max-w-none mx-1">
          {/* Header */}
          <div className="mb-4">
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{selectedDocumentName}</h1>
                  {selectedDocumentId && (
                    <p className="text-xs text-gray-600 dark:text-gray-200 font-mono opacity-60 mb-2">
                      ID: {selectedDocumentId}
                    </p>
                  )}
                  <p className="text-gray-600 dark:text-gray-400">
                    Çeviri detaylarını görüntüleyin ve yönetin
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Compare Documents Button */}
                  <button
                    onClick={fetchCompanyDocumentsForCompare}
                    className="flex flex-col items-center cursor-pointer group"
                    title="Geçmiş Dokümanla Karşılaştır"
                    disabled={isLoadingCompareDocuments}
                  >
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center transition-all duration-300 group-hover:bg-purple-200 dark:group-hover:bg-purple-800/40">
                      {isLoadingCompareDocuments ? (
                        <svg className="animate-spin h-6 w-6 text-purple-600 dark:text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                      )}
                    </div>
                    <span className="text-xs font-medium mt-1 text-purple-600 dark:text-purple-400 transition-colors">
                      Karşılaştır
                    </span>
                  </button>
                  
                  {/* Download Button - Only show if there are English text values and user can download */}
                  {canDownloadFiles() && hasEnglishTextValues() && (
                  <button
                    onClick={() => {
                      if (selectedDocumentId && selectedDocumentName) {
                        setSelectedDocumentForDownload({
                          document_id: selectedDocumentId,
                          file_name_no_ext: selectedDocumentName
                        });
                        setShowDownloadConfirmModal(true);
                      }
                    }}
                    className="flex flex-col items-center cursor-pointer group"
                    title="Dosyayı İndir"
                  >
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center transition-all duration-300 group-hover:bg-green-200 dark:group-hover:bg-green-800/40">
                      <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                    <span className="text-xs font-medium mt-1 text-green-600 dark:text-green-400 transition-colors">
                      Dosyayı İndir
                    </span>
                  </button>
                  )}
                  
                  {/* Kalite Kontrol Butonu - Her zaman görünsün */}
                  <div className="relative">
                    <div
                      ref={qualityControlButtonRef}
                      onMouseEnter={() => {
                        if (qualityControlButtonRef.current) {
                          const rect = qualityControlButtonRef.current.getBoundingClientRect();
                          const viewportHeight = window.innerHeight;
                          const viewportWidth = window.innerWidth;
                          const popoverHeight = 300;
                          const popoverWidth = 320;
                          
                          let top = rect.bottom + 8;
                          if (top + popoverHeight > viewportHeight - 20) {
                            top = rect.top - popoverHeight - 8;
                            if (top < 20) {
                              top = Math.max(20, (viewportHeight - popoverHeight) / 2);
                            }
                          }
                          
                          let right = window.innerWidth - rect.right;
                          if (rect.right + popoverWidth > viewportWidth - 20) {
                            right = 20;
                          }
                          
                          setPopoverPosition({ top, right });
                        }
                        setShowQualityControlPopover(true);
                      }}
                      onMouseLeave={() => {
                        setTimeout(() => {
                          if (!document.querySelector('[data-popover="quality-control"]:hover')) {
                            setShowQualityControlPopover(false);
                          }
                        }, 100);
                      }}
                      className="flex flex-col items-center cursor-default group"
                      title="Kalite Kontrol Durumu (Otomatik güncelleniyor)"
                    >
                      {/* Icon Container */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        qualityControlStatus?.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/30 group-hover:bg-green-200 dark:group-hover:bg-green-800/40'
                          : qualityControlStatus?.status === 'validating'
                          ? 'bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/40'
                          : qualityControlStatus?.status === 'in_progress'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-800/40'
                          : qualityControlStatus?.status === 'failed'
                          ? 'bg-red-100 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-800/40'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-800/40'
                      }`}>
                        {isCheckingQualityControl ? (
                          <svg className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                          </svg>
                        ) : qualityControlStatus?.status === 'completed' ? (
                          <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        ) : qualityControlStatus?.status === 'validating' ? (
                          <div className="relative">
                            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <div className="absolute inset-0 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : qualityControlStatus?.status === 'in_progress' ? (
                          <div className="relative">
                            <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                          </div>
                        ) : qualityControlStatus?.status === 'failed' ? (
                          <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        ) : (
                          <div className="relative">
                            <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                      
                      {/* Label */}
                      <span className={`text-xs font-medium mt-1 transition-colors ${
                        qualityControlStatus?.status === 'completed'
                          ? 'text-green-600 dark:text-green-400'
                          : qualityControlStatus?.status === 'validating'
                          ? 'text-blue-600 dark:text-blue-400'
                          : qualityControlStatus?.status === 'in_progress'
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : qualityControlStatus?.status === 'failed'
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        Kalite Kontrol
                      </span>
                    </div>
                  </div>
                  
                  {/* Completion Rate Pie Chart */}
                  {completionData && (
                    <div 
                      ref={completionChartRef}
                      className="relative flex flex-col items-center cursor-default"
                      onMouseEnter={() => {
                        if (completionChartRef.current) {
                          const rect = completionChartRef.current.getBoundingClientRect();
                          setCompletionTooltipPosition({
                            top: rect.bottom + 8,
                            left: rect.left + (rect.width / 2)
                          });
                          setShowCompletionTooltip(true);
                        }
                      }}
                      onMouseLeave={() => {
                        setShowCompletionTooltip(false);
                      }}
                    >
                      {/* Pie Chart Container */}
                      <div className="w-12 h-12 relative">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                          {(() => {
                            const checkedCount = parseInt(completionData.checked_count) || 0;
                            const uncheckedCount = parseInt(completionData.unchecked_count) || 0;
                            const total = checkedCount + uncheckedCount;
                            const percentage = total > 0 ? (checkedCount / total) * 100 : 0;
                            const circumference = 2 * Math.PI * 15.91549430918954;
                            const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                            
                            return (
                              <>
                                {/* Background circle */}
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="15.91549430918954"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  className="text-gray-300 dark:text-gray-600"
                                />
                                {/* Progress circle */}
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="15.91549430918954"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  strokeDasharray={strokeDasharray}
                                  className={percentage >= 70 ? 'text-green-600 dark:text-green-400' : percentage >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}
                                />
                                {/* Center percentage text */}
                                <text
                                  x="18"
                                  y="18"
                                  textAnchor="middle"
                                  dy="0.3em"
                                  className="text-[8px] font-bold fill-gray-900 dark:fill-white"
                                  transform="rotate(90 18 18)"
                                >
                                  {Math.round(percentage)}%
                                </text>
                              </>
                            );
                          })()}
                        </svg>
                      </div>
                      
                      {/* Label */}
                      <span className="text-xs font-medium mt-1 text-gray-600 dark:text-gray-400 transition-colors whitespace-nowrap">
                        Tamamlanma
                      </span>
                    </div>
                  )}
                  
                  <div className="hidden md:block">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          {translatedDataError ? (
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-red-200/50 dark:border-red-700/50 p-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Hata Oluştu
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {translatedDataError}
                </p>
              </div>
            </div>
          ) : !translatedData || translatedData.length === 0 ? (
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                  <svg className="w-8 h-8 text-gray-600 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Çeviri Verisi Bulunamadı
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Bu döküman için henüz çeviri verisi bulunmuyor. Döküman çevrilmemiş olabilir.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-2 w-full flex flex-col">
              {/* Kalite Kontrol Filtreleri */}
              <div className="mb-3 rounded-lg border shadow-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600">
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                          </svg>
                        </div>
                        <h4 className="text-base font-semibold text-gray-900 dark:text-white">Kalite Kontrol Filtreleri</h4>
                        {(showCriticalErrorsOnly || showHumanReviewOnly || showChangedRowsOnly || showCommentsRowsOnly || violationSortOrder !== 'none' || qualityScoreSortOrder !== 'none' || selectedViolationTypes.size > 0) && (
                          <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-sm font-medium">
                            {[showCriticalErrorsOnly, showHumanReviewOnly, showChangedRowsOnly, showCommentsRowsOnly, violationSortOrder !== 'none', qualityScoreSortOrder !== 'none', selectedViolationTypes.size > 0].filter(Boolean).length} aktif
                          </span>
                        )}
                      </div>
                      
                      {/* Reset Button */}
                      {(showCriticalErrorsOnly || showHumanReviewOnly || showChangedRowsOnly || showCommentsRowsOnly || violationSortOrder !== 'none' || qualityScoreSortOrder !== 'none' || selectedViolationTypes.size > 0) && (
                        <button
                          onClick={() => {
                            setShowCriticalErrorsOnly(false);
                            setShowHumanReviewOnly(false);
                            setShowChangedRowsOnly(false);
                            setShowCommentsRowsOnly(false);
                            setSelectedUserFilter('');
                            setSelectedCommentUserFilter('');
                            setViolationSortOrder('none');
                            setQualityScoreSortOrder('none');
                            setSelectedViolationTypes(new Set());
                          }}
                          className="flex items-center space-x-2 px-3 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                          </svg>
                          <span>Sıfırla</span>
                        </button>
                      )}
                    </div>
                    
                    {/* Filter Grid - Üst Satır (4 filtre) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                      {/* Kritik Hatalar */}
                      <button
                        onClick={() => setShowCriticalErrorsOnly(!showCriticalErrorsOnly)}
                        className={`flex items-center space-x-3 p-2 rounded-lg border transition-all text-left h-[48px] ${
                          showCriticalErrorsOnly 
                            ? 'border-red-400 bg-red-900/40 dark:bg-red-900/40' 
                            : 'border-gray-300 dark:border-gray-500 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 bg-gray-50 dark:bg-gray-700/50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          showCriticalErrorsOnly 
                            ? 'border-red-500 bg-red-500' 
                            : 'border-gray-400'
                        }`}>
                          {showCriticalErrorsOnly && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">Kritik Hatalar</span>
                          </div>
                        </div>
                      </button>

                      {/* İnsan İncelemesi */}
                      <button
                        onClick={() => setShowHumanReviewOnly(!showHumanReviewOnly)}
                        className={`flex items-center space-x-3 p-2 rounded-lg border transition-all text-left h-[48px] ${
                          showHumanReviewOnly 
                            ? 'border-orange-400 bg-orange-900/40 dark:bg-orange-900/40' 
                            : 'border-gray-300 dark:border-gray-500 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 bg-gray-50 dark:bg-gray-700/50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          showHumanReviewOnly 
                            ? 'border-orange-500 bg-orange-500' 
                            : 'border-gray-400'
                        }`}>
                          {showHumanReviewOnly && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <svg className="w-3 h-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                            </svg>
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">İnsan İncelemesi</span>
                          </div>
                        </div>
                      </button>

                      {/* İhlal Sıralaması */}
                      <div className="flex items-center space-x-2 p-2 border border-gray-300 dark:border-gray-500 rounded-lg bg-gray-50 dark:bg-gray-700/50 h-[48px]">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">İhlale Göre</span>
                            <div className="flex space-x-1">
                            <button
                              onClick={() => {
                                const newOrder = violationSortOrder === 'asc' ? 'none' : 'asc';
                                setViolationSortOrder(newOrder);
                                if (newOrder !== 'none') {
                                  setQualityScoreSortOrder('none');
                                }
                              }}
                              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                                violationSortOrder === 'asc'
                                  ? 'bg-purple-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-purple-200 dark:hover:bg-purple-400'
                              }`}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => {
                                const newOrder = violationSortOrder === 'desc' ? 'none' : 'desc';
                                setViolationSortOrder(newOrder);
                                if (newOrder !== 'none') {
                                  setQualityScoreSortOrder('none');
                                }
                              }}
                              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                                violationSortOrder === 'desc'
                                  ? 'bg-purple-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-purple-200 dark:hover:bg-purple-400'
                              }`}
                            >
                              ↓
                            </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Kalite Sıralaması */}
                      <div className="flex items-center space-x-2 p-2 border border-gray-300 dark:border-gray-500 rounded-lg bg-gray-50 dark:bg-gray-700/50 h-[48px]">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">Puana Göre</span>
                            <div className="flex space-x-1">
                            <button
                              onClick={() => {
                                const newOrder = qualityScoreSortOrder === 'asc' ? 'none' : 'asc';
                                setQualityScoreSortOrder(newOrder);
                                if (newOrder !== 'none') {
                                  setViolationSortOrder('none');
                                }
                              }}
                              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                                qualityScoreSortOrder === 'asc'
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-green-200 dark:hover:bg-green-400'
                              }`}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => {
                                const newOrder = qualityScoreSortOrder === 'desc' ? 'none' : 'desc';
                                setQualityScoreSortOrder(newOrder);
                                if (newOrder !== 'none') {
                                  setViolationSortOrder('none');
                                }
                              }}
                              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                                qualityScoreSortOrder === 'desc'
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-green-200 dark:hover:bg-green-400'
                              }`}
                            >
                              ↓
                            </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Filter Grid - Alt Satır (3 filtre) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Yorum Olan Satırlar - Dropdown */}
                      <div ref={commentUserFilterDropdownRef} className="relative">
                        <button
                          onClick={() => setShowCommentUserFilterDropdown(!showCommentUserFilterDropdown)}
                          className={`flex items-center space-x-3 p-2 rounded-lg border transition-all text-left w-full h-[48px] ${
                            (showCommentsRowsOnly || selectedCommentUserFilter) 
                              ? 'border-amber-400 bg-amber-900/40 dark:bg-amber-900/40' 
                              : 'border-gray-300 dark:border-gray-500 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 bg-gray-50 dark:bg-gray-700/50'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            (showCommentsRowsOnly || selectedCommentUserFilter) 
                              ? 'border-amber-500 bg-amber-500' 
                              : 'border-gray-400'
                          }`}>
                            {(showCommentsRowsOnly || selectedCommentUserFilter) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                              </svg>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                                <svg className="w-3 h-3 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                                </svg>
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {selectedCommentUserFilter || 'Yorum Olan'}
                                </span>
                              </div>
                              <svg 
                                className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showCommentUserFilterDropdown ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                              </svg>
                            </div>
                          </div>
                        </button>

                        {/* Dropdown Menu */}
                        {showCommentUserFilterDropdown && (
                          <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {/* Tüm Yorum Olan Satırlar */}
                            <button
                              onClick={() => {
                                setShowCommentsRowsOnly(true);
                                setSelectedCommentUserFilter('');
                                setShowCommentUserFilterDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-3 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors flex items-center space-x-3 ${
                                showCommentsRowsOnly && !selectedCommentUserFilter ? 'bg-amber-100 dark:bg-amber-900/30' : ''
                              }`}
                            >
                              <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                              <span className="text-sm text-gray-900 dark:text-white font-medium">Tüm Yorum Olan</span>
                            </button>

                            {/* Divider */}
                            {uniqueCommentUsernames.length > 0 && (
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                            )}

                            {/* Kullanıcı Seçenekleri */}
                            {uniqueCommentUsernames.map((username) => (
                              <button
                                key={username}
                                onClick={() => {
                                  setShowCommentsRowsOnly(true);
                                  setSelectedCommentUserFilter(username);
                                  setShowCommentUserFilterDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center space-x-3 ${
                                  selectedCommentUserFilter === username ? 'bg-blue-100 dark:bg-blue-900/30' : ''
                                }`}
                              >
                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                </svg>
                                <span className="text-sm text-gray-900 dark:text-white">{username}</span>
                              </button>
                            ))}

                            {/* Divider */}
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>

                            {/* Filtreyi Kaldır */}
                            <button
                              onClick={() => {
                                setShowCommentsRowsOnly(false);
                                setSelectedCommentUserFilter('');
                                setShowCommentUserFilterDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-3"
                            >
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                              </svg>
                              <span className="text-sm text-gray-600 dark:text-gray-400">Filtreyi Kaldır</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* İhlal Türlerine Göre Filtre - Dropdown */}
                      <div ref={violationFilterDropdownRef} className="relative">
                        <button
                          onClick={() => setShowViolationFilterDropdown(!showViolationFilterDropdown)}
                          className={`flex items-center space-x-3 p-2 rounded-lg border transition-all text-left w-full h-[48px] ${
                            selectedViolationTypes.size > 0
                            ? 'border-orange-400 bg-orange-900/40 dark:bg-orange-900/40' 
                            : 'border-gray-300 dark:border-gray-500 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 bg-gray-50 dark:bg-gray-700/50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            selectedViolationTypes.size > 0
                            ? 'border-orange-500 bg-orange-500' 
                            : 'border-gray-400'
                        }`}>
                            {selectedViolationTypes.size > 0 && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <svg className="w-3 h-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                            </svg>
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {selectedViolationTypes.size > 0 ? `İhlal Türleri (${selectedViolationTypes.size})` : 'İhlal Türleri'}
                                </span>
                              </div>
                              <svg 
                                className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showViolationFilterDropdown ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                              </svg>
                          </div>
                        </div>
                      </button>

                        {/* Dropdown Menu */}
                        {showViolationFilterDropdown && (
                          <div className="absolute z-[9999] mt-2 w-[600px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col">
                            {availableViolationTypes.length === 0 ? (
                              <div className="px-4 py-8 text-center">
                                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                                </svg>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Henüz ihlal verisi bulunmuyor</p>
                              </div>
                            ) : (
                              <>
                                {/* Header with Actions */}
                                <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                                    </svg>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                      İhlal Türleri
                                    </h3>
                                    {selectedViolationTypes.size > 0 && (
                                      <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">
                                        ({selectedViolationTypes.size} seçili)
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => setSelectedViolationTypes(new Set(availableViolationTypes))}
                                      className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                                    >
                                      Tümünü Seç
                                    </button>
                                    <button
                                      onClick={() => setSelectedViolationTypes(new Set())}
                                      className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    >
                                      Temizle
                                    </button>
                                  </div>
                                </div>

                                {/* Violation Types List - Scrollable */}
                                <div className="overflow-y-auto flex-1 p-2">
                                  {availableViolationTypes.map((violationCode) => {
                                    const isSelected = selectedViolationTypes.has(violationCode);
                                    const translateRuleCode = (ruleCode: string): string => {
                                      const ruleTranslations: { [key: string]: string } = {
                                        'MIS_MEAN': 'Anlam Sapması', 'MIS_INFO': 'Bilgi Ek/Çıkartma', 'STR_ADD': 'Yapısal Ekleme',
                                        'STR_DEL': 'Yapısal Silme', 'PARA_MERGE': 'Paragraf Birleştirme', 'PARA_SPLIT': 'Paragraf Bölme',
                                        'LOGIC_INVERT': 'Mantıksal Tersine Çeviri', 'CONTEXT_MISUSE': 'Bağlam Hatası',
                                        'TER_GLOSS': 'Glossary\'ye Aykırı', 'TERM_ACCURACY': 'Terminoloji Doğruluğu', 'TER_IFRS': 'IFRS Terim Hatası',
                                        'TER_GRI': 'GRI Terim Hatası', 'TER_KAP': 'KAP Terim Hatası', 'TER_STYLE': 'Kurumsal Üslup Terimi',
                                        'TER_UNIT': 'Birim Terimi Hatası', 'TER_SHORT': 'Kısaltma Hatası', 'TER_NAME': 'Özel İsim Hatası',
                                        'TERM_NAME': 'Özel İsim Hatası', 'TER_DUP': 'Terim Tutarsızlığı', 'FMT_NUM': 'Sayı Biçimi', 'FMT_CUR': 'Para Birimi Biçimi',
                                        'FMT_PCT': 'Yüzde Biçimi', 'FMT_DATE': 'Tarih Biçimi', 'FMT_SPACE': 'Boşluk Hatası',
                                        'FMT_DASH': 'Tire/En Dash Hatası', 'FMT_PUNC': 'Noktalama Hatası', 'FMT_CAPS': 'Büyük/Küçük Harf',
                                        'FMT_QUOTE': 'Tırnak Biçimi', 'FMT_UNIT': 'Birim Biçimi', 'FMT_TABLE': 'Tablo Biçimi',
                                        'FMT_LIST': 'Liste Biçimi', 'ESG_SCOPE': 'ESG Kapsam Hatası', 'ESG_METRIC': 'ESG Metrik Hatası',
                                        'ESG_TERM': 'ESG Terim Hatası', 'ESG_FRAMEWORK': 'ESG Çerçeve Hatası', 'PROHIBITED': 'Yasaklı İfade',
                                        'LEGAL_MISUSE': 'Hukuki Atıf Hatası', 'IFRS_MISUSE': 'IFRS Yanlış Kullanımı', 'GRI_MISUSE': 'GRI Yanlış Kullanımı',
                                        'KAP_MISUSE': 'KAP Yanlış Kullanımı', 'DATA_ALTER': 'Veri Değiştirme', 'NAME_CHANGE': 'Yasal İsim Değişimi',
                                        'LANG_VARIANT': 'Dil Varyantı Hatası', 'SPELLING': 'Yazım Hatası', 'GRAMMAR': 'Dilbilgisi Hatası',
                                        'WORD_ORDER': 'Sözcük Sırası', 'REGISTER': 'Üslup Uygunsuzluğu', 'CONSISTENCY': 'Tutarlılık Sorunu',
                                        'REDUNDANCY': 'Gereksiz Tekrar', 'OMISSION': 'Kelime Atlama', 'INSERTION': 'Gereksiz Ekleme',
                                        'PUNCT': 'Noktalama', 'SPACING': 'Boşluk', 'CAPS': 'Büyük/Küçük Harf',
                                        'STRUCT_ORDER': 'Sıra/Dizilim Hatası', 'STRUCT_LOSS': 'Yapısal Kayıp', 'STRUCT_EXTRA': 'Fazladan Yapı',
                                        'STRUCT_MERGE': 'Yapısal Birleştirme', 'META_TAG': 'Meta Etiket Hatası', 'LINK_BROKEN': 'Kırık Bağlantı',
                                        'REF_ERROR': 'Atıf/Referans Hatası', 'VERSION_MISMATCH': 'Sürüm Uyuşmazlığı', 'PLACEHOLDER': 'Yer Tutucu Kaldı',
                                        'COMMENT_LEAK': 'Yorum/Sistem Notu Sızıntısı'
                                      };
                                      return ruleTranslations[ruleCode] || ruleCode;
                                    };
                                    
                                    return (
                                      <button
                                        key={violationCode}
                                        onClick={() => {
                                          const newSelected = new Set(selectedViolationTypes);
                                          if (isSelected) {
                                            newSelected.delete(violationCode);
                                          } else {
                                            newSelected.add(violationCode);
                                          }
                                          setSelectedViolationTypes(newSelected);
                                        }}
                                        className={`w-full text-left px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors flex items-center justify-between rounded ${
                                          isSelected ? 'bg-orange-100 dark:bg-orange-900/30' : ''
                                        }`}
                                      >
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                            isSelected
                                              ? 'border-orange-500 bg-orange-500'
                                              : 'border-gray-300 dark:border-gray-600'
                                          }`}>
                                            {isSelected && (
                                              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                              </svg>
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                              {translateRuleCode(violationCode)}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                              {violationCode}
                                            </div>
                                          </div>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0 ml-2 ${
                                          isSelected
                                            ? 'bg-orange-500 text-white'
                                            : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                        }`}>
                                          {violationTypeCounts.get(violationCode) || 0}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* Divider */}
                                <div className="border-t border-gray-200 dark:border-gray-700"></div>

                                {/* Filtreyi Kaldır */}
                                <button
                                  onClick={() => {
                                    setSelectedViolationTypes(new Set());
                                    setShowViolationFilterDropdown(false);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-3"
                                >
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                  </svg>
                                  <span className="text-sm text-gray-600 dark:text-gray-400">Filtreyi Kaldır</span>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Değişiklik Olan Satırlar - Dropdown */}
                      <div ref={userFilterDropdownRef} className="relative">
                        <button
                          onClick={() => setShowUserFilterDropdown(!showUserFilterDropdown)}
                          className={`flex items-center space-x-3 p-2 rounded-lg border transition-all text-left w-full h-[48px] ${
                            (showChangedRowsOnly || selectedUserFilter) 
                            ? 'border-purple-400 bg-purple-900/40 dark:bg-purple-900/40' 
                            : 'border-gray-300 dark:border-gray-500 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 bg-gray-50 dark:bg-gray-700/50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            (showChangedRowsOnly || selectedUserFilter) 
                            ? 'border-purple-500 bg-purple-500' 
                            : 'border-gray-400'
                        }`}>
                            {(showChangedRowsOnly || selectedUserFilter) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <svg className="w-3 h-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {selectedUserFilter || 'Değişiklik Olan'}
                                </span>
                              </div>
                              <svg 
                                className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showUserFilterDropdown ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                              </svg>
                          </div>
                        </div>
                      </button>

                        {/* Dropdown Menu */}
                        {showUserFilterDropdown && (
                          <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {/* Tüm Değişiklik Olan Satırlar */}
                            <button
                              onClick={() => {
                                setShowChangedRowsOnly(true);
                                setSelectedUserFilter('');
                                setShowUserFilterDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center space-x-3 ${
                                showChangedRowsOnly && !selectedUserFilter ? 'bg-purple-100 dark:bg-purple-900/30' : ''
                              }`}
                            >
                              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                              <span className="text-sm text-gray-900 dark:text-white font-medium">Tüm Değişiklik Olan</span>
                            </button>

                            {/* Divider */}
                            {uniqueUsernames.length > 0 && (
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                            )}

                            {/* Kullanıcı Seçenekleri */}
                            {uniqueUsernames.map((username) => (
                              <button
                                key={username}
                                onClick={() => {
                                  setShowChangedRowsOnly(true);
                                  setSelectedUserFilter(username);
                                  setShowUserFilterDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center space-x-3 ${
                                  selectedUserFilter === username ? 'bg-blue-100 dark:bg-blue-900/30' : ''
                                }`}
                              >
                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                          </svg>
                                <span className="text-sm text-gray-900 dark:text-white">{username}</span>
                              </button>
                            ))}

                            {/* Divider */}
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>

                            {/* Filtreyi Kaldır */}
                            <button
                              onClick={() => {
                                setShowChangedRowsOnly(false);
                                setSelectedUserFilter('');
                                setShowUserFilterDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-3"
                            >
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                              </svg>
                              <span className="text-sm text-gray-600 dark:text-gray-400">Filtreyi Kaldır</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              {/* Arama Filtreleri */}
              <div className="flex gap-4 mb-6 w-full">
                <div className="flex-1 min-w-0 relative">
                  <input
                    type="text"
                    placeholder={`${selectedDocumentSourceLanguage ? getLanguageName(selectedDocumentSourceLanguage) : 'Türkçe'} metinde ara...`}
                    value={turkishSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTurkishSearch(value);
                      
                      // Clear English search when Turkish search is used
                      if (value && englishSearch) {
                        setEnglishSearch('');
                      }
                      
                      // Clear previous timeout
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }
                      
                      // If empty, clear results immediately
                      if (!value.trim()) {
                        setSearchResults([]);
                        setCurrentSearchIndex(0);
                        setSearchType(null);
                        return;
                      }
                      
                      // Debounce: Wait 350ms after user stops typing
                      searchTimeoutRef.current = setTimeout(() => {
                        // Save current position before starting search
                        savePreSearchPosition();
                        calculateSearchResults(translatedData, value, 'turkish');
                      }, 350);
                    }}
                    className="w-full px-3 py-2 pr-20 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {/* Search Options - Always visible */}
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {/* Case Sensitive Button */}
                    <button
                      onClick={() => setIsTurkishCaseSensitive(!isTurkishCaseSensitive)}
                      className={`p-1.5 rounded-md transition-all text-xs font-bold ${
                        isTurkishCaseSensitive 
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 ring-1 ring-blue-300 dark:ring-blue-600' 
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-400 dark:hover:bg-gray-500'
                      }`}
                      title="Büyük/Küçük Harf Duyarlı"
                    >
                      Aa
                    </button>
                    {/* Whole Word Button */}
                    <button
                      onClick={() => setIsTurkishWholeWord(!isTurkishWholeWord)}
                      className={`p-1.5 rounded-md transition-all ${
                        isTurkishWholeWord 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 ring-1 ring-green-300 dark:ring-green-600' 
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-400 dark:hover:bg-gray-500'
                      }`}
                      title="Tam Sözcük Eşleşmesi"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-w-0 relative">
                  <div className="relative">
                  <input
                    type="text"
                    placeholder={`${selectedDocumentDestinationLanguage ? getLanguageName(selectedDocumentDestinationLanguage) : 'İngilizce'} metinde ara...`}
                    value={englishSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEnglishSearch(value);
                      
                      // Clear Turkish search when English search is used
                      if (value && turkishSearch) {
                        setTurkishSearch('');
                      }
                      
                      // Clear previous timeout
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }
                      
                      // If empty, clear results immediately
                      if (!value.trim()) {
                        setSearchResults([]);
                        setCurrentSearchIndex(0);
                        setSearchType(null);
                        setReplacePreview([]);
                        return;
                      }
                      
                      // Debounce: Wait 350ms after user stops typing
                      searchTimeoutRef.current = setTimeout(() => {
                        // Save current position before starting search
                        savePreSearchPosition();
                        calculateSearchResults(translatedData, value, 'english');
                        // Generate preview if replace is active
                        if (showReplaceInput && replaceText) {
                          generateReplacePreview(value, replaceText);
                        }
                      }, 350);
                    }}
                      className="w-full px-3 py-2 pr-32 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {/* Search Options and Replace button - Always visible */}
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      {/* Case Sensitive Button */}
                      <button
                        onClick={() => setIsEnglishCaseSensitive(!isEnglishCaseSensitive)}
                        className={`p-1.5 rounded-md transition-all text-xs font-bold ${
                          isEnglishCaseSensitive 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 ring-1 ring-blue-300 dark:ring-blue-600' 
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-400 dark:hover:bg-gray-500'
                        }`}
                        title="Büyük/Küçük Harf Duyarlı"
                      >
                        Aa
                      </button>
                      {/* Whole Word Button */}
                      <button
                        onClick={() => setIsEnglishWholeWord(!isEnglishWholeWord)}
                        className={`p-1.5 rounded-md transition-all ${
                          isEnglishWholeWord 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 ring-1 ring-green-300 dark:ring-green-600' 
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-400 dark:hover:bg-gray-500'
                        }`}
                        title="Tam Sözcük Eşleşmesi"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                      </button>
                      {/* Replace toggle button */}
                      <button
                        onClick={() => {
                          setShowReplaceInput(!showReplaceInput);
                          if (!showReplaceInput && replaceText && englishSearch) {
                            generateReplacePreview(englishSearch, replaceText);
                          } else {
                            setReplacePreview([]);
                          }
                        }}
                        className={`p-1.5 rounded-md transition-all ${
                          showReplaceInput 
                            ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 ring-1 ring-orange-300 dark:ring-orange-600' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                        }`}
                        title="Bul ve Değiştir"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Replace input - Overlay */}
                  {showReplaceInput && englishSearch && (
                    <div className="absolute left-0 right-0 top-full mt-1 p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg shadow-xl space-y-3 z-50 backdrop-blur-sm">
                      <div className="flex items-start gap-2 w-full">
                        <input
                          type="text"
                          placeholder="Değiştirilecek metin..."
                          value={replaceText}
                          onChange={(e) => {
                            const value = e.target.value;
                            setReplaceText(value);
                            if (value && englishSearch) {
                              generateReplacePreview(englishSearch, value);
                            } else {
                              setReplacePreview([]);
                            }
                          }}
                          className="flex-1 min-w-0 px-3 py-2 text-sm border border-orange-300 dark:border-orange-700 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                        <button
                          onClick={handleFindAndReplace}
                          disabled={!replaceText || isFindReplaceProcessing || replacePreview.length === 0}
                          className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"
                        >
                          {isFindReplaceProcessing ? (
                            <>
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="hidden sm:inline">İşleniyor</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                              </svg>
                              <span>Tümünü Değiştir</span>
                              <span className="bg-orange-500 px-1.5 py-0.5 rounded text-xs">{replacePreview.length}</span>
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Preview */}
                      {replacePreview.length > 0 && (
                        <div className="max-h-96 overflow-y-auto space-y-2 bg-white dark:bg-gray-800 rounded-md p-3 border border-orange-200 dark:border-orange-800 w-full">
                          {replacePreview.map((item, idx) => {
                            // Split old text and highlight matches
                            const renderOldText = () => {
                              const parts: React.ReactElement[] = [];
                              let lastIndex = 0;
                              
                              let searchRegex: RegExp;
                              const searchPattern = buildTurkishAwareSearchPattern(englishSearch, isEnglishCaseSensitive);
                              if (isEnglishWholeWord) {
                                searchRegex = new RegExp(`(^|\\s|[.,;:!?()\\[\\]{}'"«»""])(${searchPattern})($|\\s|[.,;:!?()\\[\\]{}'"«»""])`, 'gu');
                              } else {
                                searchRegex = new RegExp(searchPattern, 'gu');
                              }
                              
                              let match;
                              while ((match = searchRegex.exec(item.oldText)) !== null) {
                                // For whole word mode, match[0] includes boundaries, match[2] is the actual word
                                const matchText = isEnglishWholeWord ? match[2] : match[0];
                                const matchStart = isEnglishWholeWord ? match.index + match[1].length : match.index;
                                
                                if (matchStart > lastIndex) {
                                  parts.push(
                                    <span key={`before-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                                      {item.oldText.substring(lastIndex, matchStart)}
                                    </span>
                                  );
                                }
                                parts.push(
                                  <span key={`match-${matchStart}`} className="text-red-600 dark:text-red-400 line-through bg-red-100 dark:bg-red-900/30 px-0.5 rounded">
                                    {matchText}
                                  </span>
                                );
                                lastIndex = matchStart + matchText.length;
                              }
                              if (lastIndex < item.oldText.length) {
                                parts.push(
                                  <span key={`after-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                                    {item.oldText.substring(lastIndex)}
                                  </span>
                                );
                              }
                              return parts;
                            };
                            
                            // Split new text and highlight replacements
                            const renderNewText = () => {
                              const parts: React.ReactElement[] = [];
                              let lastIndex = 0;
                              
                              // Find replaced text by looking for replaceText in newText
                              const replaceRegex = new RegExp(replaceText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                              let match;
                              
                              while ((match = replaceRegex.exec(item.newText)) !== null) {
                                // Add text before replacement
                                if (match.index > lastIndex) {
                                  parts.push(
                                    <span key={`before-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                                      {item.newText.substring(lastIndex, match.index)}
                                    </span>
                                  );
                                }
                                // Add replacement (highlighted)
                                parts.push(
                                  <span key={`replace-${match.index}`} className="text-green-600 dark:text-green-400 font-semibold bg-green-100 dark:bg-green-900/30 px-0.5 rounded">
                                    {match[0]}
                                  </span>
                                );
                                lastIndex = match.index + match[0].length;
                              }
                              
                              // Add remaining text
                              if (lastIndex < item.newText.length) {
                                parts.push(
                                  <span key={`after-${lastIndex}`} className="text-gray-700 dark:text-gray-300">
                                    {item.newText.substring(lastIndex)}
                                  </span>
                                );
                              }
                              
                              return parts.length > 0 ? parts : <span className="text-gray-700 dark:text-gray-300">{item.newText}</span>;
                            };
                            
                            return (
                              <div key={idx} className="text-xs p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                <div className="flex items-start gap-2 mb-1">
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span className="inline-flex items-center justify-center min-w-[2.5rem] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-mono text-[10px] font-semibold">
                                      #{item.rowNumber}
                                    </span>
                                    <svg className="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0 space-y-1">
                                    <div className="flex items-start gap-1">
                                      <span className="text-red-500 dark:text-red-400 text-[10px] font-semibold mt-0.5">ESKİ:</span>
                                      <div className="break-words flex-1">{renderOldText()}</div>
                                    </div>
                                    <div className="flex items-start gap-1">
                                      <span className="text-green-600 dark:text-green-400 text-[10px] font-semibold mt-0.5">YENİ:</span>
                                      <div className="break-words flex-1">{renderNewText()}</div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleSingleReplace(item.sid, item.oldText, item.newText)}
                                    className="flex-shrink-0 px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-semibold rounded transition-all flex items-center gap-1 self-center"
                                    title="Bu satırı değiştir"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                    </svg>
                                    Değiştir
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {(turkishSearch || englishSearch) && (
                  <div className="flex items-center gap-2">
                    {/* Search Results Counter */}
                    {searchResults.length > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-md">
                        <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                          {currentSearchIndex + 1} / {searchResults.length}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={goToPreviousSearchResult}
                            disabled={searchResults.length <= 1}
                            className={`p-1 rounded transition-colors ${
                              searchResults.length <= 1
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200'
                            }`}
                            title="Önceki sonuç"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                            </svg>
                          </button>
                          <button
                            onClick={goToNextSearchResult}
                            disabled={searchResults.length <= 1}
                            className={`p-1 rounded transition-colors ${
                              searchResults.length <= 1
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200'
                            }`}
                            title="Sonraki sonuç"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                    
                  <button
                    onClick={() => {
                      setTurkishSearch('');
                      setEnglishSearch('');
                        setSearchResults([]);
                        setCurrentSearchIndex(0);
                        setSearchType(null);
                        setHighlightedRowSid(null);
                        
                        // Restore pre-search position
                        if (preSearchPosition) {
                          // Restore page
                          setTranslatedCurrentPage(preSearchPosition.page);
                          
                          // Restore scroll position after a short delay to ensure page change
                          setTimeout(() => {
                            const tableContainer = 
                              document.querySelector('[data-testid="translation-table-container"]') ||
                              document.querySelector('[data-testid="translation-table-scroll-container"]') ||
                              document.querySelector('[data-testid="translation-table-main-container"]') ||
                              document.querySelector('.overflow-auto.flex-1') ||
                              document.querySelector('.overflow-y-scroll') ||
                              document.querySelector('.overflow-y-auto');
                            
                            if (tableContainer) {
                              tableContainer.scrollTop = preSearchPosition.scrollTop;
                            }
                          }, 100);
                          
                          // Clear saved position
                          setPreSearchPosition(null);
                        }
                    }}
                    className="px-3 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                    title="Aramaları temizle"
                  >
                    Temizle
                  </button>
                  </div>
                )}
              </div>

              {/* Tablo İstatistikleri ve Kontrol Paneli */}
              <div className="space-y-2 mb-2">
                {/* Tek Satır: İstatistikler, Sayfalama ve Aksiyon Butonları */}
                <div className="flex justify-between items-center">
                  {/* Sol: İstatistikler ve Sayfalama */}
                <div className="flex items-center gap-4">
                    {/* Toplam Kayıt */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-lg flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                      </div>
                      <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center">Toplam: {totalTranslatedItems}</span>
                    </div>

                  {sortField && (
                    <div className="flex flex-col items-center gap-1">
                      <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/>
                        </svg>
                      </div>
                      <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center">
                        {sortDirection === 'desc' ? 'Yüksek→Düşük' : 'Düşük→Yüksek'}
                    </span>
                    </div>
                  )}

                    {/* Sayfa Başına - Pie Chart */}
                    <div 
                      className="relative flex flex-col items-center gap-1"
                      onMouseEnter={() => setShowPageSizePieMenu(true)}
                      onMouseLeave={() => setShowPageSizePieMenu(false)}
                    >
                      <button
                        className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 bg-gradient-to-br from-purple-500 to-purple-700"
                        title="Sayfa başına gösterilecek kayıt sayısı"
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">{translatedItemsPerPage}</span>
                        </div>
                      </button>
                      <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center">Sayfa başına</span>

                      {/* Pie Chart Menu */}
                      {showPageSizePieMenu && (
                        <div className="absolute left-0 top-0 z-50" style={{ marginLeft: '-70px', marginTop: '-70px' }}>
                          <svg width="180" height="180" viewBox="0 0 180 180" className="pointer-events-auto drop-shadow-2xl">
                            {/* Pie Chart 4 Dilim (90 derece her biri) */}
                            
                            {/* Dilim 1: 25 (Sol) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                const newValue = 25;
                                setTranslatedItemsPerPage(newValue);
                                const newTotalPages = Math.ceil(totalTranslatedItems / newValue);
                                if (translatedCurrentPage > newTotalPages) {
                                  setTranslatedCurrentPage(Math.max(1, newTotalPages));
                                } else {
                                  setTranslatedCurrentPage(1);
                                }
                              }}
                              className="cursor-pointer transition-all duration-300 hover:opacity-90"
                              style={{ transformOrigin: '90px 90px' }}
                            >
                              <path
                                d="M 90 90 L 18 90 A 72 72 0 0 1 90 18 Z"
                                fill={translatedItemsPerPage === 25 ? "url(#gradient-purple-active)" : "url(#gradient-purple)"}
                                stroke="white"
                                strokeWidth="2"
                                className="transition-all duration-300 hover:scale-105"
                                style={{ transformOrigin: '90px 90px' }}
                              />
                              <text x="55" y="62" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                25
                              </text>
                            </g>

                            {/* Dilim 2: 50 (Üst) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                const newValue = 50;
                                setTranslatedItemsPerPage(newValue);
                                const newTotalPages = Math.ceil(totalTranslatedItems / newValue);
                                if (translatedCurrentPage > newTotalPages) {
                                  setTranslatedCurrentPage(Math.max(1, newTotalPages));
                                } else {
                                  setTranslatedCurrentPage(1);
                                }
                              }}
                              className="cursor-pointer transition-all duration-300 hover:opacity-90"
                              style={{ transformOrigin: '90px 90px' }}
                            >
                              <path
                                d="M 90 90 L 90 18 A 72 72 0 0 1 162 90 Z"
                                fill={translatedItemsPerPage === 50 ? "url(#gradient-purple-active)" : "url(#gradient-purple)"}
                                stroke="white"
                                strokeWidth="2"
                                className="transition-all duration-300 hover:scale-105"
                                style={{ transformOrigin: '90px 90px' }}
                              />
                              <text x="125" y="62" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                50
                              </text>
                            </g>

                            {/* Dilim 3: 100 (Sağ) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                const newValue = 100;
                                setTranslatedItemsPerPage(newValue);
                                const newTotalPages = Math.ceil(totalTranslatedItems / newValue);
                                if (translatedCurrentPage > newTotalPages) {
                                  setTranslatedCurrentPage(Math.max(1, newTotalPages));
                                } else {
                                  setTranslatedCurrentPage(1);
                                }
                              }}
                              className="cursor-pointer transition-all duration-300 hover:opacity-90"
                              style={{ transformOrigin: '90px 90px' }}
                            >
                              <path
                                d="M 90 90 L 162 90 A 72 72 0 0 1 90 162 Z"
                                fill={translatedItemsPerPage === 100 ? "url(#gradient-purple-active)" : "url(#gradient-purple)"}
                                stroke="white"
                                strokeWidth="2"
                                className="transition-all duration-300 hover:scale-105"
                                style={{ transformOrigin: '90px 90px' }}
                              />
                              <text x="125" y="118" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                100
                              </text>
                            </g>

                            {/* Dilim 4: 200 (Alt) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                const newValue = 200;
                                setTranslatedItemsPerPage(newValue);
                                const newTotalPages = Math.ceil(totalTranslatedItems / newValue);
                                if (translatedCurrentPage > newTotalPages) {
                                  setTranslatedCurrentPage(Math.max(1, newTotalPages));
                                } else {
                                  setTranslatedCurrentPage(1);
                                }
                              }}
                              className="cursor-pointer transition-all duration-300 hover:opacity-90"
                              style={{ transformOrigin: '90px 90px' }}
                            >
                              <path
                                d="M 90 90 L 90 162 A 72 72 0 0 1 18 90 Z"
                                fill={translatedItemsPerPage === 200 ? "url(#gradient-purple-active)" : "url(#gradient-purple)"}
                                stroke="white"
                                strokeWidth="2"
                                className="transition-all duration-300 hover:scale-105"
                                style={{ transformOrigin: '90px 90px' }}
                              />
                              <text x="55" y="118" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                200
                              </text>
                            </g>

                            {/* Merkez Daire */}
                            <circle 
                              cx="90" 
                              cy="90" 
                              r="18" 
                              fill="url(#gradient-center-purple-page)"
                              className="pointer-events-none"
                            />
                            <text x="90" y="96" fontSize="14" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                              {translatedItemsPerPage}
                            </text>

                            {/* Gradient Tanımlamaları */}
                            <defs>
                              <linearGradient id="gradient-purple" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#7e22ce', stopOpacity: 1 }} />
                              </linearGradient>
                              <linearGradient id="gradient-purple-active" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: '#c084fc', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#9333ea', stopOpacity: 1 }} />
                              </linearGradient>
                              <linearGradient id="gradient-center-purple-page" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#7e22ce', stopOpacity: 1 }} />
                              </linearGradient>
                            </defs>
                        </svg>
                        </div>
                      )}
                    </div>
                      
                      {/* Satıra Git */}
                    <div className="flex flex-col items-center gap-1">
                    <input
                      type="text"
                      value={goToRowValue}
                      onChange={handleTranslatedGoToRowChange}
                        placeholder="→"
                        className="w-14 h-14 text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-lg"
                        title="Satır numarasını yazın (otomatik olarak gider)"
                      />
                      <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center">Satıra Git</span>
                    </div>
                  </div>

                  {/* Sağ: Aksiyon Butonları */}
                  <div className="flex items-center gap-4">
                    {/* Column Visibility Toggles */}
                    <div className="flex flex-col items-center gap-1 w-16">
                      <button
                        onClick={() => setShowControlColumn(!showControlColumn)}
                        className={`relative w-12 h-12 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg ${
                          showControlColumn
                            ? 'bg-gradient-to-br from-blue-500 to-blue-700'
                            : 'bg-gradient-to-br from-gray-400 to-gray-600'
                        }`}
                        title={showControlColumn ? 'Kontrol sütununu gizle' : 'Kontrol sütununu göster'}
                      >
                        <svg className="w-6 h-6 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </button>
                      <span className="text-[9px] font-medium text-gray-600 dark:text-gray-400 text-center">Kontrol</span>
                    </div>

                    <div className="flex flex-col items-center gap-1 w-16">
                      <button
                        onClick={() => setShowGlossaryColumn(!showGlossaryColumn)}
                        className={`relative w-12 h-12 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg ${
                          showGlossaryColumn
                            ? 'bg-gradient-to-br from-purple-500 to-purple-700'
                            : 'bg-gradient-to-br from-gray-400 to-gray-600'
                        }`}
                        title={showGlossaryColumn ? 'Glossary sütununu gizle' : 'Glossary sütununu göster'}
                      >
                        <svg className="w-6 h-6 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                        </svg>
                      </button>
                      <span className="text-[9px] font-medium text-gray-600 dark:text-gray-400 text-center">Glossary</span>
                    </div>

                    <div className="flex flex-col items-center gap-1 w-16">
                      <button
                        onClick={() => setShowQualityColumn(!showQualityColumn)}
                        className={`relative w-12 h-12 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg ${
                          showQualityColumn
                            ? 'bg-gradient-to-br from-green-500 to-green-700'
                            : 'bg-gradient-to-br from-gray-400 to-gray-600'
                        }`}
                        title={showQualityColumn ? 'Kalite kontrol sütununu gizle' : 'Kalite kontrol sütununu göster'}
                      >
                        <svg className="w-6 h-6 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                        </svg>
                      </button>
                      <span className="text-[9px] font-medium text-gray-600 dark:text-gray-400 text-center">Kalite</span>
                    </div>

                    <div className="flex flex-col items-center gap-1 w-16">
                      <button
                        onClick={() => setShowCommentsColumn(!showCommentsColumn)}
                        className={`relative w-12 h-12 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg ${
                          showCommentsColumn
                            ? 'bg-gradient-to-br from-amber-500 to-amber-700'
                            : 'bg-gradient-to-br from-gray-400 to-gray-600'
                        }`}
                        title={showCommentsColumn ? 'Yorumlar sütununu gizle' : 'Yorumlar sütununu göster'}
                      >
                        <svg className="w-6 h-6 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                        </svg>
                      </button>
                      <span className="text-[9px] font-medium text-gray-600 dark:text-gray-400 text-center">Yorumlar</span>
                    </div>

                    {/* İlk İki Buton Container */}
                    <div className="flex items-center gap-2">
                      {!isEnSelectionMode ? (
                        <>
                          {/* Bütün Yorumları Sil Butonu */}
                          <div className="flex flex-col items-center gap-1 w-20">
                            <button
                              onClick={() => setShowDeleteAllCommentsModal(true)}
                              className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 bg-gradient-to-br from-orange-500 to-orange-700"
                              title="Dokümandaki bütün yorumları sil"
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                                </svg>
                                <div className="absolute bottom-1 right-1">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"/>
                                  </svg>
                                </div>
                              </div>
                            </button>
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                              Bütün<br/>Yorumları Sil
                            </span>
                          </div>

                          {/* Seçim Modu Butonu */}
                          <div className="flex flex-col items-center gap-1 w-20">
                    <button
                              onClick={() => setIsEnSelectionMode(true)}
                              className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 bg-gradient-to-br from-blue-500 to-blue-700"
                              title="Seçili satırları silmek için seçim modunu başlat"
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                              </div>
                    </button>
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                              Seçili<br/>Değişiklikleri Sil
                            </span>
                          </div>

                          {/* Tüm Değişiklikleri Sil Butonu */}
                          <div className="flex flex-col items-center gap-1 w-20">
                            <button
                              onClick={() => setShowDeleteAllEnModal(true)}
                              className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 bg-gradient-to-br from-red-500 to-red-700"
                              title="Bu dokümandaki tüm İngilizce değişiklikleri sil"
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                              </div>
                            </button>
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                              Tüm<br/>Değişiklikleri Sil
                            </span>
                  </div>
                  </>
                ) : (
                    <>
                          {/* Bütün Yorumları Sil Butonu */}
                          <div className="flex flex-col items-center gap-1 w-20">
                            <button
                              onClick={() => setShowDeleteAllCommentsModal(true)}
                              className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 bg-gradient-to-br from-orange-500 to-orange-700"
                              title="Dokümandaki bütün yorumları sil"
                            >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                                </svg>
                                <div className="absolute bottom-1 right-1">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"/>
                                  </svg>
                                </div>
                              </div>
                            </button>
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                              Bütün<br/>Yorumları Sil
                            </span>
                          </div>

                          {/* Seçili Değişiklikleri Sil Butonu (Aktif) */}
                          <div className="flex flex-col items-center gap-1 w-20">
                      <button
                        onClick={() => {
                          if (selectedEnRows.size > 0) {
                            setShowBulkDeleteEnModal(true);
                          }
                        }}
                        disabled={selectedEnRows.size === 0}
                              className={`relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 ${
                          selectedEnRows.size > 0
                                  ? 'bg-gradient-to-br from-red-500 to-red-700'
                                  : 'bg-gray-400 cursor-not-allowed opacity-50'
                        }`}
                        title="Seçili satırların İngilizce değişikliklerini sil"
                      >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                              </div>
                              {selectedEnRows.size > 0 && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-white text-red-700 rounded-full text-[10px] font-bold flex items-center justify-center border-2 border-red-700">
                                  {selectedEnRows.size}
                                </div>
                              )}
                      </button>
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                              Seçili<br/>Değişiklikleri Sil
                              {selectedEnRows.size > 0 && ` (${selectedEnRows.size})`}
                            </span>
                          </div>

                          {/* İptal Butonu */}
                          <div className="flex flex-col items-center gap-1 w-20">
                      <button
                        onClick={() => {
                          setIsEnSelectionMode(false);
                          setSelectedEnRows(new Set());
                        }}
                              className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 bg-gradient-to-br from-gray-500 to-gray-700"
                        title="Seçim modundan çık"
                      >
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                              </div>
                      </button>
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">İptal</span>
                          </div>
                    </>
                )}
                </div>

                    {/* Kontrol Modu - Yuvarlak Buton */}
                    <div 
                      className="relative flex flex-col items-center gap-1 w-20"
                      onMouseEnter={() => setShowControlPieMenu(true)}
                      onMouseLeave={() => setShowControlPieMenu(false)}
                    >
                      <button
                        onClick={() => {
                          setIsControlMode(!isControlMode);
                          if (isControlMode) {
                            setSelectedControlRows(new Set());
                          }
                        }}
                        className={`relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl ${
                          isControlMode
                            ? 'bg-gradient-to-br from-purple-500 to-purple-700'
                            : 'bg-gradient-to-br from-indigo-500 to-indigo-700'
                        }`}
                        title={isControlMode ? 'Kontrol modundan çık' : 'Kontrol seçim modunu aç'}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                </div>
                        {isControlMode && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-white"></div>
                        )}
                      </button>
                      <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                        {isControlMode ? (
                          <>Kontrol Modunu<br/>Kapat</>
                        ) : (
                          <>Kontrol Modunu<br/>Aç</>
                        )}
                      </span>

                      {/* Pie Chart Menu */}
                      {showControlPieMenu && isControlMode && currentTranslatedItems && currentTranslatedItems.length > 0 && (
                        <div className="absolute z-50" style={{ left: '50%', top: '28px', marginLeft: '-140px', marginTop: '-140px' }}>
                          <svg width="280" height="280" viewBox="0 0 280 280" className="pointer-events-auto drop-shadow-2xl">
                            {/* Pie Chart 3 Dilim */}
                            
                            {/* Dilim 1: Sayfayı Kontrol Edildi İşaretle (Sol - 80°) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                const uncheckedSids = currentTranslatedItems
                                  .filter((item: {sid: string, is_checked?: boolean}) => !item.is_checked)
                                  .map((item: {sid: string}) => item.sid);
                                setSelectedControlRows(new Set(uncheckedSids));
                                console.log(`Kontrol edilmemiş satırlar seçildi: ${uncheckedSids.length} satır`);
                              }}
                              className="cursor-pointer transition-all duration-300 hover:opacity-90"
                              style={{ transformOrigin: '140px 140px' }}
                            >
                              <path
                                d="M 140 140 L 28 140 A 112 112 0 0 1 57.5 51.5 Z"
                                fill="url(#gradient-green)"
                                stroke="white"
                                strokeWidth="3"
                                className="transition-all duration-300 hover:scale-105"
                                style={{ transformOrigin: '140px 140px' }}
                              />
                              <svg x="58" y="80" width="22" height="22" viewBox="0 0 24 24" className="pointer-events-none">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <text x="70" y="112" fontSize="11" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                Kontrol
                              </text>
                              <text x="70" y="125" fontSize="11" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                Edildi
                              </text>
                            </g>

                            {/* Dilim 2: Güncelle (Üst - 80°) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selectedControlRows.size > 0) {
                                  setShowUpdateControlModal(true);
                                }
                              }}
                              className={`transition-all duration-300 ${selectedControlRows.size > 0 ? 'cursor-pointer hover:opacity-90' : 'opacity-50 cursor-not-allowed'}`}
                              style={{ transformOrigin: '140px 140px' }}
                            >
                              <path
                                d="M 140 140 L 57.5 51.5 A 112 112 0 0 1 222.5 51.5 Z"
                                fill={selectedControlRows.size > 0 ? "url(#gradient-blue)" : "#94a3b8"}
                                stroke="white"
                                strokeWidth="3"
                                className="transition-all duration-300 hover:scale-105"
                                style={{ transformOrigin: '140px 140px' }}
                              />
                              <svg x="129" y="48" width="22" height="22" viewBox="0 0 24 24" className="pointer-events-none">
                                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <text x="140" y="80" fontSize="11" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                Güncelle
                              </text>
                              {selectedControlRows.size > 0 && (
                                <>
                                  <text x="140" y="93" fontSize="10" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                    ({selectedControlRows.size})
                                  </text>
                                </>
                              )}
                            </g>

                            {/* Dilim 3: Sayfayı Kontrol Edilmedi İşaretle (Sağ - 80°) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                const checkedSids = currentTranslatedItems
                                  .filter((item: {sid: string, is_checked?: boolean}) => item.is_checked === true)
                                  .map((item: {sid: string}) => item.sid);
                                setSelectedControlRows(new Set(checkedSids));
                                console.log(`Kontrol edilmiş satırlar seçildi: ${checkedSids.length} satır`);
                              }}
                              className="cursor-pointer transition-all duration-300 hover:opacity-90"
                              style={{ transformOrigin: '140px 140px' }}
                            >
                              <path
                                d="M 140 140 L 222.5 51.5 A 112 112 0 0 1 252 140 Z"
                                fill="url(#gradient-amber)"
                                stroke="white"
                                strokeWidth="3"
                                className="transition-all duration-300 hover:scale-105"
                                style={{ transformOrigin: '140px 140px' }}
                              />
                              <svg x="199" y="80" width="22" height="22" viewBox="0 0 24 24" className="pointer-events-none">
                                <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <text x="210" y="112" fontSize="11" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                Kontrol
                              </text>
                              <text x="210" y="125" fontSize="11" fill="white" fontWeight="bold" textAnchor="middle" className="pointer-events-none select-none">
                                Edilmedi
                              </text>
                            </g>

                            {/* Merkez Daire (Ana Buton Kopyası - Tıklanabilir) */}
                            <g 
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsControlMode(false);
                                setSelectedControlRows(new Set());
                                setShowControlPieMenu(false);
                              }}
                              className="cursor-pointer transition-all duration-300 hover:opacity-90"
                            >
                              <defs>
                                <linearGradient id="gradient-center-purple" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" style={{ stopColor: '#a855f7', stopOpacity: 1 }} />
                                  <stop offset="100%" style={{ stopColor: '#7e22ce', stopOpacity: 1 }} />
                                </linearGradient>
                              </defs>
                              <circle 
                                cx="140" 
                                cy="140" 
                                r="28" 
                                fill="url(#gradient-center-purple)"
                                className="transition-all duration-300 hover:scale-110"
                                style={{ transformOrigin: '140px 140px' }}
                              />
                              <svg x="119" y="119" width="42" height="42" viewBox="0 0 24 24" className="pointer-events-none">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {/* Pulse efekti */}
                              <circle cx="154" cy="126" r="4.5" fill="#4ade80" className="animate-pulse">
                                <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite"/>
                              </circle>
                              <circle cx="154" cy="126" r="4.5" fill="white" stroke="none" strokeWidth="0.4"/>
                            </g>

                            {/* Gradient Tanımlamaları */}
                            <defs>
                              <linearGradient id="gradient-green" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#059669', stopOpacity: 1 }} />
                              </linearGradient>
                              <linearGradient id="gradient-amber" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#d97706', stopOpacity: 1 }} />
                              </linearGradient>
                              <linearGradient id="gradient-blue" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#2563eb', stopOpacity: 1 }} />
                              </linearGradient>
                            </defs>
                          </svg>
                  </div>
                      )}
                    </div>

                    {/* Kontrol Edilenleri Gizle - Yuvarlak Buton */}
                    <div className="flex flex-col items-center gap-1 w-20">
                      <button
                        onClick={() => setHideCheckedCriticalErrors(!hideCheckedCriticalErrors)}
                        className={`relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl ${
                          hideCheckedCriticalErrors
                            ? 'bg-gradient-to-br from-orange-500 to-orange-700'
                            : 'bg-gradient-to-br from-gray-500 to-gray-700'
                        }`}
                        title={hideCheckedCriticalErrors ? 'Kontrol edilen satırları göster' : 'Kontrol edilen satırları gizle'}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {hideCheckedCriticalErrors ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                            )}
                          </svg>
                        </div>
                        {hideCheckedCriticalErrors && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-400 rounded-full animate-pulse border-2 border-white"></div>
                        )}
                      </button>
                      <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                        {hideCheckedCriticalErrors ? (
                          <>Kontrol Edilenleri<br/>Göster</>
                        ) : (
                          <>Kontrol Edilenleri<br/>Gizle</>
                        )}
                      </span>
                    </div>

                  </div>
                </div>
              </div>

              {/* Çeviri Sonuçları Tablosu */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden flex flex-col">
                {/* Sabit Tablo Başlığı */}
                <div style={{ paddingRight: `${scrollbarWidth}px` }} className="bg-gray-50 dark:bg-gray-700">
                  <table className="min-w-full w-full table-fixed">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr className="divide-x divide-gray-200 dark:divide-gray-600">
                      <th className="w-12 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                        <span></span>
                      </th>
                      <th className={`${textColumnWidth} px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600`}>
                        <span>{selectedDocumentSourceLanguage ? getLanguageName(selectedDocumentSourceLanguage) : 'Türkçe'} Metin</span>
                      </th>
                      <th className={`${textColumnWidth} px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600`}>
                        <div className="flex items-center gap-2">
                          <span>{selectedDocumentDestinationLanguage ? getLanguageName(selectedDocumentDestinationLanguage) : 'İngilizce'} Metin</span>
                          {isEnSelectionMode && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">(Seçili Değişiklikleri Sil)</span>
                          )}
                        </div>
                      </th>
                      {isEnSelectionMode && (
                        <th className="w-12 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                          <span>Seç</span>
                        </th>
                      )}
                      {showControlColumn && (
                      <th className="w-20 px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-center gap-1">
                          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <span>Kontrol</span>
                        </div>
                      </th>
                      )}
                      {showGlossaryColumn && (
                      <th className="w-24 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                        <span>Glossary</span>
                      </th>
                      )}
                      {showQualityColumn && (
                      <th className="w-20 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                          <span>Kalite Kontrol</span>
                      </th>
                      )}
                      {showCommentsColumn && (
                        <th className="w-20 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-center gap-1">
                            <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                            </svg>
                            <span>Yorumlar</span>
                          </div>
                        </th>
                      )}
                    </tr>
                    </thead>
                  </table>
                </div>
                
                {/* Scroll Edilebilir Tablo İçeriği */}
                <div 
                  ref={scrollContainerRef}
                  data-testid="translation-table-container"
                  className="overflow-y-scroll overflow-x-hidden border-t border-gray-200 dark:border-gray-600"
                  style={{ height: '504px', minHeight: '504px' }}
                >
                  <table className="min-w-full w-full table-fixed">
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                      {currentTranslatedItems.map((item, index) => (
                        <TableRow
                            key={`${item.sid}-${index}`} 
                            item={item}
                            index={index}
                            originalIndex={item.originalIndex}
                            translatedStartIndex={translatedStartIndex}
                          parseRunsText={parseRunsText}
                          highlightedRowSid={highlightedRowSid}
                          dragOverRowSid={dragOverRowSid}
                          editingEnSid={editingEnSid}
                          editingEnText={editingEnText}
                          isEnSelectionMode={isEnSelectionMode}
                          selectedEnRows={selectedEnRows}
                          draggedRowSid={draggedRowSid}
                          selectedDocumentDestinationLanguage={selectedDocumentDestinationLanguage}
                          qualityControlResults={qualityControlResults || []}
                          qualityControlDocumentId={qualityControlDocumentId}
                          selectedDocumentId={selectedDocumentId}
                          hoverTimeoutRef={hoverTimeoutRef}
                          isControlMode={isControlMode}
                          handleToggleIsChecked={handleToggleIsChecked}
                          selectedControlRows={selectedControlRows}
                          handleControlRowSelect={handleControlRowSelect}
                          enChangeHistory={enChangeHistory}
                          onShowChangeHistory={(sid) => {
                            setSelectedRowChangeHistory(enChangeHistory.find(item => item.sid === sid) || null);
                            setShowChangeHistoryModal(true);
                          }}
                          // Event handlers
                          handleDragOver={handleDragOver}
                          handleDragLeave={handleDragLeave}
                          handleDrop={handleDrop}
                          handleEnEditSave={handleEnEditSave}
                          handleEnEditCancel={handleEnEditCancel}
                          setEditingEnText={setEditingEnText}
                          calculateRows={calculateRows}
                          handleEnEditStart={handleEnEditStart}
                          setSelectedEnRowSid={setSelectedEnRowSid}
                          setShowDeleteEnRowModal={setShowDeleteEnRowModal}
                          handleEnRowSelect={handleEnRowSelect}
                          handleDragStart={handleDragStart}
                          handleDragEnd={handleDragEnd}
                          setSelectedQualityResult={setSelectedQualityResult}
                          setShowQualityDetailPopover={setShowQualityDetailPopover}
                          setQualityPopoverPosition={setQualityPopoverPosition}
                          getLanguageName={getLanguageName}
                          showControlColumn={showControlColumn}
                          showGlossaryColumn={showGlossaryColumn}
                          showQualityColumn={showQualityColumn}
                          showCommentsColumn={showCommentsColumn}
                          rowComments={rowComments}
                          onOpenComments={(sid, rowNumber) => {
                            setSelectedRowForComments({sid, rowNumber});
                            setShowCommentsModal(true);
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>


              {/* Sayfalama Kontrolleri */}
              {totalTranslatedPages > 1 && (
                <div className="flex justify-center items-center mt-2">
                  <div className="flex items-center gap-1">
                    {/* İlk sayfa butonu */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleTranslatedPageChange(1)}
                        disabled={translatedCurrentPage === 1}
                        className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                          translatedCurrentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                        }`}
                        title="İlk sayfa"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
                        </svg>
                      </button>
                      <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">İlk</span>
                    </div>
                    
                    {/* Önceki sayfa butonu */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleTranslatedPageChange(Math.max(1, translatedCurrentPage - 1))}
                        disabled={translatedCurrentPage === 1}
                        className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                          translatedCurrentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                        }`}
                        title="Önceki sayfa"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                        </svg>
                      </button>
                      <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">Önceki</span>
                    </div>

                    {/* Sayfa numaraları */}
                    <div className="flex items-center gap-1 mx-2">
                      {(() => {
                        const pages = [];
                        
                        if (totalTranslatedPages <= 7) {
                          // 7 veya daha az sayfa varsa hepsini göster
                          for (let i = 1; i <= totalTranslatedPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // İlk sayfa her zaman göster
                          pages.push(1);
                          
                          if (translatedCurrentPage <= 4) {
                            // Başlangıçta: 1, 2, 3, 4, 5, ..., son
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push('...');
                            pages.push(totalTranslatedPages);
                          } else if (translatedCurrentPage >= totalTranslatedPages - 3) {
                            // Sonda: 1, ..., son-4, son-3, son-2, son-1, son
                            pages.push('...');
                            for (let i = totalTranslatedPages - 4; i <= totalTranslatedPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // Ortada: 1, ..., current-1, current, current+1, ..., son
                            pages.push('...');
                            pages.push(translatedCurrentPage - 1);
                            pages.push(translatedCurrentPage);
                            pages.push(translatedCurrentPage + 1);
                            pages.push('...');
                            pages.push(totalTranslatedPages);
                          }
                        }
                        
                        return pages.map((page, index) => {
                          if (page === '...') {
                            return (
                              <div key={`dots-${index}`} className="flex flex-col items-center">
                                <span className="w-10 h-8 flex items-center justify-center text-gray-600 dark:text-gray-200">
                                  ...
                                </span>
                                <span className="text-xs text-transparent mt-1 opacity-0">•</span>
                              </div>
                            );
                          }
                          
                          return (
                            <div key={page} className="flex flex-col items-center">
                              <button
                                onClick={() => handleTranslatedPageChange(page as number)}
                                className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                                  translatedCurrentPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                                }`}
                              >
                                {page}
                              </button>
                              <span className="text-xs text-transparent mt-1 opacity-0">•</span>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Sonraki sayfa butonu */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleTranslatedPageChange(Math.min(totalTranslatedPages, translatedCurrentPage + 1))}
                        disabled={translatedCurrentPage === totalTranslatedPages}
                        className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                          translatedCurrentPage === totalTranslatedPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                        }`}
                        title="Sonraki sayfa"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                        </svg>
                      </button>
                      <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">Sonraki</span>
                    </div>
                    
                    {/* Son sayfa butonu */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleTranslatedPageChange(totalTranslatedPages)}
                        disabled={translatedCurrentPage === totalTranslatedPages}
                        className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                          translatedCurrentPage === totalTranslatedPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                        }`}
                        title="Son sayfa"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
                        </svg>
                      </button>
                      <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">Son</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Translations Tab Content
  const renderTranslationsTab = () => {
    // Show translated data view if selected
    if (showTranslatedDataView) {
      return renderTranslatedDataView();
    }

    return (
    <div className="w-full">
      {/* Sade Navigasyon */}
        <div className="mb-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setSelectedOption(null);
              setTranslationResults(null);
              setEditingIndex(null);
              setEditingText('');
              setCurrentDocumentId(null);
              currentDocumentIdRef.current = null;
              setTableRefreshKey(0);
              setSelectedFile(null);
              setStatusMessage('');
              setUploadProgress(0);
              setIsUploading(false);
                setCompanyDocuments(null);
                setDocumentsError(null);
            }}
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <span className="text-sm font-medium">{selectedCompany?.name}</span>
          </button>
            <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Çeviriler
              </div>
              <button
                onClick={async () => {
                  if (selectedCompany) {
                    const companyId = selectedCompany.company_id || selectedCompany.id;
                    await fetchCompanyDocuments(companyId);
                  }
                }}
                disabled={isLoadingDocuments}
                className="flex items-center px-3 py-1.5 text-sm bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-800/40 text-purple-700 dark:text-purple-300 rounded-lg transition-colors disabled:opacity-50"
              >
                <svg className={`w-4 h-4 mr-1.5 ${isLoadingDocuments ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Yenile
              </button>
          </div>
        </div>
      </div>

        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{selectedCompany?.name} Çevirileri</h1>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">
                    Şirketinizin tüm çeviri dökümanlarını görüntüleyin ve yönetin
                  </p>
                </div>
                <div className="hidden md:block">
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
                    <svg className="w-10 h-10 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          {isLoadingDocuments ? (
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
                  <svg className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Çeviriler Yükleniyor...
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
                  Şirket çevirileri getiriliyor, lütfen bekleyin.
          </p>
        </div>
            </div>
          ) : documentsError ? (
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-red-200/50 dark:border-red-700/50 p-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Hata Oluştu
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {documentsError}
                </p>
                <button
                  onClick={async () => {
                    if (selectedCompany) {
                      const companyId = selectedCompany.company_id || selectedCompany.id;
                      await fetchCompanyDocuments(companyId);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Tekrar Dene
                </button>
              </div>
            </div>
          ) : !companyDocuments || companyDocuments.length === 0 ? (
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                  <svg className="w-8 h-8 text-gray-600 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Henüz Çeviri Yok
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {selectedCompany?.name} için henüz çeviri dökümanı bulunmuyor. İlk çevirinizi yapmak için "Çeviri Yap" sekmesini kullanın.
                </p>
                <button
                  onClick={() => setSelectedOption('translate')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                >
                  Çeviri Yapmaya Başla
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam Döküman</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{companyDocuments.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Şirket</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white truncate">{selectedCompany?.name}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Son Güncelleme</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">Şimdi</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Grid */}
              <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Çeviri Dökümanları</h2>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {companyDocuments.length} döküman
                    </span>
                  </div>
                </div>
                
                <div className="grid gap-4">
                  {companyDocuments.map((document: {document_id: string, file_name_no_ext: string, current_date?: string, current_time?: string, username?: string, source_language?: string, destination_language?: string}, index) => (
                    <div
                      key={document.document_id}
                      onClick={() => {
                        console.log('Döküman kartına tıklandı:', document.document_id, document.file_name_no_ext);
                        fetchTranslatedData(document.document_id, document.file_name_no_ext, document.source_language, document.destination_language);
                      }}
                      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 transition-all duration-200 cursor-pointer hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-lg">
                            {index + 1}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {document.file_name_no_ext}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              ID: {document.document_id}
                            </p>
                            {document.username && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                <span className="inline-flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                  </svg>
                                  {document.username}
                                </span>
                              </p>
                            )}
                            {document.current_date && document.current_time && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                <span className="inline-flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                  </svg>
                                  {formatDateTime(document.current_date, document.current_time)}
                                </span>
                              </p>
                            )}
                            {document.source_language && document.destination_language && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                <span className="inline-flex items-center">
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                                  </svg>
                                  {getLanguageName(document.source_language)} → {getLanguageName(document.destination_language)}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="p-2 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Kart tıklama olayını engelle
                              handleDeleteDocumentClick(document.document_id, document.file_name_no_ext);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Dökümanı Sil"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Çeviri Dökümanı
                          </span>
                          <div className="flex items-center space-x-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                              </svg>
                              Tamamlandı
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
  };

  // Translate Tab Content (existing file upload content)
  const renderTranslateTab = () => (
    <div className="w-full">
      <div className="px-6">
        {/* Basit Dosya Yükleme Alanı - Tablo göründüğünde veya başarı mesajı gösterilirken gizle */}
        {!(translationResults && translationResults.length > 0) && !showTranslateSuccessModal && (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8 mb-8 max-w-4xl mx-auto">
        {/* Geri Dönüş Butonu */}
        <div className="mb-6">
          <button
            onClick={() => {
              setSelectedOption(null);
              setSelectedFile(null);
              setStatusMessage('');
              setUploadProgress(0);
              setIsUploading(false);
              setTranslationResults(null);
              setEditingIndex(null);
              setEditingText('');
              setCurrentDocumentId(null);
              setTableRefreshKey(0);
            }}
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors group"
          >
            <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <span className="text-sm font-medium">İşlem Seçimine Dön</span>
          </button>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
          Dosya Yükle ve Ayrıştır
        </h2>
        
        {/* Dosya Seçme Alanı */}
        <div
          onDrop={handleFileDrop}
          onDragOver={handleFileDragOver}
          onDragLeave={handleFileDragLeave}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
            isDragging 
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
          }`}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            
            <div>
              <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Dosyanızı buraya sürükleyin veya seçin
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                DOCX dosyaları desteklenir
              </p>
            </div>
            
            <label className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg cursor-pointer transition-colors font-medium">
              <input type="file" className="hidden" onChange={handleFileChange} />
              Dosya Seç
            </label>
          </div>
        </div>

        {/* Seçilen Dosya Gösterimi */}
        {selectedFile && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedFile(null)}
                className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* İlerleme Çubuğu */}
        {isUploading && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Dosya Ayrıştırılıyor...
              </span>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                {uploadProgress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Durum Mesajı */}
        {statusMessage && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">{statusMessage}</p>
          </div>
        )}

        {/* Ayrıştırma Butonu */}
        <div className="mt-6 flex gap-4">
          <button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-300 ${
              !selectedFile || isUploading
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {isUploading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Ayrıştırılıyor...
              </span>
            ) : (
              'Dosya Ayrıştır'
            )}
          </button>
        
          {isUploading && (
            <button
              onClick={handleCancel}
              className="px-6 py-3 border-2 border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium transition-colors"
            >
              İptal
            </button>
          )}
        </div>
        </div>
        )}

        {/* Başarı Mesajı */}
        {showTranslateSuccessModal && (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-12 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Çeviri İşleme Alındı
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-4 max-w-2xl mx-auto">
                Çeviriniz işleme alınmıştır. Çevirinizin durumuna "Çevirileri Görüntüle" ekranından ulaşabilirsiniz.
              </p>
              {currentDocumentId && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                    Döküman ID:
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-mono text-blue-900 dark:text-blue-100 break-all flex-1">
                      {currentDocumentId}
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          // Modern clipboard API'yi dene
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            await navigator.clipboard.writeText(currentDocumentId);
                            success('Kopyalandı', 'Döküman ID panoya kopyalandı.');
                          } else {
                            // Fallback: Geleneksel yöntem
                            const textArea = document.createElement('textarea');
                            textArea.value = currentDocumentId;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            textArea.style.top = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            
                            try {
                              document.execCommand('copy');
                              success('Kopyalandı', 'Döküman ID panoya kopyalandı.');
                            } catch (err) {
                              console.error('Kopyalama başarısız:', err);
                              showError('Hata', 'Kopyalama işlemi başarısız oldu.');
                            } finally {
                              document.body.removeChild(textArea);
                            }
                          }
                        } catch (error) {
                          console.error('Clipboard error:', error);
                          showError('Hata', 'Kopyalama işlemi başarısız oldu.');
                        }
                      }}
                      className="flex-shrink-0 p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded-md transition-colors"
                      title="Döküman ID'sini kopyala"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              
              <button
                onClick={handleGoToTranslations}
                disabled={isLoadingTranslationView}
                className={`px-8 py-4 font-semibold rounded-xl transition-colors flex items-center gap-3 mx-auto text-lg ${
                  isLoadingTranslationView
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } text-white`}
              >
                {isLoadingTranslationView ? (
                  <>
                    <svg className="w-6 h-6 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    Çevirileri Görüntüle
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Çeviri Sonuçları Tablosu */}
        {translationResults && translationResults.length > 0 && !showTranslateSuccessModal && (
          <div className="bg-green-50/70 dark:bg-green-900/30 backdrop-blur-sm border border-green-200 dark:border-green-800 rounded-2xl shadow-lg p-6 w-full flex flex-col">
          {/* Sade Navigasyon */}
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setSelectedOption(null);
                  setTranslationResults(null);
                  setEditingIndex(null);
                  setEditingText('');
                  setCurrentDocumentId(null);
                  setTableRefreshKey(0);
                  setSelectedFile(null);
                  setStatusMessage('');
                  setUploadProgress(0);
                  setIsUploading(false);
                }}
                className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
                <span className="text-sm font-medium">{selectedCompany?.name}</span>
              </button>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Çeviri Yap
              </div>
            </div>
          </div>
          {/* Butonlar - Çeviri */}
          <div className="flex justify-start items-center gap-2 mb-4">
            {/* Çeviri Butonu */}
            <button
              onClick={() => setShowTranslateConfirmModal(true)}
              disabled={!currentDocumentId || isUploading}
              className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
                !currentDocumentId || isUploading
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              title="Çeviriyi başlat"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
              </svg>
              {isUploading ? 'Çevriliyor...' : 'Çeviriyi Başlat'}
            </button>
          </div>

          {/* Tablo İstatistikleri ve Sayfa Boyutu Seçimi (Yeni Tasarım) */}
          <div className="flex justify-between items-center mb-4">
            {/* Sol taraf - Tablo İstatistikleri */}
            <div className="flex items-center gap-3">
              {/* Toplam Kayıt */}
              <div className="flex flex-col items-center gap-1 w-20">
              <div className="relative w-14 h-14 rounded-full shadow-lg bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                <span className="text-white text-xs font-bold">{totalItems}</span>
              </div>
              <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                Toplam<br/>Kayıt
              </span>
            </div>

            {/* Sayfa Başına Pie Chart */}
            <div 
              className="relative flex flex-col items-center gap-1 w-20"
              onMouseEnter={() => setShowPageSizePieMenu(true)}
              onMouseLeave={() => setShowPageSizePieMenu(false)}
            >
              <button
                className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-teal-500 to-teal-700"
                title="Sayfa başına kayıt sayısı"
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{itemsPerPage}</span>
                </div>
              </button>
              <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                Sayfa<br/>Başına
              </span>

              {/* Pie Chart Menu for Page Size */}
              {showPageSizePieMenu && (
                <div className="absolute z-50" style={{ left: '50%', top: '28px', marginLeft: '-90px', marginTop: '-90px' }}>
                  <svg width="180" height="180" viewBox="0 0 180 180" className="pointer-events-auto drop-shadow-2xl">
                    {/* 25 - Sağ Üst (0° - 90°) */}
                    <g 
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemsPerPage(25);
                        setCurrentPage(1);
                      }}
                      className="cursor-pointer transition-all duration-300 hover:opacity-90"
                    >
                      <path
                        d="M 90 90 L 90 18 A 72 72 0 0 1 162 90 Z"
                        fill={itemsPerPage === 25 ? "url(#gradient-teal-active-runs)" : "url(#gradient-teal-runs)"}
                        stroke="white"
                        strokeWidth="2"
                      />
                      <text x="120" y="60" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle">25</text>
                    </g>

                    {/* 50 - Sağ Alt (90° - 180°) */}
                    <g 
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemsPerPage(50);
                        setCurrentPage(1);
                      }}
                      className="cursor-pointer transition-all duration-300 hover:opacity-90"
                    >
                      <path
                        d="M 90 90 L 162 90 A 72 72 0 0 1 90 162 Z"
                        fill={itemsPerPage === 50 ? "url(#gradient-teal-active-runs)" : "url(#gradient-teal-runs)"}
                        stroke="white"
                        strokeWidth="2"
                      />
                      <text x="120" y="120" fontSize="16" fill="white" fontWeight="bold" textAnchor="middle">50</text>
                    </g>

                    {/* 100 - Sol Alt (180° - 270°) */}
                    <g 
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemsPerPage(100);
                        setCurrentPage(1);
                      }}
                      className="cursor-pointer transition-all duration-300 hover:opacity-90"
                    >
                      <path
                        d="M 90 90 L 90 162 A 72 72 0 0 1 18 90 Z"
                        fill={itemsPerPage === 100 ? "url(#gradient-teal-active-runs)" : "url(#gradient-teal-runs)"}
                        stroke="white"
                        strokeWidth="2"
                      />
                      <text x="60" y="120" fontSize="14" fill="white" fontWeight="bold" textAnchor="middle">100</text>
                    </g>

                    {/* 200 - Sol Üst (270° - 360°) */}
                    <g 
                      onClick={(e) => {
                        e.stopPropagation();
                        setItemsPerPage(200);
                        setCurrentPage(1);
                      }}
                      className="cursor-pointer transition-all duration-300 hover:opacity-90"
                    >
                      <path
                        d="M 90 90 L 18 90 A 72 72 0 0 1 90 18 Z"
                        fill={itemsPerPage === 200 ? "url(#gradient-teal-active-runs)" : "url(#gradient-teal-runs)"}
                        stroke="white"
                        strokeWidth="2"
                      />
                      <text x="60" y="60" fontSize="14" fill="white" fontWeight="bold" textAnchor="middle">200</text>
                    </g>

                    <defs>
                      <linearGradient id="gradient-teal-runs" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#14b8a6', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#0f766e', stopOpacity: 1 }} />
                      </linearGradient>
                      <linearGradient id="gradient-teal-active-runs" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#2dd4bf', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: '#14b8a6', stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                  </svg>
              </div>
              )}
            </div>

              {/* Satıra Git */}
              <div className="flex flex-col items-center gap-1">
                <input
                  type="text"
                  value={goToRowValue}
                  onChange={handleGoToRowChange}
                  placeholder="→"
                  className="w-14 h-14 text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-lg"
                  title="Satır numarasını yazın (otomatik olarak gider)"
                />
                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                  Satıra<br/>Git
                </span>
              </div>
            </div>

            {/* Sağ taraf - Silme Butonları */}
            <div className="flex gap-3">
            {isSelectionMode ? (
              // Seçim modundayken gösterilen butonlar
              <>
                {selectedRows.size > 0 && (
                    <div className="flex flex-col items-center gap-1 w-20">
                  <button
                    onClick={() => setShowBulkDeleteModal(true)}
                        className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-red-500 to-red-700"
                    title="Seçili satırların değişikliklerini sil"
                  >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                        </div>
                        <div className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-yellow-400 rounded-full flex items-center justify-center border-2 border-white px-1">
                          <span className="text-xs font-bold text-gray-900">{selectedRows.size}</span>
                        </div>
                  </button>
                      <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                        Seçili<br/>Değişiklikleri Sil
                      </span>
                    </div>
                )}
                  <div className="flex flex-col items-center gap-1 w-20">
                <button
                  onClick={exitSelectionMode}
                      className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-gray-500 to-gray-700"
                  title="Seçim modundan çık"
                >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </div>
                </button>
                    <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                      İptal
                    </span>
                  </div>
              </>
            ) : (
              // Normal modda gösterilen butonlar
              <>
                  <div className="flex flex-col items-center gap-1 w-20">
                <button
                  onClick={startSelectionMode}
                      className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-blue-500 to-blue-700"
                  title="Seçili değişiklikleri sil"
                >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                      </div>
                </button>
                    <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                      Seçili<br/>Değişiklikleri Sil
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-1 w-20">
                <button
                  onClick={() => setShowDeleteChangesModal(true)}
                      className="relative w-14 h-14 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl bg-gradient-to-br from-red-500 to-red-700"
                  title="Tüm değişiklikleri sil"
                >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                      </div>
                </button>
                    <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 text-center leading-tight h-8 flex items-center justify-center">
                      Tüm<br/>Değişiklikleri Sil
                    </span>
                  </div>
              </>
            )}
            </div>
          </div>

          {/* Çeviri Sonuçları Tablosu */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden flex flex-col">
            <table className="min-w-full w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-[5]">
                <tr>
                  {isSelectionMode && (
                    <th className="w-12 px-4 py-3">
                      {/* Checkbox column header */}
                    </th>
                  )}
                  <th className="flex-1 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span>Metin</span>
                  </th>
                </tr>
              </thead>
            </table>
            
            {/* Sadece tbody için scroll container */}
            <div className="overflow-auto" style={{ height: '504px', minHeight: '504px' }}>
              <table className="min-w-full w-full">
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {currentItems.map((result, index) => {
                    const actualIndex = startIndex + index;
                    return (
                    <tr 
                      key={`${result.sid}-${index}-${tableRefreshKey}`} 
                      id={`translation-row-${result.sid}`}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {isSelectionMode && (
                        <td className="w-12 px-4 py-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedRows.has(result.sid)}
                              onChange={() => handleRowSelect(result.sid)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                          </div>
                        </td>
                      )}
                      <td className="flex-1 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {editingIndex === actualIndex ? (
                          // Edit mode
                          <div className="flex items-center gap-2">
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              onInput={(e) => setEditingText(e.currentTarget.textContent || '')}
                              className="flex-1 p-2 border-2 border-blue-500 dark:border-blue-400 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent break-words"
                              style={{ minHeight: '36px' }}
                            >
                              {parseRunsText(result.runs)}
                            </div>
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => handleEditSave(actualIndex)}
                                className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                                title="Kaydet"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                </svg>
                              </button>
                              <button
                                onClick={handleEditCancel}
                                className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                                title="İptal"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div className="flex items-start justify-between group">
                            <div className="flex-1 pr-2">
                              {parseRunsText(result.runs)}
                            </div>
                            {!isSelectionMode && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleEditStart(actualIndex, parseRunsText(result.runs))}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                                  title="Düzenle"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedRowSid(result.sid);
                                    setShowDeleteRowModal(true);
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all duration-200"
                                  title="Bu satırın değişikliklerini sil"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sayfalama Kontrolleri */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-4">
              <div className="flex items-center gap-1">
                {/* İlk sayfa butonu */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                    }`}
                    title="İlk sayfa"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
                    </svg>
                  </button>
                  <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">İlk</span>
                </div>
                
                {/* Önceki sayfa butonu */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                    }`}
                    title="Önceki sayfa"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                  </button>
                  <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">Önceki</span>
                </div>

                {/* Sayfa numaraları */}
                <div className="flex items-center gap-1 mx-2">
                  {(() => {
                    const pages = [];
                    
                    if (totalPages <= 7) {
                      // 7 veya daha az sayfa varsa hepsini göster
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // İlk sayfa her zaman göster
                      pages.push(1);
                      
                      if (currentPage <= 4) {
                        // Başlangıçta: 1, 2, 3, 4, 5, ..., son
                        for (let i = 2; i <= 5; i++) {
                          pages.push(i);
                        }
                        pages.push('...');
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        // Sonda: 1, ..., son-4, son-3, son-2, son-1, son
                        pages.push('...');
                        for (let i = totalPages - 4; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        // Ortada: 1, ..., current-1, current, current+1, ..., son
                        pages.push('...');
                        pages.push(currentPage - 1);
                        pages.push(currentPage);
                        pages.push(currentPage + 1);
                        pages.push('...');
                        pages.push(totalPages);
                      }
                    }
                    
                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <div key={`dots-${index}`} className="flex flex-col items-center">
                            <span className="w-10 h-8 flex items-center justify-center text-gray-600 dark:text-gray-200">
                              ...
                            </span>
                            <span className="text-xs text-transparent mt-1 opacity-0">•</span>
                          </div>
                        );
                      }
                      
                      return (
                        <div key={page} className="flex flex-col items-center">
                          <button
                            onClick={() => setCurrentPage(page as number)}
                            className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                          <span className="text-xs text-transparent mt-1 opacity-0">•</span>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Sonraki sayfa butonu */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                    }`}
                    title="Sonraki sayfa"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                  <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">Sonraki</span>
                </div>
                
                {/* Son sayfa butonu */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`w-10 h-8 rounded text-sm font-medium transition-colors flex items-center justify-center ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                    }`}
                    title="Son sayfa"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
                    </svg>
                  </button>
                  <span className="text-xs text-gray-600 dark:text-gray-200 mt-1 opacity-70">Son</span>
                </div>
              </div>
            </div>
          )}

          </div>
        )}
      </div>
    </div>
  );

  // Render main content based on current step and selected option
  const renderContent = () => {
    if (currentStep === 'company-selection') {
      return (
        <CompanySelection
          companies={companies}
          isLoading={isLoading}
          error={error}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onAddCompany={() => setShowAddCompanyModal(true)}
          onCompanySelect={handleCompanySelect}
          onShowCompany={handleShowCompany}
          onEditCompany={handleEditCompany}
          onRemoveCompany={handleRemoveCompany}
          onRefreshCompanies={refreshCompanies}
        />
      );
    }

    if (currentStep === 'company-tabs') {
      if (!selectedOption) {
        return (
          <CompanyTabs
            selectedCompany={selectedCompany}
            selectedOption={selectedOption}
            onBackToCompanySelection={handleBackToCompanySelection}
            onOptionSelect={setSelectedOption}
          />
        );
      }

      if (selectedOption === 'translate') {
        if (translationResults && translationResults.length > 0 && !showTranslateSuccessModal) {
          return (
            <TranslationTable
              translationResults={translationResults}
              currentDocumentId={currentDocumentId}
              isUploading={isUploading}
              editingIndex={editingIndex}
              editingText={editingText}
              selectedRows={selectedRows}
              isSelectionMode={isSelectionMode}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              totalPages={totalPages}
              selectedCompany={selectedCompany as any}
              searchTerm={searchTerm}
              onBackToOptions={() => {
                setSelectedOption(null);
                setTranslationResults(null);
                setEditingIndex(null);
                setEditingText('');
                setCurrentDocumentId(null);
                setTableRefreshKey(0);
                setSelectedFile(null);
                setStatusMessage('');
                setUploadProgress(0);
                setIsUploading(false);
                setSearchTerm('');
              }}
              onStartTranslation={() => setShowTranslateConfirmModal(true)}
              onStartSelectionMode={startSelectionMode}
              onExitSelectionMode={exitSelectionMode}
              onShowBulkDeleteModal={() => setShowBulkDeleteModal(true)}
              onShowDeleteChangesModal={() => setShowDeleteChangesModal(true)}
              onItemsPerPageChange={setItemsPerPage}
              onCurrentPageChange={setCurrentPage}
              onRowSelect={handleRowSelect}
              onEditStart={handleEditStart}
              onEditSave={handleEditSave}
              onEditCancel={handleEditCancel}
              onEditingTextChange={setEditingText}
              onShowDeleteRowModal={(sid: string) => {
                setSelectedRowSid(sid);
                setShowDeleteRowModal(true);
              }}
              onSearchTermChange={setSearchTerm}
              onGoToRow={handleGoToRow}
              parseRunsText={parseRunsText}
              calculateRows={calculateRows}
              onDragDropUpdate={async (sourceSid: string, targetSid: string, combinedText: string, emptyText: string) => {
                // Update target row with combined text
                const newTargetRuns = createRunsString(combinedText);
                await logChanges(targetSid, newTargetRuns);

                // Clear source row text
                const emptyRuns = createRunsString(emptyText);
                await logChanges(sourceSid, emptyRuns);
              }}
              showToast={(message: string, type: 'success' | 'error') => {
                if (type === 'success') {
                  success(message);
                } else {
                  showError(message);
                }
              }}
              onRefreshData={async () => {
                await getSerializedData();
              }}
            />
          );
        } else {
          return (
            <FileUpload
              selectedFile={selectedFile}
              uploadProgress={uploadProgress}
              statusMessage={statusMessage}
              isDragging={isDragging}
              isUploading={isUploading}
              showTranslateSuccessModal={showTranslateSuccessModal}
              currentDocumentId={currentDocumentId}
              reportType={reportType}
              reportYear={reportYear}
              showConfigModal={showConfigModal}
              fileName={fileName}
              sourceLanguage={sourceLanguage}
              destinationLanguage={destinationLanguage}
              onFileChange={handleFileChange}
              onDrop={handleFileDrop}
              onDragOver={handleFileDragOver}
              onDragLeave={handleFileDragLeave}
              onUpload={handleUpload}
              onCancel={handleCancel}
              onContinue={handleContinue}
              onConfigModalClose={handleConfigModalClose}
              onFileNameChange={handleFileNameChange}
              onSourceLanguageChange={handleSourceLanguageChange}
              onDestinationLanguageChange={handleDestinationLanguageChange}
              onRemoveFile={handleRemoveFile}
              onBackToOptions={() => {
                setSelectedOption(null);
                setSelectedFile(null);
                setStatusMessage('');
                setUploadProgress(0);
                setIsUploading(false);
                setTranslationResults(null);
                setEditingIndex(null);
                setEditingText('');
                setCurrentDocumentId(null);
                setTableRefreshKey(0);
                setReportType(null);
                setReportYear(null);
                setShowConfigModal(false);
                setFileName('');
                // Dil seçeneklerini default değerlere sıfırla
                setSourceLanguage('tr');
                setDestinationLanguage('en-US');
              }}
              onGoToTranslations={handleGoToTranslations}
              onCopyDocumentId={async (documentId: string) => {
                try {
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(documentId);
                    success('Kopyalandı', 'Döküman ID panoya kopyalandı.');
                  } else {
                    const textArea = document.createElement('textarea');
                    textArea.value = documentId;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      success('Kopyalandı', 'Döküman ID panoya kopyalandı.');
                    } catch (err) {
                      showError('Hata', 'Kopyalama işlemi başarısız oldu.');
                    }
                    document.body.removeChild(textArea);
                  }
                } catch (err) {
                  showError('Hata', 'Kopyalama işlemi başarısız oldu.');
                }
              }}
              onReportTypeChange={(value) => setReportType(value || null)}
              onReportYearChange={(value) => setReportYear(value || null)}
            />
          );
        }
      }

      if (selectedOption === 'translations') {
        if (showTranslatedDataView) {
          return renderTranslatedDataView();
        }
        
        return (
          <CompanyDocuments
            selectedCompany={selectedCompany as any}
            companyDocuments={companyDocuments}
            isLoadingDocuments={isLoadingDocuments}
            documentsError={documentsError}
            onBackToOptions={() => {
              setSelectedOption(null);
              setTranslationResults(null);
              setEditingIndex(null);
              setEditingText('');
              setCurrentDocumentId(null);
              currentDocumentIdRef.current = null;
              setTableRefreshKey(0);
              setSelectedFile(null);
              setStatusMessage('');
              setUploadProgress(0);
              setIsUploading(false);
              setCompanyDocuments(null);
              setDocumentsError(null);
            }}
            onRefreshDocuments={async () => {
              if (selectedCompany) {
                const companyId = selectedCompany.company_id || selectedCompany.id;
                await fetchCompanyDocuments(companyId);
              }
            }}
            onGoToTranslate={() => setSelectedOption('translate')}
            onViewDocument={(documentId: string, documentName: string) => {
              // companyDocuments'tan dil bilgilerini bul
              const document = companyDocuments?.find(doc => doc.document_id === documentId) as {document_id: string, file_name_no_ext: string, current_date?: string, current_time?: string, username?: string, source_language?: string, destination_language?: string} | undefined;
              fetchTranslatedData(documentId, documentName, document?.source_language, document?.destination_language);
            }}
            onDeleteDocument={(documentId: string) => {
              // CompanyDocuments'tan gelen silme isteği için doküman adını bul
              const document = companyDocuments?.find(doc => doc.document_id === documentId);
              handleDeleteDocumentClick(documentId, document?.file_name_no_ext || 'Bilinmeyen Döküman');
            }}
            onToggleCompletion={toggleDocumentCompletion}
          />
        );
      }
    }

    return null;
  };

  return (
    <>
      {renderContent()}
      
      {/* Completion Tooltip Portal */}
      {showCompletionTooltip && completionTooltipPosition && completionData && createPortal(
        <div 
          className="fixed w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 shadow-lg z-[9999999]"
          style={{
            top: `${completionTooltipPosition.top}px`,
            left: `${completionTooltipPosition.left}px`,
            transform: 'translateX(-50%)'
          }}
        >
          {/* Arrow */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900 dark:border-b-gray-100"></div>
          <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
            Kontrol Durumu
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                <span>Kontrol Edildi:</span>
              </div>
              <span className="font-bold">{parseInt(completionData.checked_count) || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                <span>Kontrol Edilmedi:</span>
              </div>
              <span className="font-bold">{parseInt(completionData.unchecked_count) || 0}</span>
            </div>
            <div className="flex items-center justify-between pt-1.5 border-t border-gray-700 dark:border-gray-300 mt-1.5">
              <span>Toplam:</span>
              <span className="font-bold">{(parseInt(completionData.checked_count) || 0) + (parseInt(completionData.unchecked_count) || 0)}</span>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Add Company Modal */}
      <AddCompanyModal
        isOpen={showAddCompanyModal}
        onClose={() => setShowAddCompanyModal(false)}
        onSubmit={handleAddCompany}
      />

      {/* Company Detail Modal */}
      <CompanyDetailModal
        isOpen={showDetailModal}
        onClose={handleCloseDetailModal}
        company={selectedCompanyForDetail}
      />

      {/* Edit Company Modal */}
      <EditCompanyModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        company={selectedCompanyForEdit}
        onUpdate={handleUpdateCompany}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCompanyForDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={cancelDeleteCompany}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Şirketi Sil
                </h2>
              </div>
              <button
                onClick={cancelDeleteCompany}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                <strong>"{selectedCompanyForDelete.name}"</strong> şirketini silmek istediğinizden emin misiniz?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      Dikkat!
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                      Bu işlem geri alınamaz. Şirket ve tüm ilişkili dosyalar kalıcı olarak silinecektir.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={cancelDeleteCompany}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmDeleteCompany}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteChangesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            {deleteSuccess ? (
              // Success State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Değişiklikler Silindi
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Tüm değişiklikler başarıyla silindi ve orijinal metinler geri yüklendi.
                </p>
              </div>
            ) : (
              // Confirmation State
              <>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Değişiklikleri Sil
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Bu işlem geri alınamaz
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  Bu dokümana ait tüm değişiklikler silinecek ve orijinal metinler geri yüklenecek. Bu işlemi onaylıyor musunuz?
                </p>
                
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowDeleteChangesModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={deleteLogChanges}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
                  >
                    Evet, Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Row Confirmation Modal */}
      {showDeleteRowModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            {deleteRowSuccess ? (
              // Success State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Satır Değişiklikleri Silindi
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Bu satıra ait değişiklikler başarıyla silindi ve orijinal metin geri yüklendi.
                </p>
              </div>
            ) : (
              // Confirmation State
              <>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Satır Değişikliklerini Sil
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Bu işlem geri alınamaz
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  Bu satıra ait değişiklikler silinecek ve orijinal metin geri yüklenecek. Bu işlemi onaylıyor musunuz?
                </p>
                
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowDeleteRowModal(false);
                      setSelectedRowSid(null);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={deleteLogChangesBySid}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
                  >
                    Evet, Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            {bulkDeleteSuccess ? (
              // Success State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Seçili Değişiklikler Silindi
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {selectedRows.size} satıra ait değişiklikler başarıyla silindi ve orijinal metinler geri yüklendi.
                </p>
              </div>
            ) : (
              // Confirmation State
              <>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Seçili Değişiklikleri Sil
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Bu işlem geri alınamaz
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  <span className="font-medium">{selectedRows.size} satıra</span> ait değişiklikler silinecek ve orijinal metinler geri yüklenecek. Bu işlemi onaylıyor musunuz?
                </p>
                
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowBulkDeleteModal(false);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={deleteLogChangesBySidList}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
                  >
                    Evet, {selectedRows.size} Satırı Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete EN Changes Confirmation Modal */}
      {showDeleteEnChangesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            {deleteEnSuccess ? (
              // Success State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Başarılı!
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Tüm {selectedDocumentDestinationLanguage ? getLanguageName(selectedDocumentDestinationLanguage) : 'İngilizce'} değişiklikler başarıyla silindi.
                </p>
              </div>
            ) : (
              // Confirmation State
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedDocumentDestinationLanguage ? getLanguageName(selectedDocumentDestinationLanguage) : 'İngilizce'} Değişiklikleri Sil
                  </h3>
                  <button
                    onClick={() => setShowDeleteEnChangesModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Bu işlem tüm {selectedDocumentDestinationLanguage ? getLanguageName(selectedDocumentDestinationLanguage) : 'İngilizce'} metin değişikliklerini silecek. Bu işlem geri alınamaz.
                </p>
                
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowDeleteEnChangesModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={deleteEnLogChanges}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
                  >
                    Evet, Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete EN Row Confirmation Modal */}
      {showDeleteEnRowModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            {deleteEnRowSuccess ? (
              // Success State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Başarılı!
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  İngilizce satır değişiklikleri başarıyla silindi.
                </p>
              </div>
            ) : (
              // Confirmation State
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    İngilizce Satır Değişikliklerini Sil
                  </h3>
                  <button
                    onClick={() => setShowDeleteEnRowModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Bu satırın İngilizce metin değişikliklerini silmek istediğinizden emin misiniz?
                </p>
                
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowDeleteEnRowModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={deleteEnLogChangesBySid}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
                  >
                    Evet, Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Delete EN Confirmation Modal */}
      {showBulkDeleteEnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            {bulkDeleteEnSuccess ? (
              // Success State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Başarılı!
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Seçili İngilizce satır değişiklikleri başarıyla silindi.
                </p>
              </div>
            ) : (
              // Confirmation State
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Seçili İngilizce Değişiklikleri Sil
                  </h3>
                  <button
                    onClick={() => setShowBulkDeleteEnModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {selectedEnRows.size} adet seçili İngilizce satırın değişikliklerini silmek istediğinizden emin misiniz?
                </p>
                
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowBulkDeleteEnModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={deleteEnLogChangesBySidList}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors"
                  >
                    Evet, {selectedEnRows.size} Satırı Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tüm EN Değişiklikleri Sil Modal */}
      {showDeleteAllEnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Tüm İngilizce Değişiklikleri Sil
              </h3>
              <button
                onClick={() => setShowDeleteAllEnModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="mb-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <p className="text-center text-gray-600 dark:text-gray-400">
                Bu dokümandaki <strong>tüm İngilizce değişiklikleri</strong> silinecek. 
                Bu işlem geri alınamaz.
              </p>
              <p className="text-center text-sm text-red-600 dark:text-red-400 mt-2">
                Emin misiniz?
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteAllEnModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  setShowDeleteAllEnModal(false);
                  deleteEnLogChanges();
                }}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Tümünü Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kontrol Durumu Güncelleme Modal */}
      {showUpdateControlModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Kontrol Durumunu Güncelle
              </h3>
              <button
                onClick={() => !isUpdatingControl && setShowUpdateControlModal(false)}
                disabled={isUpdatingControl}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="mb-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <p className="text-center text-gray-600 dark:text-gray-400">
                <strong>{selectedControlRows.size} satır</strong> için kontrol durumu güncellenecek.
              </p>
              <p className="text-center text-sm text-blue-600 dark:text-blue-400 mt-2">
                Devam etmek istiyor musunuz?
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpdateControlModal(false)}
                disabled={isUpdatingControl}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                İptal
              </button>
              <button
                onClick={async () => {
                  setIsUpdatingControl(true);
                  try {
                    const sidArray = Array.from(selectedControlRows);
                    const checkParameterArray = sidArray.map(sid => {
                      const currentItem = translatedData?.find(item => item.sid === sid);
                      return !currentItem?.is_checked;
                    });
                    const formattedSids = `(${sidArray.map(sid => `'${sid}'`).join(', ')})`;
                    const formattedCheckParameter = `(${checkParameterArray.join(', ')})`;
                    
                    const response = await fetch('https://n8n.mahir.ai/webhook/update_checked_rows', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        document_id: selectedDocumentId,
                        sids: formattedSids,
                        check_parameter: formattedCheckParameter,
                        user_id: Number(user?.id),
                        timestamp: Math.floor(Date.now() / 1000)
                      })
                    });
                    
                    if (response.ok) {
                      const responseText = await response.text();
                      if (responseText && responseText.trim()) {
                        const jsonResponse = JSON.parse(responseText);
                        if (Array.isArray(jsonResponse) && jsonResponse.length > 0 && jsonResponse[0].success === true) {
                          // Başarılı - Veriyi yenile
                          setSelectedControlRows(new Set());
                          if (selectedDocumentId && selectedDocumentName) {
                            await refreshTranslatedDataOnly(selectedDocumentId, selectedDocumentName);
                          }
                          success('Kontrol durumu başarıyla güncellendi');
                          setShowUpdateControlModal(false);
                        } else {
                          showError('Güncelleme başarısız oldu');
                        }
                      } else {
                        showError('Sunucudan geçersiz yanıt alındı');
                      }
                    } else {
                      showError('Güncelleme sırasında hata oluştu');
                    }
                  } catch (error) {
                    console.error('Güncelleme hatası:', error);
                    showError('Güncelleme sırasında hata oluştu');
                  } finally {
                    setIsUpdatingControl(false);
                  }
                }}
                disabled={isUpdatingControl}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingControl ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    <span>Güncelleniyor...</span>
                  </>
                ) : (
                  'Güncelle'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quality Control Popover Portal */}
      {showQualityControlPopover && popoverPosition && createPortal(
        <div
          data-popover="quality-control"
          className="fixed w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 p-4 z-[99999]"
          style={{
            top: `${popoverPosition.top}px`,
            right: `${popoverPosition.right}px`
          }}
          onMouseEnter={() => setShowQualityControlPopover(true)}
          onMouseLeave={() => {
            setTimeout(() => {
              setShowQualityControlPopover(false);
            }, 100);
          }}
        >
          {isCheckingQualityControl ? (
            <div className="flex items-center justify-center py-4">
              <svg className="w-6 h-6 animate-spin text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Yükleniyor...</span>
            </div>
          ) : qualityControlStatus ? (
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Kalite Kontrol Durumu</h4>
              
              {/* Status */}
              <div className="mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Durum:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    qualityControlStatus.status === 'completed' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : qualityControlStatus.status === 'in_progress'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : qualityControlStatus.status === 'validating'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      : qualityControlStatus.status === 'failed'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {qualityControlStatus.status === 'completed' ? 'Tamamlandı' :
                     qualityControlStatus.status === 'in_progress' ? 'Devam Ediyor' :
                     qualityControlStatus.status === 'validating' ? 'Doğrulanıyor' :
                     qualityControlStatus.status === 'failed' ? 'Başarısız' :
                     qualityControlStatus.status}
                  </span>
                </div>
              </div>

              {/* Progress */}
              {qualityControlStatus.status === 'validating' ? (
                <div className="mb-3">
                  <div className="flex items-center justify-center py-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 animate-spin text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Sonuçlar doğrulanıyor...</span>
                    </div>
                  </div>
                </div>
              ) : qualityControlStatus.request_counts && qualityControlStatus.request_counts.total > 0 ? (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">İlerleme:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {Math.round((qualityControlStatus.request_counts.completed / qualityControlStatus.request_counts.total) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(qualityControlStatus.request_counts.completed / qualityControlStatus.request_counts.total) * 100}%` 
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{qualityControlStatus.request_counts.completed} tamamlandı</span>
                    <span>{qualityControlStatus.request_counts.total} toplam</span>
                  </div>
                </div>
              ) : null}

              {/* Otomatik İndirme Bilgisi */}
              {qualityControlStatus.status === 'completed' && (
                <div className="w-full px-3 py-2 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-green-700 dark:text-green-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    {isLoadingQualityResults ? 'Sonuçlar otomatik yükleniyor...' : 'Sonuçlar otomatik yüklendi'}
                  </div>
                </div>
              )}

              {qualityControlStatus.status === 'validating' && (
                <div className="text-center py-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Sonuçlar doğrulanıyor, lütfen bekleyin
                  </span>
                </div>
              )}

              {qualityControlStatus.status !== 'completed' && qualityControlStatus.status !== 'validating' && (
                <div className="text-center py-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Sonuçlar henüz hazır değil
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              {canStartQualityControl() && hasEnglishTextValues() ? (
                !isBatchTranslateTriggered ? (
                  <button
                    onClick={triggerBatchTranslate}
                    disabled={isTriggeringBatchTranslate}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      isTriggeringBatchTranslate
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isTriggeringBatchTranslate ? (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                        </svg>
                        <span>Başlatılıyor...</span>
                      </div>
                    ) : (
                      'Kalite Kontrolü Başlat'
                    )}
                  </button>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Durum bilgisi alınamadı
                  </span>
                )
              ) : !hasEnglishTextValues() ? (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Kalite kontrolü için çeviri verileri bulunamadı
                </span>
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Bu işlem için yetkiniz yok
                </span>
              )}
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Quality Control Status Modal */}
      {showQualityControlModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Kalite Kontrol Durumu
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedDocumentName}
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              {qualityControlStatus ? (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(qualityControlStatus, null, 2)}
                  </pre>
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">
                  Kalite kontrol durumu bilgisi alınamadı.
                </p>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowQualityControlModal(false);
                  setQualityControlStatus(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-md transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Translate Confirmation Modal */}
      {showTranslateConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Çeviriyi Başlat
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Onay gerekiyor
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Doküman bu haliyle çevirilecektir. Devam etmek istediğinizden emin misiniz?
              </p>
              
              {/* Çeviri Dil Bilgisi */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <div className="flex flex-col items-center">
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                      {getLanguageName(sourceLanguage)}
                    </span>
                    <div className="flex flex-col items-center mx-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                      </svg>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 whitespace-nowrap">
                        Çeviri Yönü
                      </p>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                      {getLanguageName(destinationLanguage)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Bilgi
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Çeviri işlemi başladıktan sonra tablodaki mevcut değişiklikler korunacaktır.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowTranslateConfirmModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  setShowTranslateConfirmModal(false);
                  handleTranslate();
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                </svg>
                Evet, Çeviriyi Başlat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quality Control Detail Popover */}
      {showQualityDetailPopover && selectedQualityResult && qualityPopoverPosition && createPortal(
        <>
          {/* Backdrop Overlay - No blur for performance */}
          <div 
            className="fixed inset-0 bg-black/15 dark:bg-black/30 z-[99998] cursor-pointer" 
            onClick={() => {
              setShowQualityDetailPopover(false);
              setSelectedQualityResult(null);
              setQualityPopoverPosition(null);
            }}
          />
          
        <div
          data-popover="quality-detail"
            className="fixed bg-white dark:bg-slate-900 rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)] dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.8)] border border-gray-300 dark:border-gray-600 z-[99999] flex flex-col ring-1 ring-gray-200 dark:ring-gray-700 pointer-events-auto"
          style={{
            top: `${qualityPopoverPosition.top}px`,
            left: `${qualityPopoverPosition.left}px`,
            width: `${(() => {
              const viewportWidth = window.innerWidth;
              const horizontalPadding = 100;
              const maxWidth = viewportWidth - horizontalPadding;
              return Math.min(maxWidth, 1200); // Maksimum 1200px
            })()}px`,
            height: `${(() => {
              const viewportHeight = window.innerHeight;
              const viewportWidth = window.innerWidth;
              
              // Padding for viewport
              const verticalPadding = 40; // Top + bottom padding
              const maxHeight = viewportHeight - verticalPadding;
              
              // Height hesaplama - viewport'a sığacak şekilde
              let baseHeight = maxHeight * 0.90; // Maksimum alanın %90'ı
              
              // Ekran boyutuna göre height ayarlaması
              if (viewportWidth >= 1440) {
                baseHeight = Math.min(maxHeight * 0.95, 900); // Büyük ekranda daha fazla
              } else if (viewportWidth <= 1024) {
                baseHeight = Math.min(maxHeight * 0.85, 650); // Küçük ekranda biraz daha az
              }
              
              return baseHeight;
            })()}px`
          }}
        >
          {/* Header - Fixed */}
          <div className="p-6 pb-4 border-b border-slate-200 dark:border-slate-600 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  Kalite Kontrol Sonucu
                </h4>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Çevirinizin kalite denetim sonuçlarını aşağıda görebilirsiniz.
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
              {/* Score Summary */}
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{
                    color: selectedQualityResult.score >= 70 ? '#4ADE80' : selectedQualityResult.score >= 50 ? '#FACC15' : '#F87171'
                  }}>
                    {selectedQualityResult.score}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Genel Puan</div>
                </div>
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowQualityDetailPopover(false);
                    setSelectedQualityResult(null);
                    setQualityPopoverPosition(null);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Status Indicators */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center justify-center bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${selectedQualityResult.hard_fail ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                  {selectedQualityResult.hard_fail ? (
                    <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
                  ) : (
                    <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  )}
              </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Kritik Hata</div>
                  <div className={`text-sm font-bold ${selectedQualityResult.hard_fail ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {selectedQualityResult.hard_fail ? 'Var' : 'Yok'}
                  </div>
            </div>
          </div>

              {/* Violations Column with Hover */}
              {(() => {
                let violationsList: Array<{code: string, detail: string}> = [];
                try {
                  if (selectedQualityResult.violations) {
                    violationsList = JSON.parse(selectedQualityResult.violations);
                  }
                } catch (e) {
                  violationsList = [];
                }

                const translateRuleCode = (ruleCode: string): string => {
                  const ruleTranslations: { [key: string]: string } = {
                    // A. ANLAM / İÇERİK
                    'MIS_MEAN': 'Anlam Sapması',
                    'MIS_INFO': 'Bilgi Ek/Çıkartma',
                    'STR_ADD': 'Yapısal Ekleme',
                    'STR_DEL': 'Yapısal Silme',
                    'PARA_MERGE': 'Paragraf Birleştirme',
                    'PARA_SPLIT': 'Paragraf Bölme',
                    'LOGIC_INVERT': 'Mantıksal Tersine Çeviri',
                    'CONTEXT_MISUSE': 'Bağlam Hatası',

                    // B. TERİM / TERMINOLOJİ
                    'TER_GLOSS': 'Glossary\'ye Aykırı',
                    'TERM_ACCURACY': 'Terminoloji Doğruluğu',
                    'TER_IFRS': 'IFRS Terim Hatası',
                    'TER_GRI': 'GRI Terim Hatası',
                    'TER_KAP': 'KAP Terim Hatası',
                    'TER_STYLE': 'Kurumsal Üslup Terimi',
                    'TER_UNIT': 'Birim Terimi Hatası',
                    'TER_SHORT': 'Kısaltma Hatası',
                    'TER_NAME': 'Özel İsim Hatası',
                    'TERM_NAME': 'Özel İsim Hatası',
                    'TER_DUP': 'Terim Tutarsızlığı',

                    // C. FORMAT / SAYI / TARİH
                    'FMT_NUM': 'Sayı Biçimi',
                    'FMT_CUR': 'Para Birimi Biçimi',
                    'FMT_PCT': 'Yüzde Biçimi',
                    'FMT_DATE': 'Tarih Biçimi',
                    'FMT_SPACE': 'Boşluk Hatası',
                    'FMT_DASH': 'Tire/En Dash Hatası',
                    'FMT_PUNC': 'Noktalama Hatası',
                    'FMT_CAPS': 'Büyük/Küçük Harf',
                    'FMT_QUOTE': 'Tırnak Biçimi',
                    'FMT_UNIT': 'Birim Biçimi',
                    'FMT_TABLE': 'Tablo Biçimi',
                    'FMT_LIST': 'Liste Biçimi',

                    // D. ESG
                    'ESG_SCOPE': 'ESG Kapsam Hatası',
                    'ESG_METRIC': 'ESG Metrik Hatası',
                    'ESG_TERM': 'ESG Terim Hatası',
                    'ESG_FRAMEWORK': 'ESG Çerçeve Hatası',

                    // E. YASAKLI / UYGUNSUZ
                    'PROHIBITED': 'Yasaklı İfade',
                    'LEGAL_MISUSE': 'Hukuki Atıf Hatası',
                    'IFRS_MISUSE': 'IFRS Yanlış Kullanımı',
                    'GRI_MISUSE': 'GRI Yanlış Kullanımı',
                    'KAP_MISUSE': 'KAP Yanlış Kullanımı',
                    'DATA_ALTER': 'Veri Değiştirme',
                    'NAME_CHANGE': 'Yasal İsim Değişimi',

                    // F. DİL / YAZIM / ÜSLUP
                    'LANG_VARIANT': 'Dil Varyantı Hatası',
                    'SPELLING': 'Yazım Hatası',
                    'GRAMMAR': 'Dilbilgisi Hatası',
                    'WORD_ORDER': 'Sözcük Sırası',
                    'REGISTER': 'Üslup Uygunsuzluğu',
                    'CONSISTENCY': 'Tutarlılık Sorunu',
                    'REDUNDANCY': 'Gereksiz Tekrar',
                    'OMISSION': 'Kelime Atlama',
                    'INSERTION': 'Gereksiz Ekleme',
                    'PUNCT': 'Noktalama',
                    'SPACING': 'Boşluk',
                    'CAPS': 'Büyük/Küçük Harf',

                    // G. YAPI / BÜTÜNLÜK
                    'STRUCT_ORDER': 'Sıra/Dizilim Hatası',
                    'STRUCT_LOSS': 'Yapısal Kayıp',
                    'STRUCT_EXTRA': 'Fazladan Yapı',
                    'STRUCT_MERGE': 'Yapısal Birleştirme',

                    // H. METAVERİ / TEKNİK
                    'META_TAG': 'Meta Etiket Hatası',
                    'LINK_BROKEN': 'Kırık Bağlantı',
                    'REF_ERROR': 'Atıf/Referans Hatası',
                    'VERSION_MISMATCH': 'Sürüm Uyuşmazlığı',
                    'PLACEHOLDER': 'Yer Tutucu Kaldı',
                    'COMMENT_LEAK': 'Yorum/Sistem Notu Sızıntısı'
                  };
                  return ruleTranslations[ruleCode] || ruleCode;
                };

                return (
                  <div className="flex items-center justify-center bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
                    <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                      </svg>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">İhlal Sayısı</div>
                      <div className="text-sm font-bold text-orange-600 dark:text-orange-400">
                        {selectedQualityResult.n_violation}
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="flex items-center justify-center bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${selectedQualityResult.needs_human_review ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                  {selectedQualityResult.needs_human_review ? (
                    <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                  ) : (
                    <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  )}
                    </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">İnsan İncelemesi</div>
                  <div className={`text-sm font-bold ${selectedQualityResult.needs_human_review ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                    {selectedQualityResult.needs_human_review ? 'Gerekli' : 'Gerekli değil'}
                  </div>
                </div>
                  </div>
                </div>
              </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-6">
            {/* Translation Comparison Section - Full Width at Top */}
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mr-2">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                  </svg>
                </div>
                <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Çeviri Karşılaştırması</h6>
              </div>
              
              {(() => {
                const rowData = translatedData?.find(item => item.sid === selectedQualityResult.sid);
                const turkishText = (() => {
                  if (rowData?.runs) {
                    try {
                      const runs = JSON.parse(rowData.runs);
                      return runs.map((run: any) => run.text).join('');
                    } catch (e) {
                      return rowData.runs;
                    }
                  }
                  return 'Türkçe metin bulunamadı';
                })();
                
                const englishText = (() => {
                  if (rowData?.en_runs) {
                    try {
                      const enRuns = JSON.parse(rowData.en_runs);
                      return enRuns.map((run: any) => run.text).join('');
                    } catch (e) {
                      return rowData.en_runs;
                    }
                  }
                  return 'İngilizce metin bulunamadı';
                })();
                
                return (
                  <div className="grid grid-cols-2 gap-4 items-start">
                    {/* Turkish Text - Left Column */}
                    <div className="flex flex-col">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Türkçe</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600 leading-relaxed break-words">
                        {turkishText}
                      </div>
                    </div>
                    
                    {/* English Text - Right Column */}
                    <div className="flex flex-col">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                        İngilizce
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600 leading-relaxed break-words">
                        {englishText}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Two Column Layout: Violations and Correction */}
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column - Violations Detail */}
              {(() => {
                let violationsList: Array<{code: string, detail: string}> = [];
                try {
                  if (selectedQualityResult.violations) {
                    violationsList = JSON.parse(selectedQualityResult.violations);
                  }
                } catch (e) {
                  violationsList = [];
                }

                const translateRuleCode = (ruleCode: string): string => {
                  const ruleTranslations: { [key: string]: string } = {
                    // A. ANLAM / İÇERİK
                    'MIS_MEAN': 'Anlam Sapması',
                    'MIS_INFO': 'Bilgi Ek/Çıkartma',
                    'STR_ADD': 'Yapısal Ekleme',
                    'STR_DEL': 'Yapısal Silme',
                    'PARA_MERGE': 'Paragraf Birleştirme',
                    'PARA_SPLIT': 'Paragraf Bölme',
                    'LOGIC_INVERT': 'Mantıksal Tersine Çeviri',
                    'CONTEXT_MISUSE': 'Bağlam Hatası',

                    // B. TERİM / TERMINOLOJİ
                    'TER_GLOSS': 'Glossary\'ye Aykırı',
                    'TERM_ACCURACY': 'Terminoloji Doğruluğu',
                    'TER_IFRS': 'IFRS Terim Hatası',
                    'TER_GRI': 'GRI Terim Hatası',
                    'TER_KAP': 'KAP Terim Hatası',
                    'TER_STYLE': 'Kurumsal Üslup Terimi',
                    'TER_UNIT': 'Birim Terimi Hatası',
                    'TER_SHORT': 'Kısaltma Hatası',
                    'TER_NAME': 'Özel İsim Hatası',
                    'TERM_NAME': 'Özel İsim Hatası',
                    'TER_DUP': 'Terim Tutarsızlığı',

                    // C. FORMAT / SAYI / TARİH
                    'FMT_NUM': 'Sayı Biçimi',
                    'FMT_CUR': 'Para Birimi Biçimi',
                    'FMT_PCT': 'Yüzde Biçimi',
                    'FMT_DATE': 'Tarih Biçimi',
                    'FMT_SPACE': 'Boşluk Hatası',
                    'FMT_DASH': 'Tire/En Dash Hatası',
                    'FMT_PUNC': 'Noktalama Hatası',
                    'FMT_CAPS': 'Büyük/Küçük Harf',
                    'FMT_QUOTE': 'Tırnak Biçimi',
                    'FMT_UNIT': 'Birim Biçimi',
                    'FMT_TABLE': 'Tablo Biçimi',
                    'FMT_LIST': 'Liste Biçimi',

                    // D. ESG
                    'ESG_SCOPE': 'ESG Kapsam Hatası',
                    'ESG_METRIC': 'ESG Metrik Hatası',
                    'ESG_TERM': 'ESG Terim Hatası',
                    'ESG_FRAMEWORK': 'ESG Çerçeve Hatası',

                    // E. YASAKLI / UYGUNSUZ
                    'PROHIBITED': 'Yasaklı İfade',
                    'LEGAL_MISUSE': 'Hukuki Atıf Hatası',
                    'IFRS_MISUSE': 'IFRS Yanlış Kullanımı',
                    'GRI_MISUSE': 'GRI Yanlış Kullanımı',
                    'KAP_MISUSE': 'KAP Yanlış Kullanımı',
                    'DATA_ALTER': 'Veri Değiştirme',
                    'NAME_CHANGE': 'Yasal İsim Değişimi',

                    // F. DİL / YAZIM / ÜSLUP
                    'LANG_VARIANT': 'Dil Varyantı Hatası',
                    'SPELLING': 'Yazım Hatası',
                    'GRAMMAR': 'Dilbilgisi Hatası',
                    'WORD_ORDER': 'Sözcük Sırası',
                    'REGISTER': 'Üslup Uygunsuzluğu',
                    'CONSISTENCY': 'Tutarlılık Sorunu',
                    'REDUNDANCY': 'Gereksiz Tekrar',
                    'OMISSION': 'Kelime Atlama',
                    'INSERTION': 'Gereksiz Ekleme',
                    'PUNCT': 'Noktalama',
                    'SPACING': 'Boşluk',
                    'CAPS': 'Büyük/Küçük Harf',

                    // G. YAPI / BÜTÜNLÜK
                    'STRUCT_ORDER': 'Sıra/Dizilim Hatası',
                    'STRUCT_LOSS': 'Yapısal Kayıp',
                    'STRUCT_EXTRA': 'Fazladan Yapı',
                    'STRUCT_MERGE': 'Yapısal Birleştirme',

                    // H. METAVERİ / TEKNİK
                    'META_TAG': 'Meta Etiket Hatası',
                    'LINK_BROKEN': 'Kırık Bağlantı',
                    'REF_ERROR': 'Atıf/Referans Hatası',
                    'VERSION_MISMATCH': 'Sürüm Uyuşmazlığı',
                    'PLACEHOLDER': 'Yer Tutucu Kaldı',
                    'COMMENT_LEAK': 'Yorum/Sistem Notu Sızıntısı'
                  };
                  return ruleTranslations[ruleCode] || ruleCode;
                };

                return violationsList.length > 0 ? (
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600 h-full flex flex-col">
                    <div className="flex items-center mb-4">
                      <div className="w-5 h-5 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                        </svg>
                      </div>
                      <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300">İhlal Detayları</h6>
                      <span className="ml-2 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded-full">
                        {violationsList.length} İhlal
                      </span>
                    </div>
                    
                    <div className="space-y-3 overflow-y-auto flex-1">
                      {violationsList.map((violation: {code: string, detail: string}, index: number) => (
                        <div key={index} className="bg-white dark:bg-slate-800 rounded-lg p-3 border-l-4 border-orange-500">
                          <div className="flex items-start gap-2 mb-2">
                            <div className="font-semibold text-sm text-orange-600 dark:text-orange-400 text-left flex-1">
                              {translateRuleCode(violation.code)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded flex-shrink-0">
                              {violation.code}
                            </div>
                          </div>
                          <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed text-left">
                            {violation.detail}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600 h-full flex items-center justify-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">İhlal bulunamadı</p>
                  </div>
                );
              })()}

              {/* Right Column - Correction Section */}
              {selectedQualityResult.corrected_en ? (
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-5 h-5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </div>
                      <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Düzeltilmiş Sürüm</h6>
                    </div>
                    <button
                      onClick={() => applyCorrectedVersion(selectedQualityResult.sid, parseRunsText(selectedQualityResult.corrected_en))}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors duration-200 flex items-center gap-1.5 shadow-sm hover:shadow-md"
                      title="Düzeltilmiş sürümü ana tabloya uygula"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      Uygula
                    </button>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600 flex-1 overflow-y-auto">
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {parseRunsText(selectedQualityResult.corrected_en)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600 h-full flex items-center justify-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Düzeltilmiş sürüm mevcut değil</p>
                </div>
              )}
            </div>
          </div>

          {/* Dynamic Arrow */}
          {qualityPopoverPosition.arrowDirection && qualityPopoverPosition.arrowDirection !== 'none' && (
            <div 
              className={`absolute w-0 h-0 ${
                  qualityPopoverPosition.arrowDirection?.startsWith('right') 
                    ? '-right-2 border-t-8 border-b-8 border-l-8 border-transparent border-l-white dark:border-l-slate-800'
                    : qualityPopoverPosition.arrowDirection?.startsWith('left')
                    ? '-left-2 border-t-8 border-b-8 border-r-8 border-transparent border-r-white dark:border-r-slate-800'
                  : ''
              }`}
              style={{
                top: `${qualityPopoverPosition.arrowTop || 200}px`,
                transform: 'translateY(-50%)'
              }}
            ></div>
          )}
        </div>
        </>,
        document.body
      )}

      {/* Download Confirmation Modal */}
      {showDownloadConfirmModal && selectedDocumentForDownload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowDownloadConfirmModal(false);
              setSelectedDocumentForDownload(null);
              setDownloadProgress(0);
            }}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            {isDownloading ? (
              // Download Progress State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  {downloadProgress === 100 ? (
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {downloadProgress === 100 ? 'Dosya İndirildi!' : 'Dosya İndiriliyor...'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {downloadProgress === 100 
                    ? `${selectedDocumentForDownload.file_name_no_ext}.docx dosyası başarıyla indirildi.`
                    : 'Lütfen bekleyin, dosyanız hazırlanıyor...'
                  }
                </p>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${downloadProgress}%` }}
                  ></div>
                </div>
                
                {downloadProgress === 100 && (
                  <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    DOCX formatında
                  </div>
                )}
              </div>
            ) : (
              // Confirmation State
              <>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Dosyayı İndir
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      setShowDownloadConfirmModal(false);
                      setSelectedDocumentForDownload(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="mb-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    <strong>"{selectedDocumentForDownload.file_name_no_ext}"</strong> dosyasını indirmek istediğinizden emin misiniz?
                  </p>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">
                          Dosya Bilgisi
                        </p>
                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                          Dosya DOCX formatında indirilecek ve çevirileri içerecektir.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDownloadConfirmModal(false);
                      setSelectedDocumentForDownload(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => downloadDeserializeFile(selectedDocumentForDownload.document_id, selectedDocumentForDownload.file_name_no_ext)}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    İndir
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Document Confirmation Modal */}
      {showDeleteDocumentModal && selectedDocumentForDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Dökümanı Sil
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Bu işlem geri alınamaz
                  </p>
                </div>
              </div>
              
              {/* Content */}
              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  <strong>"{selectedDocumentForDelete.name}"</strong> dökümanını silmek istediğinizden emin misiniz?
                </p>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        Dikkat
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                        Bu döküman ve tüm çeviri verileri kalıcı olarak silinecektir. Bu işlem geri alınamaz.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteDocumentModal(false);
                    setSelectedDocumentForDelete(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleConfirmDeleteDocument}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                  Evet, Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showCommentsModal && selectedRowForComments && createPortal(
        <div 
          className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => {
            setShowCommentsModal(false);
            setSelectedRowForComments(null);
            setNewComment('');
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Yorumlar</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Satır #{selectedRowForComments.rowNumber}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Delete row comments button */}
                {rowComments[selectedRowForComments.sid]?.length > 0 && (
                  <button
                    onClick={() => setShowDeleteRowCommentsModal(true)}
                    className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center space-x-1"
                    title="Satırdaki bütün yorumları sil"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                    <span>Satırdaki Bütün Yorumları Sil</span>
                  </button>
                )}

                {/* Close button */}
                <button
                  onClick={() => {
                    setShowCommentsModal(false);
                    setSelectedRowForComments(null);
                    setNewComment('');
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                >
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {rowComments[selectedRowForComments.sid]?.length > 0 ? (
                rowComments[selectedRowForComments.sid].map((comment, index) => (
                  <div 
                    key={index}
                    className="flex space-x-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors animate-in slide-in-from-bottom duration-300 group"
                    style={{animationDelay: `${index * 50}ms`}}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-md">
                        {comment.user.charAt(0).toUpperCase()}
                      </div>
                    </div>

                    {/* Comment Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {comment.user}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(comment.timestamp * 1000).toLocaleString('tr-TR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {/* Delete comment button */}
                          <button
                            onClick={() => {
                              setCommentToDelete({sid: selectedRowForComments.sid, timestamp: comment.timestamp});
                              setShowDeleteSingleCommentModal(true);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            title="Yorumu sil"
                          >
                            <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                        {comment.comment}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">Henüz yorum yok</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">İlk yorumu siz yapın!</p>
                </div>
              )}
            </div>

            {/* Add Comment */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex space-x-3">
                {/* Current User Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold shadow-md">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </div>

                {/* Comment Input */}
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Yorumunuzu yazın..."
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:border-amber-500 dark:focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors resize-none"
                    rows={3}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && e.ctrlKey && newComment.trim()) {
                        // Ctrl+Enter to submit
                        const result = await handleAddComment(selectedRowForComments.sid, newComment);
                        if (result) {
                          setNewComment('');
                        }
                      }
                    }}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Ctrl+Enter ile gönder
                    </span>
                    <button
                      onClick={async () => {
                        if (!newComment.trim()) return;
                        const result = await handleAddComment(selectedRowForComments.sid, newComment);
                        if (result) {
                          setNewComment('');
                        }
                      }}
                      disabled={!newComment.trim()}
                      className="px-6 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                      </svg>
                      <span>Gönder</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* EN Change History Modal */}
      <EnChangeHistoryModal
        isOpen={showChangeHistoryModal}
        onClose={() => {
          setShowChangeHistoryModal(false);
          setSelectedRowChangeHistory(null);
        }}
        changeHistory={enChangeHistory}
        rowSid={selectedRowChangeHistory?.sid || ''}
      />

      {/* Delete All Comments Modal */}
      {showDeleteAllCommentsModal ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200000]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Warning Icon and Title */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Dokümandaki Tüm Yorumları Sil
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Bu işlem geri alınamaz
                </p>
              </div>
            </div>

            {/* Message */}
            <p className="text-gray-700 dark:text-gray-300 mb-8">
              Dokümandaki <span className="font-semibold">TÜM yorumları</span> silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm yorumlar kalıcı olarak silinecektir.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteAllCommentsModal(false)}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={async () => {
                  await handleDeleteAllDocumentComments();
                  setShowDeleteAllCommentsModal(false);
                }}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Delete Row Comments Modal */}
      {showDeleteRowCommentsModal && selectedRowForComments ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200000]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Warning Icon and Title */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Satırdaki Tüm Yorumları Sil
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Bu işlem geri alınamaz
                </p>
              </div>
            </div>

            {/* Message */}
            <p className="text-gray-700 dark:text-gray-300 mb-8">
              Satır #{selectedRowForComments.rowNumber} için <span className="font-semibold">tüm yorumları</span> silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteRowCommentsModal(false)}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={async () => {
                  await handleDeleteRowComments(selectedRowForComments.sid);
                  setShowDeleteRowCommentsModal(false);
                }}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Delete Single Comment Modal */}
      {showDeleteSingleCommentModal && commentToDelete ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200000]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Warning Icon and Title */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Yorumu Sil
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Bu işlem geri alınamaz
                </p>
              </div>
            </div>

            {/* Message */}
            <p className="text-gray-700 dark:text-gray-300 mb-8">
              Bu yorumu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteSingleCommentModal(false);
                  setCommentToDelete(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={async () => {
                  await handleDeleteComment(commentToDelete.sid, commentToDelete.timestamp);
                  setShowDeleteSingleCommentModal(false);
                  setCommentToDelete(null);
                }}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {/* Compare Documents Modal */}
      {/* Bulk History Replace Modal */}
      {showBulkHistoryReplaceModal && compareData && createPortal(
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={() => setShowBulkHistoryReplaceModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4 border-b border-teal-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Toplu Geçmiş Değiştirme</h2>
                    <p className="text-teal-100 text-xs">Eşleşme oranına göre satırları geçmiş verilerle değiştir</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowClearCompareDataModal(true)}
                    className="text-sm font-medium text-white/90 hover:text-white bg-white/10 hover:bg-red-500/80 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Karşılaştırma Verilerini Temizle
                  </button>
                  <button
                    onClick={() => setShowBulkHistoryReplaceModal(false)}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors backdrop-blur-sm"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Filter for Applied Items */}
              <div className="mb-4 flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={hideAppliedHistory}
                    onChange={(e) => {
                      setHideAppliedHistory(e.target.checked);
                      setHistoryCurrentPage(1);
                    }}
                    className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500 dark:focus:ring-teal-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                    Uygulanmış satırları gizle
                  </span>
                </label>
              </div>

              {/* Range Selection */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Eşleşme Oranı Aralığı Seçin:
                </label>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    // Pre-calculate counts for all ranges (do once)
                    const rangeCounts: {[key: string]: number} = {};
                    compareData.forEach(item => {
                      if (item.match_percentage === 100) rangeCounts['100-100'] = (rangeCounts['100-100'] || 0) + 1;
                      else if (item.match_percentage >= 95) rangeCounts['95-99'] = (rangeCounts['95-99'] || 0) + 1;
                      else if (item.match_percentage >= 90) rangeCounts['90-94'] = (rangeCounts['90-94'] || 0) + 1;
                      else if (item.match_percentage >= 85) rangeCounts['85-89'] = (rangeCounts['85-89'] || 0) + 1;
                      else if (item.match_percentage >= 80) rangeCounts['80-84'] = (rangeCounts['80-84'] || 0) + 1;
                      else if (item.match_percentage >= 75) rangeCounts['75-79'] = (rangeCounts['75-79'] || 0) + 1;
                      else if (item.match_percentage >= 70) rangeCounts['70-74'] = (rangeCounts['70-74'] || 0) + 1;
                      else if (item.match_percentage >= 65) rangeCounts['65-69'] = (rangeCounts['65-69'] || 0) + 1;
                    });

                    return [
                      { min: 100, max: 100, label: '%100' },
                      { min: 95, max: 99, label: '%95-99' },
                      { min: 90, max: 94, label: '%90-94' },
                      { min: 85, max: 89, label: '%85-89' },
                      { min: 80, max: 84, label: '%80-84' },
                      { min: 75, max: 79, label: '%75-79' },
                      { min: 70, max: 74, label: '%70-74' },
                      { min: 65, max: 69, label: '%65-69' }
                    ].map((range) => {
                      const count = rangeCounts[`${range.min}-${range.max}`] || 0;
                      const isSelected = selectedHistoryRange.min === range.min && selectedHistoryRange.max === range.max;
                    
                    return (
                      <button
                        key={`${range.min}-${range.max}`}
                        onClick={() => {
                          setSelectedHistoryRange(range);
                          setSelectedHistoryItems(new Set());
                          setHistoryCurrentPage(1);
                        }}
                        className={`px-4 py-2.5 rounded-lg border-2 font-medium transition-all duration-200 ${
                          isSelected
                            ? 'bg-teal-600 border-teal-600 text-white shadow-lg'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-teal-500 dark:hover:border-teal-500'
                        }`}
                      >
                        <span className="block text-sm">{range.label}</span>
                        <span className={`block text-xs mt-0.5 ${isSelected ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                          {count} satır
                        </span>
                      </button>
                    );
                  });
                  })()}
                </div>
              </div>

              {/* Preview Table */}
              {(() => {
                // Create a Map for O(1) lookup instead of O(n) find
                const translatedDataMap = new Map(
                  translatedData?.map(td => [td.sid, td]) || []
                );

                const filteredItems = compareData
                  .filter(item => {
                    // Range filter
                    const matchesRange = item.match_percentage >= selectedHistoryRange.min && 
                                        item.match_percentage <= selectedHistoryRange.max;
                    // Applied filter
                    const passesActiveFilter = hideAppliedHistory ? !item.is_active : true;
                    
                    return matchesRange && passesActiveFilter;
                  })
                  .map(item => {
                    // Ana tablodaki index'i bul
                    const tableIndex = translatedData?.findIndex(td => td.sid === item.sid) ?? -1;
                    return { ...item, tableIndex };
                  })
                  .sort((a, b) => a.tableIndex - b.tableIndex); // Ana tablodaki sıraya göre sırala
                
                // Pagination
                const totalPages = Math.ceil(filteredItems.length / historyItemsPerPage);
                const startIndex = (historyCurrentPage - 1) * historyItemsPerPage;
                const endIndex = startIndex + historyItemsPerPage;
                const currentPageItems = filteredItems.slice(startIndex, endIndex);
                
                // Check if all selectable items (is_active=false) in ENTIRE RANGE are selected
                const allSelectableInRange = filteredItems.filter(item => !item.is_active);
                const allSelected = allSelectableInRange.length > 0 && allSelectableInRange.every(item => selectedHistoryItems.has(item.sid));

                return (
                  <>
                    {/* Table Header with Select All */}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Önizleme ({filteredItems.length} satır{totalPages > 1 ? ` - Sayfa ${historyCurrentPage}/${totalPages}` : ''})
                      </h3>
                      {allSelectableInRange.length > 0 && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => {
                              const newSet = new Set(selectedHistoryItems);
                              if (e.target.checked) {
                                allSelectableInRange.forEach(item => newSet.add(item.sid));
                              } else {
                                allSelectableInRange.forEach(item => newSet.delete(item.sid));
                              }
                              setSelectedHistoryItems(newSet);
                            }}
                            className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500 dark:focus:ring-teal-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Bu Aralıktaki Hepsini Seç ({allSelectableInRange.length})
                          </span>
                        </label>
                      )}
                    </div>

                    {/* Table */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div ref={historyTableContainerRef} className="max-h-[450px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                            <tr className="divide-x divide-gray-200 dark:divide-gray-600">
                              <th className="w-16 px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Seç
                              </th>
                              <th className="w-12 px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                #
                              </th>
                              <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Skor
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Mevcut TR
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Mevcut EN
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Eski TR
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Eski EN
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {currentPageItems.length > 0 ? (
                              currentPageItems.map((item, idx) => {
                                const currentData = translatedDataMap.get(item.sid);
                                const isChecked = selectedHistoryItems.has(item.sid);
                                const rowNumber = item.tableIndex + 1;
                                
                                // Pre-parse texts to avoid multiple parseRunsText calls
                                const currentTrText = currentData ? parseRunsText(currentData.runs) : '-';
                                const currentEnText = currentData ? parseRunsText(currentData.en_runs) : '-';
                                const oldTrText = parseRunsText(item.old_data_runs);
                                const oldEnText = parseRunsText(item.old_data_en_runs);
                                
                                return (
                                  <tr 
                                    key={item.sid}
                                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors divide-x divide-gray-200 dark:divide-gray-600 ${
                                      isChecked ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                                    } ${item.is_active ? 'bg-green-50 dark:bg-green-900/10' : ''}`}
                                  >
                                    <td className="w-16 px-4 py-3">
                                      <div className="flex items-center justify-center">
                                        {item.is_active ? (
                                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shadow-sm" title="Uygulandı">
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
                                            </svg>
                                          </div>
                                        ) : (
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => {
                                              const newSet = new Set(selectedHistoryItems);
                                              if (e.target.checked) {
                                                newSet.add(item.sid);
                                              } else {
                                                newSet.delete(item.sid);
                                              }
                                              setSelectedHistoryItems(newSet);
                                            }}
                                            className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500 dark:focus:ring-teal-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                          />
                                        )}
                                      </div>
                                    </td>
                                    <td className="w-12 px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                                      {rowNumber}
                                    </td>
                                    <td className="w-16 px-4 py-3">
                                      <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${
                                        item.match_percentage === 100
                                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                          : item.match_percentage >= 90
                                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                          : item.match_percentage >= 75
                                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                          : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                      }`}>
                                        {item.match_percentage}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                      <div className="whitespace-pre-wrap break-words">
                                        {currentTrText}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                      <div className="whitespace-pre-wrap break-words">
                                        {currentEnText}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                      <div className="whitespace-pre-wrap break-words">
                                        {oldTrText}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                      <div className="whitespace-pre-wrap break-words">
                                        {oldEnText}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr className="divide-x divide-gray-200 dark:divide-gray-600">
                                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                  Bu aralıkta satır bulunamadı
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Selection Info */}
                    {selectedHistoryItems.size > 0 && (
                      <div className="mt-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg p-2 border border-teal-200 dark:border-teal-700">
                        <p className="text-sm font-medium text-teal-700 dark:text-teal-300 text-center">
                          <span className="font-bold">{selectedHistoryItems.size}</span> satır seçildi
                        </p>
                      </div>
                    )}

                    {/* Footer with Pagination */}
                    <div className="bg-gray-50 dark:bg-gray-900 px-6 py-3 border-t border-gray-200 dark:border-gray-700 mt-auto">
                    <div className="flex justify-between items-center gap-4">
                      {/* Left: Cancel Button */}
                      <button
                        onClick={() => {
                          setShowBulkHistoryReplaceModal(false);
                          setSelectedHistoryItems(new Set());
                          setHistoryCurrentPage(1);
                        }}
                        className="px-4 py-2 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        disabled={isBulkHistoryReplacing}
                      >
                        İptal
                      </button>

                      {/* Center: Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setHistoryCurrentPage(1)}
                            disabled={historyCurrentPage === 1}
                            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            İlk
                          </button>
                          <button
                            onClick={() => setHistoryCurrentPage(p => Math.max(1, p - 1))}
                            disabled={historyCurrentPage === 1}
                            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Önceki
                          </button>
                          <span className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                            {historyCurrentPage} / {totalPages}
                          </span>
                          <button
                            onClick={() => setHistoryCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={historyCurrentPage === totalPages}
                            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Sonraki
                          </button>
                          <button
                            onClick={() => setHistoryCurrentPage(totalPages)}
                            disabled={historyCurrentPage === totalPages}
                            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Son
                          </button>
                        </div>
                      )}

                      {/* Right: Submit Button */}
                      <button
                        onClick={handleBulkHistoryReplace}
                        disabled={isBulkHistoryReplacing || selectedHistoryItems.size === 0}
                        className="px-4 py-2 rounded-lg font-medium transition-colors bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-teal-600 flex items-center gap-2"
                      >
                        {isBulkHistoryReplacing ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>İşleniyor...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                            </svg>
                            <span>Değiştir ({selectedHistoryItems.size})</span>
                          </>
                        )}
                      </button>
                    </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Clear Compare Data Confirmation Modal */}
      {showClearCompareDataModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            {clearCompareDataSuccess ? (
              // Success State
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Başarılı!
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Karşılaştırma verileri başarıyla temizlendi.
                </p>
              </div>
            ) : (
              // Confirmation State
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Karşılaştırma Verilerini Temizle
                  </h3>
                  <button
                    onClick={() => setShowClearCompareDataModal(false)}
                    disabled={isClearingCompareData}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Bu dokümana ait karşılaştırma verilerini temizlemek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </p>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowClearCompareDataModal(false)}
                    disabled={isClearingCompareData}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors disabled:opacity-50"
                  >
                    İptal
                  </button>
                  <button
                    onClick={clearCompareData}
                    disabled={isClearingCompareData}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isClearingCompareData ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Temizleniyor...</span>
                      </>
                    ) : (
                      <span>Evet, Temizle</span>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {showCompareDocumentsModal && createPortal(
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={() => !isComparingDocuments && setShowCompareDocumentsModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700 px-8 py-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Geçmiş Dokümanla Karşılaştır</h2>
                  <p className="text-purple-100 text-sm mt-1">
                    {selectedCompany?.name} şirketinin dokümanları
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCompareDocumentsModal(false)}
                disabled={isComparingDocuments}
                className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(80vh-180px)]">
              {isLoadingCompareDocuments ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <svg className="animate-spin h-12 w-12 text-purple-600 dark:text-purple-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-gray-600 dark:text-gray-400">Dokümanlar yükleniyor...</p>
                  </div>
                </div>
              ) : companyDocumentsForCompare && companyDocumentsForCompare.length > 0 ? (
                <div className="space-y-4">
                  {/* Selection Info */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                        </svg>
                        <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                          {selectedCompareDocuments.size} doküman seçildi
                        </span>
                      </div>
                      {selectedCompareDocuments.size > 0 && (
                        <button
                          onClick={() => setSelectedCompareDocuments(new Set())}
                          className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
                        >
                          Seçimi Temizle
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Documents List */}
                  <div className="grid grid-cols-1 gap-4">
                    {companyDocumentsForCompare.map((doc) => {
                      const isSelected = selectedCompareDocuments.has(doc.document_id);
                      return (
                        <div
                          key={doc.document_id}
                          onClick={() => toggleDocumentSelection(doc.document_id)}
                          className={`w-full p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                            isSelected
                              ? 'bg-gradient-to-r from-purple-100 to-purple-200 dark:from-purple-800/40 dark:to-purple-700/40 border-purple-500 dark:border-purple-400 shadow-lg'
                              : 'bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-lg'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4 flex-1">
                              {/* Checkbox */}
                              <div className="flex-shrink-0 pt-1">
                                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                                  isSelected
                                    ? 'bg-purple-600 border-purple-600'
                                    : 'bg-white dark:bg-gray-700 border-purple-300 dark:border-purple-600'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                    </svg>
                                  )}
                                </div>
                              </div>

                              {/* Document Info */}
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? 'bg-purple-300 dark:bg-purple-600'
                                      : 'bg-purple-200 dark:bg-purple-700'
                                  }`}>
                                    <svg className="w-5 h-5 text-purple-700 dark:text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                    </svg>
                                  </div>
                                  <div>
                                    <h3 className={`text-lg font-bold transition-colors ${
                                      isSelected
                                        ? 'text-purple-900 dark:text-purple-100'
                                        : 'text-purple-800 dark:text-purple-200'
                                    }`}>
                                      {doc.file_name_no_ext}
                                    </h3>
                                    {doc.current_date && doc.current_time && (
                                      <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                                        {formatDateTime(doc.current_date, doc.current_time)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-16">
                  <svg className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">
                    Karşılaştırılacak doküman bulunamadı
                  </p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm">
                    Bu şirkete ait başka doküman bulunmuyor.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-900 px-8 py-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedCompareDocuments.size > 0 ? (
                  <span className="font-medium text-purple-600 dark:text-purple-400">
                    {selectedCompareDocuments.size} doküman seçildi
                  </span>
                ) : (
                  <span>Karşılaştırmak için doküman seçin</span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCompareDocumentsModal(false)}
                  disabled={isComparingDocuments}
                  className="px-6 py-2.5 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  İptal
                </button>
                <button
                  onClick={compareDocuments}
                  disabled={selectedCompareDocuments.size === 0 || isComparingDocuments}
                  className="px-6 py-2.5 rounded-lg font-medium transition-colors bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-600 flex items-center gap-2"
                >
                  {isComparingDocuments ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Karşılaştırılıyor...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                      </svg>
                      Karşılaştır ({selectedCompareDocuments.size})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

    </>
  );
};

export default TranslatorPage;


